import { bandForSeconds, medianWordsForSeconds } from './video-length-data'

export interface TemplateData {
  slug: string
  title: string
  metaTitle: string
  metaDescription: string
  category: string
  templateText: string
  annotatedBreakdown: { section: string; purpose: string; tips: string }[]
  filledExample: string
  /** A complete script written start to finish, rendered as its own visible
   *  section. The Example tab only mounts when clicked, so a worked example
   *  that should be readable without a click lives here. */
  fullExample?: TemplateFullExample
  commonMistakes: string[]
  retentionBenchmarks: { metric: string; value: string }[]
  faqs: { question: string; answer: string }[]
  keywords: string[]
  relatedTemplates: string[]
  relatedGuides: string[]
}

export interface TemplateFullExample {
  heading: string
  /** Says plainly that the script is an illustrative example we wrote. */
  note: string
  /** Runtime the template is paced for, used for the word budgets. */
  runtimeSeconds: number
  /** One entry per template beat. Lines in square brackets are on-screen
   *  directions, every other line is narration. */
  beats: { beat: string; timing: string; budget: string; script: string }[]
}

/** Narration words in a beat, leaving out the bracketed on-screen directions. */
export function narrationWordCount(script: string): number {
  return script
    .split('\n')
    .filter((line) => !line.trim().startsWith('['))
    .join(' ')
    .split(/\s+/)
    .filter((token) => /[A-Za-z0-9]/.test(token)).length
}

/** Total narration words in a full example and the runtime they fill at the
 *  median pace of the example's own length band. */
export function fullExampleStats(example: TemplateFullExample): {
  words: number
  minutes: string
  wpm: number
  bandLabel: string
} {
  const total = example.beats.reduce((sum, beat) => sum + narrationWordCount(beat.script), 0)
  const band = bandForSeconds(example.runtimeSeconds)
  return {
    words: total,
    minutes: (total / band.wpmMedian).toFixed(1),
    wpm: band.wpmMedian,
    bandLabel: band.label,
  }
}

// Word budgets quoted inside the templates below are read from the generated
// corpus in ./video-length-data (never hand edited). Each template is paced at
// the median words per minute of the length band its own runtime falls in, so a
// section budget, the "script length that fills N minutes" planning row and the
// per-length pages all quote the same figure for the same runtime. The median is
// a planning anchor, not a target: delivery pace still varies by creator.

/** Words that fill `seconds` of a script paced like a `runtimeSeconds` video. */
const words = (seconds: number, runtimeSeconds: number): string =>
  Math.round((seconds / 60) * bandForSeconds(runtimeSeconds).wpmMedian).toLocaleString('en-US')

/** The planning figure for a template: how many words fill its own runtime. */
const fills = (runtimeSeconds: number): string =>
  medianWordsForSeconds(runtimeSeconds).toLocaleString('en-US')

/** A band label in the form it reads before "videos": "8 to 12 minutes" -> "8 to 12 minute". */
const bandAdjective = (label: string): string => label.toLowerCase().replace(' minutes', ' minute')

/** The pace reference for a template, read from its own length band. */
const paceNote = (runtimeSeconds: number): string => {
  const band = bandForSeconds(runtimeSeconds)
  return `${band.wpmMedian} wpm (median for ${bandAdjective(band.label)} videos)`
}

/** The same pace reference written as prose, for FAQ answers. */
const paceProse = (runtimeSeconds: number): string => {
  const band = bandForSeconds(runtimeSeconds)
  return `${band.wpmMedian} words per minute of finished video, the median for ${bandAdjective(band.label)} videos`
}

// Illustrative example for /templates/video-essay, written by us on a neutral
// topic so it makes no factual claim about any real person, event, or study.
// Every beat matches a section of the template above it.
const videoEssayExample: TemplateFullExample = {
  heading: 'Video Essay Script Example: The List That Never Ends',
  note: 'Illustrative example written by Prepublish to show the template in use. The narrator, the notebook, and every detail in it are invented. Nothing here is a real case, a study, or a statistic. Each beat is labelled with the template section it fills, and lines in square brackets are on-screen directions rather than narration.',
  runtimeSeconds: 900,
  beats: [
    {
      beat: 'Cold open',
      timing: '0:00 to 0:45',
      budget: words(45, 900),
      script: `[On screen: a page from a paper notebook. One line is circled in pencil.]
This line is on page four of my notebook. It says: call the dentist. It is also on page five. And page six. I copied it forward every morning for most of a year.
I never once forgot about it. I could have recited it in my sleep. I just never did it.
Every morning I opened the notebook, wrote the date at the top, and carried that line over with the rest, like a coin I kept moving from one pocket to the other. At some point I stopped seeing it as a task. It became part of the page, the way a watermark is part of the paper.
This video is about that line, and about why the list it lives on was never going to end.
[Hold on the circled line for two full seconds before the title card.]`,
    },
    {
      beat: 'Thesis',
      timing: '0:45 to 2:00',
      budget: words(75, 900),
      script: `[On screen: narrator to camera.]
Most advice about to-do lists assumes the list is a plan. Write everything down, sort it, work through it from the top. If the list never gets shorter, the advice says the problem is you. You need a better app, a better system, more discipline in the morning.
I think that is wrong. A to-do list is not a plan. It is a record of what you are afraid to forget. Those are different documents, and they behave differently. A plan has an end. A record of worry grows every time you notice something new to worry about, which is every day.
If that is true, a list that never ends is not a sign of failure. It is the list doing exactly what it was built to do. The fix is not more discipline. The fix is to stop asking one document to do two jobs.
To show you, I am going to look at three lists. My own notebook, which is where I first noticed the problem. A grocery list, which is the one kind of list almost everyone finishes. And a note I left for a friend who looked after my apartment, which turned out to be the best list I have ever written, because I was not the one who had to read it.
[On screen: three cards labelled Notebook, Grocery list, Note for a friend.]`,
    },
    {
      beat: 'Evidence one',
      timing: '2:00 to 5:00',
      budget: words(180, 900),
      script: `[On screen: a slow pan across notebook pages, the date written at the top of each.]
Start with the notebook. I kept it for a year, and when I sat down to make this video I read it from the first page to the last. I do not recommend the experience.
Here is what I found. The lines that got crossed off were almost all small and concrete. Buy stamps. Return the library book. Email Sam the photos. Each one had a verb, an object, and a clear moment where it was done. You either sent the email or you did not.
The lines that got carried forward looked different. Sort out the garage. Think about the job. Get fit. These were not tasks. They were worries written in the shape of tasks. Sort out the garage does not tell you what to do when you walk into the garage. It tells you that the garage bothers you.
[On screen: two columns. Left, the crossed-off lines. Right, the carried lines.]
So what does this tell us? The notebook was really two lists mixed together. One was a list of errands, and it worked fine. The other was a list of concerns, and it could never be finished, because a concern does not have a last step. You do not complete worrying about your health. You only stop writing it down.
And here is the part that took me longest to see. The carried lines did all the damage. Every morning, copying them forward cost a few minutes and a little self-respect. The errands got done and disappeared. The concerns stayed, and because they stayed, the page always looked unfinished. A page that always looks unfinished teaches you that you are someone who does not finish things.
[On screen: the carried lines, stacked by how many pages each one survived.]
Halfway through the year I ran an experiment without realizing it was one. On a bad morning, I copied only the errands onto the new page and left the concerns behind. Nothing happened. I did not forget the garage. I did not forget the job. I did not forget that I wanted to get fit. The concerns were still in my head, exactly where they had always been. The only thing that changed was the page. For the first time in months, it was a page I could finish, and I finished it before lunch. The next morning, out of habit, I copied everything back.
This matters because it changes what the problem is. If the list is a plan, a carried line is a failure to execute. If the list is a record of worry, a carried line is simply a worry that has not been turned into an action yet. Those call for different fixes. One says try harder. The other says write a different sentence.
[On screen: back to the circled dentist line.]
Which brings me back to the dentist. Call the dentist looks like an errand. It has a verb and an object. So why did it sit there for a year? I will answer that, but the second list has to come first, because it is the one that breaks the pattern.`,
    },
    {
      beat: 'Evidence two',
      timing: '5:00 to 8:00',
      budget: words(180, 900),
      script: `[On screen: a crumpled grocery list on a kitchen counter.]
The grocery list is the one list most people finish without effort. Nobody carries milk forward for a year. So if my argument is right, the grocery list should be a pure list of errands, with no worry in it at all.
It is not. Notice that this cuts against what we just saw. My grocery lists are full of worry. Something for dinner on Thursday. Check whether we need trash bags. Something for the party. Those are concerns, not errands, and they sit on the list right next to eggs.
And yet the list still gets finished. Every line is gone by the time I leave the store.
[On screen: the same list, with the vague lines highlighted.]
So what is different? Two things. First, the grocery list has a deadline built in: the moment you walk out of the store. The list stops existing at the door. It never gets copied forward, because there is no tomorrow version of it. Second, the store forces every vague line into a decision. You cannot buy something for dinner. You stand in the aisle and choose pasta. The building turns the worry into an errand for you, whether you planned it or not.
Both can be true at once, and that is the interesting part. Lists are full of worry, and some lists still get finished. The difference is not the quality of the items. It is whether something outside the list forces each item to become a concrete action, and gives the whole list a point where it ends.
My notebook had neither. It had no door to walk out of. Every morning was a fresh page with the same contents, and nothing in my day stood in front of sort out the garage and made me pick up the first box.
This also explains something in the notebook I could not make sense of at first. A few of the big, vague lines did get crossed off, and every one of them had an outside deadline attached. Clear the spare room disappeared the week before my brother came to stay. The visit did what the aisle does. It turned a worry into a set of actions, with a door at the end.
[On screen: the notebook line, clear the spare room, crossed off, beside a calendar entry for the visit.]
There is one more detail on the grocery list worth noticing. When something cannot be bought, it does not get copied to next week. It just falls off. If the store is out of the cheese I wanted, I make a decision in the aisle: something else, or nothing. The list never asks me to feel bad about the cheese. That is a small thing, but it is the opposite of what my notebook did. The notebook kept every failure in plain sight and moved it forward, day after day, as if carrying it were the same as working on it.
So the grocery list does not disprove the thesis. It sharpens it. A list finishes when something converts its worries into actions and gives it an end. The question is whether you can do that conversion yourself, without a store or a visiting brother to do it for you. The third list says you can, but only if you pretend someone else is reading.`,
    },
    {
      beat: 'Evidence three',
      timing: '8:00 to 11:00',
      budget: words(180, 900),
      script: `[On screen: a handwritten note held to a fridge by a magnet.]
Last spring I went away for ten days, and a friend looked after my apartment. Before I left, I wrote her a note. I found it in a drawer while I was making this video, and it is the best to-do list I have ever written.
Nobody thinks of a note like this as a to-do list, because it is not for you. But read it.
[On screen: the note, one line at a time.]
Water the plants on the windowsill on Monday and Thursday, one glass each. Take the recycling out on Wednesday night, the blue bin, to the curb. If the router light turns red, unplug it, count to ten, and plug it back in.
Every line has a verb, an object, and a moment where it is done. There is no line that says sort out the plants. There could not be. She would have had no idea what I meant, and I knew that, so I did the conversion for her before I wrote anything down.
This is the case nobody applies the argument to, because it is not a productivity tool. It is a favor. But it shows that the conversion is something I already knew how to do. I could turn worry into action perfectly well. I only did it when the reader was somebody else.
Why? Because when I write for myself, I know what I mean. Sort out the garage feels like a complete thought to me, because I can picture the garage. The vagueness is invisible to the writer. Writing for another person removes that comfort. You have to imagine someone standing in the room, holding your note, asking what they are actually supposed to do now.
[On screen: the note side by side with a page from the notebook.]
I tested this on the notebook. I took the five lines that had survived the longest and rewrote each one as if I were leaving it on the fridge for my friend. Sort out the garage became take the three boxes by the door to the donation drop-off on Saturday morning. Think about the job became read the two job listings saved in my inbox and write one sentence about each. Two of the five lines turned into something I could do that week. The other three would not convert, no matter how I phrased them, and that told me something too. They were not tasks I was avoiding. They were decisions I had not made yet.
So the note also shows what the fix looks like. It is not a better app. It is a different reader. When you put a line on your own list, write it for a stranger who has to do the task tomorrow without asking you anything. If the stranger would not know where to start, the line is still a worry, and it belongs somewhere else.
And it shows where the dentist line went wrong. Call the dentist looks concrete, but a stranger would ask at once: which dentist, which number, and about what? I did not have a number. I had moved, and I no longer had a dentist. The real first step was to find a dentist near the new apartment, and that was a worry I did not want to look at. So I wrote a line that looked like an errand and let it sit there, being carried, for a year.`,
    },
    {
      beat: 'Counterargument',
      timing: '11:00 to 13:30',
      budget: words(150, 900),
      script: `[On screen: narrator to camera, plain background.]
The strongest case against me is this. Writing a worry down is useful even if you never act on it. Getting it out of your head and onto paper lets you stop thinking about it, and that relief is the real job of the list. On that view, a long list of carried worries is not a failure. It is a storage unit that lets you sleep. That is a good argument, and part of it is right.
Writing things down does take weight off. I felt that every morning. Putting sort out the garage on the page meant I did not have to hold it in my head while I bought the stamps and returned the library book.
Here is where it fails. A storage unit does not judge you. A to-do list does. The worry sits on the same page as the errands, in the same format, with the same empty box next to it, waiting to be ticked. Every day the box stays empty, it tells you that you did not do something. The relief of writing it down is paid back, with interest, by the guilt of looking at it.
So the real problem is not that worries get written down. It is that they get written down in the wrong place, in a format that promises an end they cannot have.
There is a second version of the objection, and it deserves an answer too. Some people say the guilt is useful. The empty box nags you, and the nagging is what eventually gets the garage cleared. I believed that for most of the year. But read the notebook again. The garage line was nagged at every single morning for months, and the nagging never moved it. The conversion did: a first action small enough to do on a Saturday. The guilt did not produce the action. It only made the months before the action worse.
The fix that respects both sides is simple. Keep two pages. On one, write worries freely, with no boxes, no dates, and no promise to finish them. On the other, write only actions a stranger could do, and only the ones you could do this week. Once a week, read the worry page and ask the question from the note on the fridge: what is the first thing someone would actually do? If there is an answer, move it across. If there is not, leave it where it is, and do not copy it forward. It is already written down. That was the point.
[On screen: two pages. One is titled Worries and has no boxes. The other is titled Actions and has a box beside every line.]`,
    },
    {
      beat: 'Synthesis and outro',
      timing: '13:30 to 15:00',
      budget: words(90, 900),
      script: `[On screen: the notebook page from the cold open.]
So here are the three lists together. The notebook never ended because it mixed actions and worries, and treated both as unfinished business. The grocery list ended because the store converted every worry into a decision and the door gave it an end. The note on the fridge ended because I wrote it for someone else, and that forced me to do the conversion myself.
Put those together, and the thesis holds. A to-do list is a record of what you are afraid to forget. It only becomes a plan when each worry is turned into a first action and the list is given a point where it stops.
Which leaves the question I started with: why did call the dentist sit there for a year? Not because I lacked discipline. Because it was a worry dressed as an errand, and I never asked what a stranger would do first.
When I finally asked, the answer was to search for a dentist near the new apartment. I did that this morning, before I recorded this. It took four minutes, and the appointment is on Tuesday.
The boxes by the door went to the donation drop-off on Saturday. The garage is not sorted out, and it does not need to be. It only needs a next action, written for a stranger.
[Cut to the notebook. The line is crossed out.]
There is a version of this argument about calendars, which make the opposite mistake and give everything an end, that I did not get to here. That is the next essay.`,
    },
  ],
}

export const templates: Record<string, TemplateData> = {
  'gaming-lets-play': {
    slug: 'gaming-lets-play',
    title: 'Gaming Let\'s Play',
    metaTitle: 'Gaming Let\'s Play Script Template: Free Outline and Example',
    metaDescription: 'Free gaming let\'s play script template with a hook, commentary beats, and a full example to adapt. Copy it and fill in your own game and challenge.',
    category: 'Gaming',
    templateText: `[HOOK - 0:00 to 0:15]
"[SHOCKING STATEMENT or CHALLENGE SETUP about the game]"
Quick montage of the craziest moments from this session.

[CONTEXT SETUP - 0:15 to 0:45]
"So today we're playing [GAME NAME], and the goal is [OBJECTIVE/CHALLENGE]."
Briefly explain the rules or challenge you've set for yourself.
Show the game's title screen or character select.

[EARLY GAMEPLAY - 0:45 to 3:00]
Start playing and react naturally.
Point out interesting details: "[OBSERVATION about game mechanic, environment, or enemy]"
Build tension: "Okay, this part always gets me..."
Include a small win or funny moment to keep energy up.

[MID-VIDEO ESCALATION - 3:00 to 7:00]
Introduce a difficulty spike or unexpected event.
"Wait, WHAT? I did NOT expect [UNEXPECTED EVENT]."
React genuinely: frustration, excitement, or humor.
Provide commentary on your strategy: "Alright, new plan. I'm going to [STRATEGY]."

[CLIMAX MOMENT - 7:00 to 9:00]
The hardest boss fight, the final puzzle, or the big payoff.
Build tension with your voice and pacing.
"This is it. If I mess this up, it's over."
Show the full unedited attempt with raw reactions.

[OUTRO & TEASE - 9:00 to 10:00]
React to the outcome: celebrate or commiserate.
"If you enjoyed this, [CALL TO ACTION]."
Tease the next episode: "Next time, we're tackling [NEXT CHALLENGE]..."
End screen with subscribe prompt.`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:15)',
        purpose: 'Stop the scroll and promise value immediately. Gaming audiences decide within 5 seconds whether to stay.',
        tips: 'Use a clip from later in the video that shows a peak emotion moment. Avoid generic "hey guys" intros.',
      },
      {
        section: 'Context Setup (0:15-0:45)',
        purpose: 'Tell viewers exactly what they are about to watch so they commit to the video.',
        tips: 'Keep this under 30 seconds. State the game, the challenge, and what success looks like.',
      },
      {
        section: 'Early Gameplay (0:45-3:00)',
        purpose: 'Ease viewers in with lighter moments while establishing your personality and commentary style.',
        tips: 'Cut dead air and loading screens. Keep a small win or laugh every 30-45 seconds to sustain attention.',
      },
      {
        section: 'Mid-Video Escalation (3:00-7:00)',
        purpose: 'Raise the stakes and create genuine suspense. This is where most let\'s plays lose viewers.',
        tips: 'Narrate your thought process. Viewers stay when they feel invested in your decision-making.',
      },
      {
        section: 'Climax Moment (7:00-9:00)',
        purpose: 'Deliver the payoff promised in the hook. This is the emotional peak of the video.',
        tips: 'Do not over-edit this section. Audiences want authentic reactions, so show the full attempt.',
      },
      {
        section: 'Outro & Tease (9:00-10:00)',
        purpose: 'Convert viewers into subscribers and set up anticipation for the next video.',
        tips: 'Tease specific content for the next episode rather than a vague "see you next time."',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:15]
"I just found a glitch that lets you skip the entire Elden Ring DLC final boss, and FromSoft is going to patch this ANY day now."
Quick montage: character falling through the map, boss health bar disappearing, victory screen.

[CONTEXT SETUP - 0:15 to 0:45]
"So today we're playing Elden Ring: Shadow of the Erdtree, and the goal is to beat the final boss using only this one newly discovered skip. Nobody's done a full guide on it yet, so let's see if it actually works consistently."
Show the DLC title screen and character build.

[EARLY GAMEPLAY - 0:45 to 3:00]
Navigate to the skip location. "Alright, you need to get to this exact spot on the cliff edge. See this rock? That's your marker."
First attempt at the skip. "Okay, rolling... rolling... and... I fell. Classic. Let's try again."
Second attempt works. "YES! We're through. Look at this, we're completely out of bounds."

[MID-VIDEO ESCALATION - 3:00 to 7:00]
"Wait, there's actually enemies loaded down here? That's terrifying."
Navigating the void area. "Okay, new plan. I need to angle toward the boss arena from below. If I miss this jump, I'm back at the bonfire."
Multiple failed attempts with genuine frustration. "This is the fifth time. My hands are actually sweating."

[CLIMAX MOMENT - 7:00 to 9:00]
"This is it. One more jump. If I mess this up, I've wasted 40 minutes of footage."
Lands the skip. Boss loads in with zero health triggers. Victory screen appears.
"NO WAY. It actually worked! The boss just... died. That's the fastest kill of this boss on record."

[OUTRO & TEASE - 9:00 to 10:00]
"If you want me to try this on the other DLC bosses, smash that like button. Drop a comment telling me which boss to skip next."
"Next time, we're attempting this same skip in NG+7 where everything one-shots you. Subscribe so you don't miss it."`,
    commonMistakes: [
      'Starting with a long intro before showing any gameplay. Viewers leave within 10 seconds if they do not see action.',
      'Leaving in long stretches of uneventful gameplay without commentary or cuts.',
      'Forgetting to explain your thought process, which is what separates a let\'s play from a silent walkthrough.',
      'Over-editing reactions to the point they feel manufactured rather than genuine.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 10 minutes', value: `About ${fills(600)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:15' },
      { metric: 'Words per minute of finished video', value: `${paceNote(600)}` },
    ],
    faqs: [
      {
        question: 'How long should a gaming let\'s play video be?',
        answer: 'The sweet spot for let\'s play videos is 10-20 minutes. A shorter episode is easier to keep at a steady pace, and a longer one gives you room for a chapter that pays off a setup from earlier. Match your length to how much genuinely engaging content you have; never pad for runtime.',
      },
      {
        question: 'Should I script my let\'s play or improvise?',
        answer: 'Use a loose script or outline rather than a word-for-word script. Plan your hook, key talking points, and outro, but let your in-game reactions be natural. The template above gives you structure without killing spontaneity.',
      },
      {
        question: 'How do I keep slow gameplay sections watchable?',
        answer: 'Cut aggressively during slow sections, add commentary explaining strategy or lore, or use picture-in-picture to show your facecam reactions. If a section has no commentary value, speed it up or skip it entirely with a transition.',
      },
    ],
    keywords: [
      'gaming let\'s play script template',
      'youtube gaming video structure',
      'let\'s play script format',
      'gaming video retention tips',
      'how to script a let\'s play',
    ],
    relatedTemplates: ['challenge-30-days', 'reaction', 'storytime', 'youtube-shorts'],
    relatedGuides: ['youtube-hook-examples', 'youtube-watch-time-psychology'],
  },

  'product-review': {
    slug: 'product-review',
    title: 'Product Review',
    metaTitle: 'Product Review Script Template: Free Outline and Example',
    metaDescription: 'Free product review script template with a verdict-first structure, feature beats, and a full example. Copy it and fill in your own product and notes.',
    category: 'Reviews',
    templateText: `[HOOK - 0:00 to 0:20]
"[PRODUCT NAME]: is it worth [PRICE]? After [TIME PERIOD] of daily use, here's my honest verdict."
Show the product in use with quick B-roll cuts.

[QUICK VERDICT - 0:20 to 0:45]
"Let me give you the short answer right now: [1-SENTENCE VERDICT]."
"But the full story is more nuanced, and there's [NUMBER] things you NEED to know before buying."

[UNBOXING / FIRST IMPRESSIONS - 0:45 to 2:00]
Show the packaging and what's included.
"Build quality feels [ASSESSMENT]. Compared to [COMPETITOR], it's [COMPARISON]."
Highlight the design, materials, and first-touch impressions.

[KEY FEATURE #1 - 2:00 to 3:30]
"The main reason you'd buy this is [PRIMARY FEATURE]."
Show it in action with real-world usage footage.
Give specific numbers or measurements where possible.
"Compared to [COMPETITOR], [SPECIFIC COMPARISON]."

[KEY FEATURE #2 - 3:30 to 5:00]
"The second thing worth talking about is [SECONDARY FEATURE]."
Demonstrate with B-roll.
Address a common concern: "A lot of people asked about [CONCERN]. Here's what I found..."

[KEY FEATURE #3 / DEAL-BREAKER CHECK - 5:00 to 6:30]
"Now here's where it gets interesting: [CONTROVERSIAL or UNEXPECTED FINDING]."
Be specific about limitations: "[PRODUCT] does NOT do [EXPECTED FEATURE], and here's why that matters."

[WHO IS THIS FOR? - 6:30 to 7:30]
"Buy this if you [USE CASE 1] or [USE CASE 2]."
"Skip this if you [ANTI-USE CASE]."
Compare to 1-2 alternatives briefly.

[FINAL VERDICT & CTA - 7:30 to 8:00]
"After [TIME PERIOD] with [PRODUCT], my score is [SCORE/RATING]."
"If you're buying, link is in the description."
"[CALL TO ACTION], and tell me in the comments what you want me to review next."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'Answer the viewer\'s core question ("is this worth it?") immediately to prove this video has the answer.',
        tips: 'Mention the price and usage period upfront. A review built on long-term use shows more than a day-one impression can.',
      },
      {
        section: 'Quick Verdict (0:20-0:45)',
        purpose: 'Give impatient viewers the answer fast, but create a curiosity gap so they stay for details.',
        tips: 'The "but there\'s X things you need to know" line leaves an open question, which gives the viewer a reason to keep going past the 1-minute mark.',
      },
      {
        section: 'Unboxing / First Impressions (0:45-2:00)',
        purpose: 'Satisfy the visual curiosity about the product and establish credibility through hands-on footage.',
        tips: 'Use close-up B-roll with good lighting. Compare build quality to something the viewer already knows.',
      },
      {
        section: 'Key Features (2:00-6:30)',
        purpose: 'Deep-dive into the features that matter most for the purchase decision.',
        tips: 'Lead with the strongest feature. Use specific numbers rather than vague adjectives. Always compare to a competitor.',
      },
      {
        section: 'Who Is This For? (6:30-7:30)',
        purpose: 'Help viewers self-select. This is the section that drives actual purchase decisions.',
        tips: 'Be direct about who should NOT buy this. Honest "skip this if" recommendations build long-term audience trust.',
      },
      {
        section: 'Final Verdict & CTA (7:30-8:00)',
        purpose: 'Deliver a clear, memorable conclusion and convert viewers into subscribers or buyers.',
        tips: 'Restate your verdict in one sentence. Do not ramble; end decisively.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20]
"The Sony WH-1000XM5: are they worth $350? After 6 months of daily use on flights, trains, and in the office, here's my honest verdict."
Quick cuts: headphones on a plane, at a desk, on a morning run.

[QUICK VERDICT - 0:20 to 0:45]
"Let me give you the short answer right now: these are the best noise-cancelling headphones most people can buy, but they are NOT perfect."
"There are 3 things you need to know before buying, especially if you're coming from the XM4s."

[UNBOXING / FIRST IMPRESSIONS - 0:45 to 2:00]
Show the case and accessories. "Build quality is lighter and more plasticky than the XM4. They don't fold anymore, so this case is huge. Compared to the AirPods Max, they feel less premium but weigh 100g less."

[KEY FEATURE #1 - 2:00 to 3:30]
"The main reason you'd buy these is the noise cancellation. I tested them on 12 flights and in 3 different office environments."
Show noise tests with a decibel meter. "They block about 3dB more low-frequency noise than the XM4 and roughly match the AirPods Max."

[KEY FEATURE #2 - 3:30 to 5:00]
"Sound quality with the new 30mm drivers is warmer and more detailed. But here's what matters: the LDAC codec support means if you have an Android phone, you're getting near-lossless audio."
"A lot of people asked about the multipoint connection. It works flawlessly now. Switching between my laptop and phone takes about 2 seconds."

[KEY FEATURE #3 / DEAL-BREAKER CHECK - 5:00 to 6:30]
"Now here's where it gets interesting: the headband design. They don't fold, which is a genuine dealbreaker for some travelers. My carry-on pocket that fit the XM4 case does NOT fit these."

[WHO IS THIS FOR? - 6:30 to 7:30]
"Buy these if you prioritize noise cancellation and comfort for long sessions, or if you're an Android user who wants LDAC."
"Skip these if you need folding headphones for travel, or if you already own the XM4, since the upgrade is marginal."

[FINAL VERDICT & CTA - 7:30 to 8:00]
"After 6 months with the XM5, my score is 8.5 out of 10. Best in class, but that headband redesign costs them a perfect score."
"Buying link is in the description. Subscribe and tell me what headphones you want me to compare these to next."`,
    commonMistakes: [
      'Burying the verdict at the end. Viewers click away if they have to watch 8 minutes to find out if a product is worth buying.',
      'Only covering positives to protect affiliate relationships, which destroys audience trust.',
      'Using vague language like "the camera is good" instead of specific comparisons and measurements.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 8 minutes', value: `About ${fills(480)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(480)}` },
    ],
    faqs: [
      {
        question: 'Should I give the verdict at the beginning or end of a product review?',
        answer: 'Give a quick verdict early (within the first 30-45 seconds) and a detailed verdict at the end. The early verdict keeps impatient viewers from leaving, while the "but there\'s more to know" framing gives them a reason to stay for the full review.',
      },
      {
        question: 'How do I structure a product review for maximum watch time?',
        answer: 'Use the verdict-first structure: hook with a quick opinion, then go deep on 2-3 key features with real-world footage, address deal-breakers honestly, and close with a "who should buy this" recommendation. This template keeps viewers engaged because each section answers a different buying question.',
      },
      {
        question: 'How long should a YouTube product review be?',
        answer: 'Most product reviews perform best at 7-12 minutes. Go shorter (5-7 min) for simple products and longer (12-18 min) for complex tech or comparisons. Always match length to the product complexity: a phone review warrants more time than a phone case review.',
      },
    ],
    keywords: [
      'product review script template',
      'youtube review video structure',
      'how to script a product review',
      'tech review template youtube',
      'product review video format',
    ],
    relatedTemplates: ['unboxing', 'before-after-transformation', 'comparison-video', 'top-10-list'],
    relatedGuides: ['script-structure-guide', 'youtube-retention-guide'],
  },

  'tutorial': {
    slug: 'tutorial',
    title: 'Tutorial / How-To',
    metaTitle: 'Tutorial Script Template for YouTube (Free, Copy-Paste)',
    metaDescription: 'Free tutorial script template with a step-by-step structure, a troubleshooting beat, and a full worked example. Copy it and fill in your own steps.',
    category: 'Education',
    templateText: `[HOOK - 0:00 to 0:15]
"In this video, I'll show you exactly how to [DESIRED OUTCOME], even if you're a complete beginner."
Show the finished result upfront so viewers know what they'll achieve.

[WHY THIS MATTERS - 0:15 to 0:30]
"Most people get stuck on [COMMON PAIN POINT], and the usual advice of [BAD ADVICE] doesn't actually work."
"I'm going to show you the method that [CREDIBILITY STATEMENT]."

[PREREQUISITES - 0:30 to 1:00]
"Before we start, you'll need: [TOOL/SOFTWARE 1], [TOOL/SOFTWARE 2], and [RESOURCE]."
"Links to everything are in the description."
Show the starting point so viewers can follow along.

[STEP 1 - 1:00 to 3:00]
"Step one: [ACTION VERB] your [THING]."
Walk through the step slowly with screen recording or demonstration.
Explain WHY this step matters in addition to WHAT to do.
"A common mistake here is [MISTAKE]. Instead, do [CORRECT APPROACH]."

[STEP 2 - 3:00 to 5:00]
"Step two: [ACTION VERB] the [THING]."
Build on step one with clear transitions.
Use zoom-ins or annotations to highlight key details.
"If yours looks like [WRONG RESULT], go back and check [SPECIFIC THING]."

[STEP 3 - 5:00 to 7:00]
"Step three: [ACTION VERB] to [RESULT]."
This is usually the most complex step, so slow down and be extra clear.
Offer an alternative method: "If you prefer, you can also [ALTERNATIVE APPROACH]."

[RESULTS & TROUBLESHOOTING - 7:00 to 8:00]
"Here's the final result." Show before and after.
"If you're running into [COMMON ISSUE 1], try [FIX]."
"For [COMMON ISSUE 2], the solution is [FIX]."

[OUTRO & NEXT STEPS - 8:00 to 8:30]
"Now that you know how to [BASIC OUTCOME], the next step is [ADVANCED TOPIC]."
"I have a video on that right here [POINT TO END SCREEN]."
"Drop a comment if you have questions. I reply to everything."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:15)',
        purpose: 'Promise a clear, achievable outcome. Tutorial viewers are searching for a specific result.',
        tips: 'Show the final result in the first 5 seconds. A how-to viewer who sees the outcome knows the video has the answer they came for.',
      },
      {
        section: 'Why This Matters (0:15-0:30)',
        purpose: 'Validate the viewer\'s problem and differentiate your approach from what they\'ve already tried.',
        tips: 'Reference the common bad advice they\'ve seen elsewhere. This builds trust and positions your method as superior.',
      },
      {
        section: 'Prerequisites (0:30-1:00)',
        purpose: 'Ensure viewers have what they need before starting, reducing frustration and drop-off mid-tutorial.',
        tips: 'Keep this brief and link everything in the description. Do not make viewers hunt for tools.',
      },
      {
        section: 'Steps (1:00-7:00)',
        purpose: 'Deliver the core tutorial content in a logical, easy-to-follow sequence.',
        tips: 'Number your steps clearly. Explain the "why" behind each action. Anticipate mistakes and address them proactively.',
      },
      {
        section: 'Results & Troubleshooting (7:00-8:00)',
        purpose: 'Show the payoff and preemptively answer common questions that would otherwise go to comments.',
        tips: 'Before-and-after comparisons are powerful. List the top 2-3 issues people encounter.',
      },
      {
        section: 'Outro & Next Steps (8:00-8:30)',
        purpose: 'Convert one-time tutorial viewers into subscribers by linking to the logical next learning step.',
        tips: 'Tutorials drive the most subscriptions when you create a clear learning path viewers can follow.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:15]
"In this video, I'll show you exactly how to remove any background from a photo in Photoshop, even if you've never used the software before."
Show a complex photo with hair and transparency, then the clean cutout result.

[WHY THIS MATTERS - 0:15 to 0:30]
"Most tutorials tell you to use the Magic Wand tool, which leaves horrible jagged edges on hair and fur."
"I'm going to show you the Refine Edge method that professional retouchers actually use."

[PREREQUISITES - 0:30 to 1:00]
"You'll need Photoshop CC. Any version from 2020 or later works. If you don't have it, there's a free trial link in the description."
"Open your image and make sure you're on the Background layer."

[STEP 1 - 1:00 to 3:00]
"Step one: Select your subject. Go to Select > Subject. Photoshop's AI will make a rough selection."
Demonstrate on screen. "See how it got the body but missed some hair strands? That's normal, we'll fix that next."
"A common mistake here is trying to manually lasso around every hair strand. Don't do that, it'll take forever and look worse."

[STEP 2 - 3:00 to 5:00]
"Step two: Open Select and Mask. Click the 'Select and Mask' button in the options bar."
"Set View to 'On Black' so you can see the edges clearly."
Zoom into the hair. "Use the Refine Edge Brush and paint over the hair boundary like this."
"If yours looks too smooth with no hair detail, increase the radius to about 5 pixels."

[STEP 3 - 5:00 to 7:00]
"Step three: Output as a new layer with a mask. In the Output Settings, choose 'New Layer with Layer Mask' and check 'Decontaminate Colors.'"
"This removes the color fringing you'd normally get around edges."
"If you prefer a non-destructive workflow, you can skip Decontaminate Colors and clean the edges manually with a soft brush on the mask."

[RESULTS & TROUBLESHOOTING - 7:00 to 8:00]
Show the before and after side by side. "Clean edges, preserved hair detail, no color fringing."
"If you're getting a white halo around the edges, go back to Select and Mask and increase the Shift Edge slider to -30%."
"For dark backgrounds that bleed into the subject, try the 'On White' view to spot issues."

[OUTRO & NEXT STEPS - 8:00 to 8:30]
"Now that you can cut out any subject, the next step is compositing: placing your cutout onto a new background realistically. I have a full tutorial on that right here."
"Drop a comment if you have questions. I reply to everything."`,
    commonMistakes: [
      'Skipping the finished result at the beginning. Tutorial viewers need proof that your method works before investing their time.',
      'Moving too fast through steps without explaining why each action matters.',
      'Not anticipating common errors, which causes viewers to get stuck and leave frustrated.',
      'Making the tutorial longer than necessary by over-explaining simple steps while rushing complex ones.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 8 minutes', value: `About ${fills(480)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:15' },
      { metric: 'Words per minute of finished video', value: `${paceNote(480)}` },
    ],
    faqs: [
      {
        question: 'What makes a good YouTube tutorial script?',
        answer: 'A good tutorial script shows the end result first, lists prerequisites briefly, numbers every step clearly, explains the "why" behind each action, and anticipates common mistakes. The key differentiator is pacing: slow down on complex steps and speed through obvious ones.',
      },
      {
        question: 'How do I optimize a tutorial video for YouTube search?',
        answer: 'Start your title with the exact search phrase viewers use (e.g., "How to Remove Background in Photoshop"). Repeat the key phrase in your hook, description, and tags. Timestamps for each step improve both SEO and viewer experience.',
      },
      {
        question: 'Should I use chapters in tutorial videos?',
        answer: 'Yes, always. YouTube chapters (timestamps in the description) let a viewer jump straight to the step they need, which matters most when someone is following along with the video paused.',
      },
    ],
    keywords: [
      'youtube tutorial script template',
      'how to video script format',
      'tutorial video structure',
      'youtube how-to template',
      'educational video script',
      'step by step video template',
    ],
    relatedTemplates: ['software-tutorial', 'beginner-guide', 'educational-explainer', 'product-review'],
    relatedGuides: ['youtube-tutorial-scripts', 'script-structure-guide'],
  },

  'youtube-shorts': {
    slug: 'youtube-shorts',
    title: 'YouTube Shorts',
    metaTitle: 'YouTube Shorts Script Template: Free Outline and Example',
    metaDescription: 'Free YouTube Shorts script template optimized for vertical video. Hook viewers in 1 second and deliver value in under 60 seconds.',
    category: 'Short-Form',
    templateText: `[HOOK - 0:00 to 0:02]
[VISUAL HOOK: Start with the most surprising visual or action mid-motion]
Text overlay: "[BOLD CLAIM or QUESTION]"

[CONTEXT - 0:02 to 0:05]
"[ONE-SENTENCE SETUP explaining what viewer is about to see]"

[CORE CONTENT - 0:05 to 0:40]
Deliver your main point, tip, or story in rapid-fire pacing.
- Point 1: "[KEY INFORMATION]" + visual demonstration
- Point 2: "[KEY INFORMATION]" + visual demonstration
- Point 3 (if applicable): "[KEY INFORMATION]" + visual demonstration
Keep each point under 10 seconds. Use jump cuts to maintain pace.

[PAYOFF - 0:40 to 0:50]
"[SURPRISING RESULT, PUNCHLINE, or FINAL REVEAL]"
Show the before/after or the final outcome.

[LOOP / CTA - 0:50 to 0:58]
"[ONE-LINE CTA: Follow for more, or ask a question to drive comments]"
Design the last frame to visually connect back to the first frame for a seamless loop.`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:02)',
        purpose: 'In Shorts, you have 1-2 seconds before the viewer swipes. The hook must be visual, not verbal.',
        tips: 'Start mid-action. Never begin with a still frame or "hey guys." Use text overlays for context since many viewers watch without sound.',
      },
      {
        section: 'Context (0:02-0:05)',
        purpose: 'Give viewers just enough information to understand what they are watching without slowing the pace.',
        tips: 'One sentence maximum. If it takes longer to explain, simplify your concept.',
      },
      {
        section: 'Core Content (0:05-0:40)',
        purpose: 'Deliver the actual value. This is what viewers will share or save.',
        tips: 'Cut every pause and filler word. Use B-roll, text overlays, and quick zooms to maintain visual novelty every 3-5 seconds.',
      },
      {
        section: 'Payoff (0:40-0:50)',
        purpose: 'Deliver the surprising result or conclusion that makes the Short feel worth watching.',
        tips: 'The payoff should exceed expectations set in the hook. Under-delivering here tanks your completion rate.',
      },
      {
        section: 'Loop / CTA (0:50-0:58)',
        purpose: 'Give the viewer a reason to watch again, or to follow for the next one.',
        tips: 'Design the ending to loop seamlessly into the beginning, so the last frame reads as a continuation of the first.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:02]
Close-up of hands speed-folding a shirt in one motion.
Text overlay: "Fold any shirt in 2 seconds"

[CONTEXT - 0:02 to 0:05]
"This Japanese folding method works on any shirt. Watch carefully."

[CORE CONTENT - 0:05 to 0:40]
- Step 1: "Pinch the shirt here and here." Demonstrate pinch points with a close-up.
- Step 2: "Cross your arms, then uncross." Show the crossing motion in slow motion, then full speed.
- Step 3: "Lay it down and tuck." Final fold in one motion.
- Show it on 3 different shirt types: T-shirt, dress shirt, polo. Each takes 2 seconds.

[PAYOFF - 0:40 to 0:50]
Side by side: messy pile of 10 shirts vs. a perfectly folded stack. "That took me 20 seconds for all 10."

[LOOP / CTA - 0:50 to 0:58]
"Follow for more life hacks you'll actually use."
Final frame: hands reaching for a shirt, matching the opening shot for a seamless loop.`,
    commonMistakes: [
      'Starting with a verbal intro instead of a visual hook. Viewers swipe before you finish saying "hey guys."',
      'Cramming too much content into 60 seconds, making it feel rushed and confusing rather than fast-paced and clear.',
      'Not adding text overlays, which alienates the large portion of viewers watching without sound.',
      'Ending abruptly without a loop or a CTA, so the final frame feels unrelated to the opening one.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills a 58 second Short', value: `About ${fills(58)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:02' },
      { metric: 'Words per minute of finished video', value: `${paceNote(58)}` },
    ],
    faqs: [
      {
        question: 'How long should a YouTube Short be?',
        answer: 'Shorts between 30 and 45 seconds are a practical default: long enough to deliver something and short enough to hold attention to the end. A 60-second Short has to keep earning the next second the whole way through, so use the longer runtime only when the idea genuinely needs it.',
      },
      {
        question: 'Do I need a script for YouTube Shorts?',
        answer: 'Yes, scripting is even more important for Shorts because every second counts. A 60-second Short has no room for filler. Write out your hook, key points, and payoff so you hit each beat precisely. Many top Shorts creators script down to the word.',
      },
      {
        question: 'How do I make a YouTube Short loop seamlessly?',
        answer: 'Design your last 2-3 seconds to visually and thematically connect to your first 2-3 seconds. Use matching camera angles, continue an action across the cut, or end with a phrase that flows into your opening line. A seamless loop makes the Short feel deliberate and gives a viewer who reached the end somewhere to go next.',
      },
    ],
    keywords: [
      'youtube shorts script template',
      'short form video script',
      'youtube shorts structure',
      'how to script youtube shorts',
      'vertical video template',
    ],
    relatedTemplates: ['channel-trailer', 'video-intro', 'recipe-video', 'workout-video'],
    relatedGuides: ['first-30-seconds', 'youtube-hook-examples'],
  },

  'top-10-list': {
    slug: 'top-10-list',
    title: 'Top 10 List',
    metaTitle: 'Top 10 List Script Template for YouTube (Free, Copy-Paste)',
    metaDescription: 'Free top 10 list script template with an ordering plan, hooks between items, and a full example. Copy it and fill in your own topic and entries.',
    category: 'Lists',
    templateText: `[HOOK - 0:00 to 0:20]
"[NUMBER] [TOPIC] that [PROMISE/BENEFIT], and number [TEASE NUMBER] completely changed my [OUTCOME]."
Quick montage previewing 3-4 items from the list.

[RULES / CONTEXT - 0:20 to 0:40]
"Before we start, here's how I ranked these: [CRITERIA]."
"I've [CREDIBILITY: tested/researched/used] every single one."
"Let me know in the comments if your favorite made the list."

[ITEM #10 - 0:40 to 1:30]
"Starting at number 10: [ITEM NAME]."
Explain what it is, why it's on the list, and one standout detail.
"The reason it's only at 10 is [LIMITATION]."

[ITEM #9 through #6 - 1:30 to 5:00]
One item per 45-60 seconds. For each:
- Name and quick visual
- What makes it noteworthy: "[SPECIFIC DETAIL or STAT]"
- How it compares to others on the list
- Transition hook: "But the next one is even better because..."

[MID-ROLL ENGAGEMENT - 5:00 to 5:15]
"We're halfway through. If you're finding value, a like really helps the video."
"Quick prediction: can you guess what number 1 is? Drop it in the comments."

[ITEM #5 through #2 - 5:15 to 8:30]
Increase depth and energy for higher-ranked items (60-90 seconds each).
More detailed explanations, better visuals, stronger opinions.
Build anticipation: "This next one is a fan favorite, and honestly, it almost took the top spot."

[ITEM #1 - 8:30 to 9:30]
"And the number 1 [TOPIC] is... [ITEM NAME]."
Explain in detail why it earned the top spot.
Give your strongest argument with evidence or demonstration.

[HONORABLE MENTIONS & OUTRO - 9:30 to 10:00]
"Honorable mentions: [2-3 ITEMS that almost made the list]."
"If you think I missed something, drop it in the comments."
"Subscribe for more [TOPIC] content. I drop new videos every [SCHEDULE]."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'Tease a specific item to create curiosity that keeps viewers watching through the entire list.',
        tips: 'Never reveal which item is #1. Tease a mid-list item (like #3 or #4) to create an open loop.',
      },
      {
        section: 'Rules / Context (0:20-0:40)',
        purpose: 'Establish your ranking criteria so viewers understand and respect your ordering.',
        tips: 'Ask viewers to comment their guess for #1 early; it gives people something to write before the reveal.',
      },
      {
        section: 'Lower Items #10-#6 (0:40-5:00)',
        purpose: 'Cover less impactful items quickly to maintain pacing and build toward stronger content.',
        tips: 'Keep these brisk at 45-60 seconds each. Use transitions that compare to the next item.',
      },
      {
        section: 'Mid-Roll Engagement (5:00-5:15)',
        purpose: 'Break the pattern to reset attention and give viewers something to respond to.',
        tips: 'Place this right before the top 5 when viewer curiosity is highest for a like or comment.',
      },
      {
        section: 'Top Items #5-#1 (5:15-9:30)',
        purpose: 'Deliver the most valuable content when viewers are most invested.',
        tips: 'Spend more time on top items. The #1 reveal should feel like a genuine payoff, not an anticlimax.',
      },
      {
        section: 'Honorable Mentions & Outro (9:30-10:00)',
        purpose: 'Acknowledge items that almost made it and convert viewers into subscribers.',
        tips: 'Honorable mentions drive comments because viewers love debating what should or should not be on the list.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20]
"10 free apps that replaced $500 worth of subscriptions, and number 3 completely changed how I manage my entire business."
Quick montage: app interfaces flashing across screen with price tags being crossed out.

[RULES / CONTEXT - 0:20 to 0:40]
"I ranked these by how much money they save and how well they actually replace the paid alternative. I've used every single one for at least 3 months."
"Let me know in the comments if your favorite free app made the list."

[ITEM #10 - 0:40 to 1:30]
"Starting at number 10: Photopea. It's a free browser-based Photoshop alternative that handles PSD files, layers, and even Photoshop plugins."
"It's at 10 because the interface can lag on large files, but for everyday edits it handles the same jobs as Photoshop."

[ITEMS #9-#6]
#9: DaVinci Resolve (replaces Premiere Pro, $22/mo saved)
#8: Obsidian (replaces Notion for notes, free vs $10/mo)
#7: Thunderbird (replaces paid email clients)
#6: LibreOffice (replaces Microsoft 365, $7/mo saved)

[MID-ROLL ENGAGEMENT - 5:00 to 5:15]
"Halfway through. If any of these saved you money, a like helps other people find this video."
"Can you guess number 1? I bet most of you won't get it."

[ITEMS #5-#2]
#5: Bitwarden (replaces 1Password, $3/mo saved)
#4: Figma starter plan (replaces basic Sketch, $10/mo saved)
#3: Hetzner + Coolify (replaces Vercel/Heroku, $50+/mo saved), with a detailed walkthrough
#2: Plausible Community Edition (replaces Google Analytics paid tiers)

[ITEM #1 - 8:30 to 9:30]
"Number 1: Litestream + SQLite. It replaces managed database services that can cost $50-200 per month. I run my entire SaaS backend on this for $4 per month in server costs."
Detailed breakdown of the architecture.

[HONORABLE MENTIONS & OUTRO - 9:30 to 10:00]
"Honorable mentions: GIMP, Blender, and VSCodium."
"If you think I missed a free app that belongs on this list, drop it in the comments."
"Subscribe. I post a new productivity breakdown every Thursday."`,
    commonMistakes: [
      'Revealing the #1 pick in the hook or thumbnail, which eliminates the reason to watch the full video.',
      'Spending equal time on every item. Lower-ranked items should be brisk while top picks get more depth.',
      'Not using transitions between items, making the video feel like a disconnected slideshow.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 10 minutes', value: `About ${fills(600)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(600)}` },
    ],
    faqs: [
      {
        question: 'Should I count up or down in a top 10 list video?',
        answer: 'Count down from 10 to 1. Counting down creates ascending tension and gives viewers a reason to watch until the end. Counting up front-loads the best content and causes viewers to leave once they see #1, killing your average view duration.',
      },
      {
        question: 'How long should each item in a top 10 list be?',
        answer: 'Lower-ranked items (10-6) should be 45-60 seconds each. Higher-ranked items (5-2) should be 60-90 seconds. The #1 pick deserves 60-90 seconds of detailed coverage since it is the payoff your viewers waited for.',
      },
      {
        question: 'How do I keep a list video moving between items?',
        answer: 'Use transition hooks between items ("but the next one is even better because..."), tease a specific higher-ranked item in the hook, and put a prompt at the halfway point. Each of those gives the viewer a reason to move from one item to the next instead of leaving after the one they came for.',
      },
    ],
    keywords: [
      'top 10 list script template',
      'youtube listicle template',
      'top 10 video structure',
      'list video script format',
      'youtube ranking video template',
    ],
    relatedTemplates: ['book-summary', 'comparison-video', 'product-review', 'science-explainer'],
    relatedGuides: ['youtube-retention-guide', 'youtube-pattern-interrupts'],
  },

  'storytime': {
    slug: 'storytime',
    title: 'Storytime',
    metaTitle: 'Storytime Script Template for YouTube (Free, Copy-Paste)',
    metaDescription: 'Free storytime script template with a narrative arc, tension beats, and a full example. Copy it and fill in your own story and turning point.',
    category: 'Entertainment',
    templateText: `[COLD OPEN - 0:00 to 0:15]
"[DROP THE VIEWER INTO THE MOST INTENSE MOMENT OF THE STORY]."
"And that's when I realized... [CLIFFHANGER STATEMENT]."
"But let me back up. This all started when [QUICK CONTEXT]."

[SETUP - 0:15 to 1:30]
Introduce yourself in the context of the story (not a generic intro).
"So this happened [WHEN], and at the time I was [SITUATION]."
Set the scene: "[DESCRIBE THE SETTING vividly, with sights, sounds, feelings]."
Introduce key people: "[PERSON] was there, and they're the type of person who [CHARACTER DETAIL]."

[RISING ACTION - 1:30 to 4:00]
Build the story beat by beat:
"At first, everything was [NORMAL STATE]."
"Then [FIRST COMPLICATION] happened."
"I thought [INITIAL REACTION], so I decided to [ACTION]."
"But here's where it gets crazy: [ESCALATION]."
Layer in emotions: what you were feeling, thinking, fearing.

[CLIMAX - 4:00 to 6:00]
"So there I am, [VIVID DESCRIPTION of the peak moment]."
Slow down the pacing here and detail every second.
"[PERSON] looked at me and said [QUOTE]."
"And I [YOUR REACTION/DECISION in the critical moment]."
This section should be the longest and most detailed.

[FALLING ACTION / RESOLUTION - 6:00 to 7:30]
"After that, [WHAT HAPPENED NEXT]."
"Turns out, [REVEAL or EXPLANATION]."
Tie up loose ends: what happened to the people involved.

[REFLECTION & CTA - 7:30 to 8:00]
"Looking back, the lesson I took from this is [INSIGHT/MORAL]."
"Has anything like this ever happened to you? Tell me in the comments."
"If you want more stories like this, subscribe. I have a [RELATED STORY] coming next week."`,
    annotatedBreakdown: [
      {
        section: 'Cold Open (0:00-0:15)',
        purpose: 'Drop the viewer into the climax to create immediate curiosity, then loop back to the beginning.',
        tips: 'Use the in-medias-res technique: start at the most intense moment, then say "let me back up." Starting at the peak gives the story somewhere to fall back from.',
      },
      {
        section: 'Setup (0:15-1:30)',
        purpose: 'Build the world of the story so viewers are emotionally invested in what happens.',
        tips: 'Use sensory details: what you saw, heard, and felt. Generic setups bore viewers; specific details create immersion.',
      },
      {
        section: 'Rising Action (1:30-4:00)',
        purpose: 'Escalate tension with each new development, making the viewer need to know what happens next.',
        tips: 'Layer complications. Each new event should raise the stakes. Use your emotional reactions as a guide: if you were stressed, show that stress.',
      },
      {
        section: 'Climax (4:00-6:00)',
        purpose: 'Deliver the peak moment of the story with maximum detail and emotional intensity.',
        tips: 'Slow down during the climax. Describe individual seconds. Quote exact dialogue. This is where viewers feel the most payoff.',
      },
      {
        section: 'Resolution (6:00-7:30)',
        purpose: 'Answer all the questions the story raised and give viewers a sense of closure.',
        tips: 'Don\'t rush the ending. An abrupt resolution wastes the time the viewer has already spent with the story.',
      },
      {
        section: 'Reflection & CTA (7:30-8:00)',
        purpose: 'Extract meaning from the story and convert viewers into an engaged community.',
        tips: 'Ask viewers to share their similar experiences. Storytime videos generate the most comments when the CTA is a personal question.',
      },
    ],
    filledExample: `[COLD OPEN - 0:00 to 0:15]
"The security guard grabbed my arm and said 'You need to come with us.' I looked down at the shopping bag in my hand and realized I hadn't paid for any of it."
"And that's when I realized the self-checkout had glitched and I'd walked out with $200 of groceries without paying."
"But let me back up. This all started with me trying to do a quick grocery run."

[SETUP - 0:15 to 1:30]
"So this was last Tuesday. I'd just finished a 12-hour work day and needed to grab a few things for dinner. I went to the new Whole Foods near my apartment, the one that's fully automated with those self-checkout kiosks."
"The store was almost empty. Just me, a couple stocking shelves, and the security guard sitting by the door scrolling his phone."

[RISING ACTION - 1:30 to 4:00]
"I grabbed about 15 items, went to self-checkout, and started scanning. Everything was normal: beep, beep, item goes in the bag."
"Then the screen froze. I waited about 30 seconds, it came back, and showed my total: $0.00. I figured it was loading."
"I tapped 'Pay' and it said 'Payment Complete. Thank you.' I hadn't inserted my card or tapped my phone."
"I looked around. Nobody was watching. And here's where my brain did something stupid. Instead of flagging an employee, I thought 'I'll just sort this out at customer service on my way out.'"
"But there was no customer service desk at the exit. Just the door. And I walked through it."

[CLIMAX - 4:00 to 6:00]
"I got exactly four steps into the parking lot when I felt a hand on my shoulder. The security guard, the one I thought was scrolling his phone, had watched the entire thing on the ceiling camera."
"He said, 'Sir, did you pay for those items?' Very calm, very firm."
"My heart dropped. I started stammering about the screen freezing. He said, 'I need you to come back inside. We have a process for this.'"
"In that moment, I genuinely thought I was about to be arrested for shoplifting over a kiosk glitch."

[FALLING ACTION / RESOLUTION - 6:00 to 7:30]
"They brought me to a back office, reviewed the camera footage, and confirmed the kiosk screen had frozen. The manager actually apologized, re-scanned everything, and let me pay normally."
"Turns out, this had happened three times that week with the same kiosk. They'd already filed a maintenance ticket."
"I was in that back office for 45 minutes over a $47 grocery order."

[REFLECTION & CTA - 7:30 to 8:00]
"Looking back, I should have immediately flagged an employee instead of walking toward the exit. In the age of automated stores, a glitch can make you look like a shoplifter in seconds."
"Has anything like this happened to you with self-checkout? Tell me in the comments."
"Subscribe. Next week I'm telling the story of the time I accidentally boarded the wrong flight."`,
    commonMistakes: [
      'Starting the story chronologically instead of using a cold open. Linear storytelling without a hook loses viewers in the first 15 seconds.',
      'Telling the audience what you felt instead of showing it through your actions and reactions.',
      'Rushing through the climax to get to the point, when the climax should be the most detailed part.',
      'Ending abruptly without reflecting on why the story matters.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 8 minutes', value: `About ${fills(480)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:15' },
      { metric: 'Words per minute of finished video', value: `${paceNote(480)}` },
    ],
    faqs: [
      {
        question: 'How do I structure a storytime video?',
        answer: 'Use the in-medias-res technique: open with the most dramatic moment, then rewind to the beginning. This creates an immediate hook and a built-in reason for viewers to watch the entire video: they want to understand how you got to that moment.',
      },
      {
        question: 'How long should a YouTube storytime video be?',
        answer: 'Most storytime videos perform best at 8-12 minutes. Shorter stories (under 5 minutes) often lack the detail needed for emotional investment. Longer stories (over 15 minutes) risk losing pacing. Match your length to the complexity of the story.',
      },
      {
        question: 'How do I make my storytime video more engaging?',
        answer: 'Use sensory details (what you saw, heard, smelled), include direct quotes instead of paraphrasing, slow down during the climax, and layer in your genuine emotional reactions. The best storytime videos make the viewer feel like they were there.',
      },
    ],
    keywords: [
      'storytime script template',
      'youtube storytelling template',
      'storytime video structure',
      'how to tell stories on youtube',
      'narrative video script format',
    ],
    relatedTemplates: ['true-crime', 'history-story', 'faceless-narration', 'day-in-the-life-vlog'],
    relatedGuides: ['youtube-storytelling-narrative-structures', 'youtube-hook-examples'],
  },

  'day-in-the-life-vlog': {
    slug: 'day-in-the-life-vlog',
    title: 'Day in the Life Vlog',
    metaTitle: 'Day in the Life Vlog Script Template (Free, Copy-Paste)',
    metaDescription: 'Free day in the life vlog script template with a narrative structure, a beat sheet, and a full example. Copy it and fill in your own day and routines.',
    category: 'Vlogging',
    templateText: `[HOOK / COLD OPEN - 0:00 to 0:15]
"[INTRIGUING MOMENT from later in the day: something unusual, challenging, or exciting]."
Cinematic shot that sets the tone for the video.

[MORNING ROUTINE - 0:15 to 2:00]
"[TIME] AM. [OPENING LINE that establishes who you are and what makes this day special]."
Show your morning routine with quick cuts and no long, uneventful sequences.
Voiceover: "[WHAT YOU'RE WORKING ON TODAY / WHY THIS DAY MATTERS]."
Include one personal detail or habit that makes you relatable.

[MAIN ACTIVITY BLOCK 1 - 2:00 to 4:30]
"First up today: [PRIMARY TASK OR ACTIVITY]."
Show the process with a mix of wide shots and close-ups.
Add commentary explaining your decisions: "I'm doing [THIS] because [REASON]."
Include a small challenge or complication to create narrative tension.

[MIDDAY TRANSITION - 4:30 to 5:30]
Meal, commute, or break scene; keep it brief.
Use this as a breather moment with music and minimal talking.
Voiceover a reflection or lesson: "[INSIGHT about your work/life]."

[MAIN ACTIVITY BLOCK 2 - 5:30 to 8:00]
"The afternoon is where things get [INTERESTING/CHALLENGING]."
Second major activity or continuation of the first with a twist.
Show a genuine struggle or surprise: "[UNEXPECTED DEVELOPMENT]."
Include interaction with other people if possible: collaboration, conversation, etc.

[EVENING WIND-DOWN - 8:00 to 9:00]
"It's [TIME] PM, and [REFLECTION on how the day went]."
Show your evening routine briefly.
Tie back to the opening: "[CONNECT to the theme or hook from the beginning]."

[OUTRO - 9:00 to 9:30]
"If you want to see more days like this, subscribe."
"[QUESTION for the audience to answer in comments]."
"I'll see you in the next one."`,
    annotatedBreakdown: [
      {
        section: 'Hook / Cold Open (0:00-0:15)',
        purpose: 'Show viewers the most interesting moment of your day upfront to convince them the video is worth watching.',
        tips: 'Avoid starting with waking up and an alarm clock; it is the most overused vlog opening. Open with action.',
      },
      {
        section: 'Morning Routine (0:15-2:00)',
        purpose: 'Establish your identity and the day\'s context while building a personal connection.',
        tips: 'Keep morning routines under 2 minutes. Show what makes YOUR morning unique, not generic coffee-making footage.',
      },
      {
        section: 'Main Activity Blocks (2:00-8:00)',
        purpose: 'Deliver the core content: the activities, work, or experiences that define this particular day.',
        tips: 'Every activity block needs a mini narrative arc: setup, complication, resolution. Without conflict or challenge, vlogs become boring montages.',
      },
      {
        section: 'Midday Transition (4:30-5:30)',
        purpose: 'Give viewers a pacing break and provide a moment for reflection or insight.',
        tips: 'Use B-roll with music. This breathing room gives the narration somewhere to sit and lets the place make its own impression.',
      },
      {
        section: 'Evening Wind-Down (8:00-9:00)',
        purpose: 'Create closure by reflecting on the day and connecting back to the opening theme.',
        tips: 'The best vlogs have a narrative thread that ties the morning to the evening. State what you learned or how the day changed you.',
      },
      {
        section: 'Outro (9:00-9:30)',
        purpose: 'Convert viewers into subscribers and prompt engagement.',
        tips: 'Ask a specific question related to the vlog content rather than a generic "did you like this video?"',
      },
    ],
    filledExample: `[HOOK / COLD OPEN - 0:00 to 0:15]
Drone shot pulling back from a packed warehouse. "I just signed a lease on a 3,000 square foot warehouse, and I have 30 days to turn it into a functional studio."
Quick flash of the empty space, then the final result.

[MORNING ROUTINE - 0:15 to 2:00]
"5:30 AM. I've been up since 4 because I couldn't sleep. Today we're moving everything from my garage studio into the new space."
Quick cuts: coffee, loading the van, sunrise through the windshield.
"I've been running my video production business from a single-car garage for 2 years. Today that changes."

[MAIN ACTIVITY BLOCK 1 - 2:00 to 4:30]
"First load. I'm starting with the heavy stuff: lights, c-stands, the desk."
Timelapse of carrying equipment. "I'm doing this without movers because honestly, I can't afford them and the first month's rent."
A shelf bracket snaps while being installed. "Well, that's not great. This wall is drywall over concrete block, so I need different anchors."

[MIDDAY TRANSITION - 4:30 to 5:30]
Eating a sandwich on the warehouse floor surrounded by boxes.
Voiceover: "There's this weird feeling when you commit money to a space. It's equal parts exciting and terrifying. This is the first time the business has a real overhead cost."

[MAIN ACTIVITY BLOCK 2 - 5:30 to 8:00]
"The afternoon is where things get real. I'm wiring the electrical for the lighting grid."
Show the wiring process, consulting YouTube tutorials. "I learned how to do basic electrical from a video just like this one."
A friend stops by to help with the desk build. Conversation while working.
Unexpected: the internet provider can't install for 2 weeks. "I need internet to edit and upload. This is a problem."

[EVENING WIND-DOWN - 8:00 to 9:00]
"It's 9 PM, and the warehouse is barely usable. But standing in here with the lights on, it actually feels like a studio."
Panning shot of the progress. "This morning I was terrified. Tonight I'm just excited for tomorrow."

[OUTRO - 9:00 to 9:30]
"If you want to see the finished studio, subscribe. The next video is the full build-out."
"Have you ever made a big leap with your creative work? Tell me about it in the comments."
"I'll see you in the next one."`,
    commonMistakes: [
      'Filming everything chronologically without a narrative thread. Vlogs need a story, not a bare timeline.',
      'Including too much filler footage (long breakfast scenes, commute footage) without commentary or purpose.',
      'Not planning an interesting hook and opening with the alarm clock wake-up cliche.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 9 minutes 30 seconds', value: `About ${fills(570)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:15' },
      { metric: 'Words per minute of finished video', value: `${paceNote(570)}` },
    ],
    faqs: [
      {
        question: 'How do I make a day in the life vlog interesting?',
        answer: 'Every vlog needs a narrative thread: a challenge you are facing, a goal for the day, or a transformation from morning to evening. Film the complications and genuine moments of struggle rather than only the polished highlights. Viewers connect with authenticity and story arcs, not aesthetic B-roll alone.',
      },
      {
        question: 'How long should a day in the life vlog be?',
        answer: 'Aim for 8-12 minutes. Condense an entire day into the highlights that serve your narrative. Most days have about 10-15 minutes of genuinely interesting content. Cut everything else, no matter how good the footage looks.',
      },
      {
        question: 'Should I script a vlog?',
        answer: 'Outline rather than script. Plan your hook, the 2-3 main activity blocks, key voiceover reflections, and your outro. Let the in-the-moment dialogue be natural, but know the narrative arc before you start filming.',
      },
    ],
    keywords: [
      'day in the life vlog template',
      'youtube vlog script structure',
      'vlog outline template',
      'how to structure a vlog',
      'day in the life video format',
    ],
    relatedTemplates: ['travel-vlog', 'personal-finance', 'storytime', 'recipe-video'],
    relatedGuides: ['youtube-storytelling-narrative-structures', 'youtube-watch-time-psychology'],
  },

  'reaction': {
    slug: 'reaction',
    title: 'Reaction Video',
    metaTitle: 'Reaction Video Script Template: Free Outline and Example',
    metaDescription: 'Free reaction video script template with a commentary structure, pause beats, and a full example. Copy it and fill in the clip you are reacting to.',
    category: 'Entertainment',
    templateText: `[HOOK - 0:00 to 0:15]
"[REACTION CLIP: Show your most extreme genuine reaction from the video: shock, laughter, or disbelief]."
"I just watched [CONTENT TITLE] and I was NOT ready for [TEASE]."

[CONTEXT & EXPECTATIONS - 0:15 to 0:45]
"So [CONTEXT: why you're reacting to this (why it's trending, who recommended it, etc.)]."
"Going in, I [EXPECT/KNOW]: [EXPECTATIONS or BACKGROUND KNOWLEDGE]."
"My [EXPERTISE/PERSPECTIVE] is [RELEVANT BACKGROUND], so I'll be looking at [SPECIFIC ANGLE]."

[REACTION SEGMENT 1 - 0:45 to 3:00]
Play the first section of the content (show only what's needed for context).
Pause and comment: "[YOUR ANALYSIS or GENUINE REACTION]."
Add value with your expertise: "[INSIGHT the average viewer wouldn't know]."
"What I notice here that most people miss is [OBSERVATION]."

[REACTION SEGMENT 2 - 3:00 to 5:30]
Continue with the next key section.
Build on your earlier points: "This connects to what I said about [EARLIER POINT]."
React genuinely to surprises: "[AUTHENTIC REACTION]."
Deeper analysis: "[EXPLAIN WHY this works/doesn't work from your perspective]."

[REACTION SEGMENT 3 - 5:30 to 7:30]
Final section or climax of the original content.
Biggest reaction moment: "[PEAK EMOTIONAL RESPONSE]."
"I have to pause here because [DETAILED BREAKDOWN of what just happened]."
Connect to broader context: "[HOW THIS FITS into the larger trend/genre/discussion]."

[FINAL THOUGHTS - 7:30 to 8:30]
"Okay, overall thoughts on [CONTENT TITLE]."
Rate or summarize your reaction: "[VERDICT]."
"The thing that impressed me most was [HIGHLIGHT]."
"The thing I'd change is [CONSTRUCTIVE POINT]."

[CTA - 8:30 to 9:00]
"What should I react to next? Drop suggestions in the comments."
"If you want me to react to [RELATED CONTENT], hit subscribe."
"[LINK to the original content] is in the description if you want to watch the full thing."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:15)',
        purpose: 'Lead with your most authentic reaction clip to prove this video has genuine emotional moments.',
        tips: 'The hook clip should be from a later moment in the video. Viewers click on reaction videos to see YOUR reaction, so show it immediately.',
      },
      {
        section: 'Context & Expectations (0:15-0:45)',
        purpose: 'Establish why your reaction has value: your expertise, perspective, or unique angle.',
        tips: 'A music producer reacting to music or a chef reacting to cooking adds expertise. Even without expertise, share what makes your perspective interesting.',
      },
      {
        section: 'Reaction Segments (0:45-7:30)',
        purpose: 'Deliver the core reaction with genuine commentary and added value between reaction moments.',
        tips: 'Pause to add insight after key moments. The pause-and-analyze pattern is what separates great reaction content from someone sitting silently in front of a screen.',
      },
      {
        section: 'Final Thoughts (7:30-8:30)',
        purpose: 'Synthesize your reaction into a clear overall takeaway.',
        tips: 'Give a definitive opinion. Fence-sitting final thoughts leave the viewer with nothing to respond to.',
      },
      {
        section: 'CTA (8:30-9:00)',
        purpose: 'Let viewers drive your content calendar by suggesting what to react to next.',
        tips: 'Asking "what should I react to next?" generates high comment counts and gives you content ideas directly from your audience.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:15]
Clip of jaw-dropping reaction: "No. NO. There is no way they just did that in a music video."
"I just watched Kendrick Lamar's new video and as a filmmaker, I was NOT ready for that one-shot sequence."

[CONTEXT & EXPECTATIONS - 0:15 to 0:45]
"This video dropped 6 hours ago and it's already trending #1. Everyone's talking about the visuals."
"Going in, I know Dave Meyers directed this (he did HUMBLE and DNA), so I'm expecting high production value."
"I've been a cinematographer for 8 years, so I'll be breaking down the camera work, lighting, and visual effects."

[REACTION SEGMENT 1 - 0:45 to 3:00]
Watch the opening 30 seconds. Pause. "Okay, right away: that's a 12mm lens on a Steadicam. See how the edges warp? They're using that distortion intentionally to make the environment feel oppressive."
"What most people miss is the color grading shift: we went from 6500K daylight to 4000K tungsten in one continuous shot. That's a lighting change happening live."

[REACTION SEGMENT 2 - 3:00 to 5:30]
Watch the main verse section. "This connects to what I said about the wide lens: they're moving the camera in a complete 360 and there's NO crew visible. That means every crew member is moving in sync with the camera rotation."
React to a visual effect. "That transition? That was not CG. That was a practical in-camera effect using a mirror rig. You can see the seam if you pause at 2:47."

[REACTION SEGMENT 3 - 5:30 to 7:30]
Watch the climax. Biggest reaction: "This entire last sequence is ONE SHOT. No cuts. That's at least 90 seconds of choreographed camera movement, lighting changes, and performance, all in one take."
"I have to pause because this level of coordination is genuinely rare. This is on par with the Birdman long takes."

[FINAL THOUGHTS - 7:30 to 8:30]
"Overall, this is the most technically impressive music video I've seen this year. The cinematography alone justifies watching it 5 times."
"The thing that impressed me most was the live lighting changes during the one-shot sequences."
"The one thing I'd change: the final 10 seconds felt rushed compared to the rest. But that's a minor gripe."

[CTA - 8:30 to 9:00]
"What music video should I break down next? Drop it in the comments."
"If you want me to do a full behind-the-scenes analysis of how they lit this, hit subscribe."
"Link to the full music video is in the description."`,
    commonMistakes: [
      'Adding no commentary or insight beyond facial reactions. This is what separates valuable reaction content from lazy content.',
      'Showing too much of the original content without adding your own perspective between clips.',
      'Faking exaggerated reactions instead of being genuine, which audiences quickly detect and punish.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 9 minutes', value: `About ${fills(540)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:15' },
      { metric: 'Words per minute of finished video', value: `${paceNote(540)}` },
    ],
    faqs: [
      {
        question: 'How do I make reaction videos that add value?',
        answer: 'Bring expertise, context, or a unique perspective that the viewer cannot get by watching the original content alone. Pause between reaction moments to analyze, explain, or connect what you are seeing to broader knowledge. The best reaction videos are part reaction, part analysis.',
      },
      {
        question: 'How much of the original content should I show in a reaction video?',
        answer: 'Show only the clips necessary for context, and let your commentary make up the majority of the runtime. Keep each excerpt short and keep your own response to it inside the video, so what you publish reads as your argument rather than a rebroadcast.',
      },
      {
        question: 'How do I avoid copyright issues with reaction videos?',
        answer: 'Add substantial commentary and transformative analysis to every clip you show. Do not play extended portions of the original content without pausing to comment. Show your facecam alongside the content, and keep the original content clips brief. Link to the original in your description.',
      },
    ],
    keywords: [
      'reaction video script template',
      'youtube reaction format',
      'how to structure reaction videos',
      'reaction video commentary tips',
      'youtube reaction video template',
    ],
    relatedTemplates: ['news-commentary', 'video-essay', 'gaming-lets-play', 'podcast-clip'],
    relatedGuides: ['youtube-hook-examples', 'youtube-pattern-interrupts'],
  },

  'comparison-video': {
    slug: 'comparison-video',
    title: 'Comparison Video',
    metaTitle: 'Comparison Video Script Template: Free Outline and Example',
    metaDescription: 'Free YouTube comparison video script template for A vs B format. Structure head-to-head reviews that are fair, thorough, and retain viewers.',
    category: 'Reviews',
    templateText: `[HOOK - 0:00 to 0:20]
"[PRODUCT A] vs [PRODUCT B]: I bought both, tested them for [TIME PERIOD], and there's a clear winner."
Show both products side by side.
"But the answer depends on one thing: [KEY DIFFERENTIATOR]."

[WHY THIS COMPARISON - 0:20 to 0:50]
"If you're choosing between these two, you're probably stuck on [COMMON DILEMMA]."
"I'm going to compare them across [NUMBER] categories and give each one a score."
"Here are the categories: [CATEGORY 1], [CATEGORY 2], [CATEGORY 3], [CATEGORY 4], [CATEGORY 5]."

[CATEGORY 1: DESIGN / BUILD - 0:50 to 2:30]
Show both products with close-up B-roll.
"[PRODUCT A] has [SPECIFIC DETAIL]. [PRODUCT B] has [SPECIFIC DETAIL]."
"In daily use, [OBSERVATION about real-world difference]."
"Winner for [CATEGORY 1]: [PRODUCT]." Show a running scoreboard.

[CATEGORY 2: KEY FEATURE - 2:30 to 4:30]
"This is the category most people care about: [FEATURE NAME]."
Side-by-side demonstration or test footage.
Include specific measurements or data: "[PRODUCT A]: [METRIC]. [PRODUCT B]: [METRIC]."
"Winner for [CATEGORY 2]: [PRODUCT]." Update scoreboard.

[CATEGORY 3: SECONDARY FEATURE - 4:30 to 6:00]
"Next up: [FEATURE]."
Real-world test or demonstration.
"This is closer than you'd think: [NUANCED COMPARISON]."
"Winner for [CATEGORY 3]: [PRODUCT]." Update scoreboard.

[CATEGORY 4: VALUE / PRICE - 6:00 to 7:00]
"[PRODUCT A] costs [PRICE]. [PRODUCT B] costs [PRICE]. But cost per [RELEVANT UNIT] is what actually matters."
"When you factor in [ONGOING COSTS, ACCESSORIES, etc.], the real price difference is [AMOUNT]."
"Winner for value: [PRODUCT]." Update scoreboard.

[CATEGORY 5: WILD CARD - 7:00 to 7:45]
"The one thing nobody talks about: [UNEXPECTED CATEGORY like ecosystem, resale value, warranty, etc.]."
"This might not matter to everyone, but if [USE CASE], it's a dealbreaker."
"Winner: [PRODUCT]." Final scoreboard.

[FINAL VERDICT - 7:45 to 8:30]
"The final score is [PRODUCT A]: [SCORE] vs [PRODUCT B]: [SCORE]."
"But here's the nuance: buy [PRODUCT A] if [USE CASE]. Buy [PRODUCT B] if [DIFFERENT USE CASE]."
"For most people, I recommend [PRODUCT] because [REASON]."

[CTA - 8:30 to 9:00]
"Links to both are in the description."
"If there's a comparison you want me to do next, drop it in the comments."
"Subscribe for more head-to-head reviews like this."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'Confirm there is a clear winner while creating a curiosity gap about which one wins.',
        tips: 'Say "there\'s a clear winner" but do NOT reveal it. The promise of a definitive answer keeps viewers watching through all categories.',
      },
      {
        section: 'Why This Comparison (0:20-0:50)',
        purpose: 'Validate that you understand the viewer\'s dilemma and lay out a transparent scoring system.',
        tips: 'Listing the categories upfront serves as a mini table of contents and sets expectations for the video structure.',
      },
      {
        section: 'Category Comparisons (0:50-7:45)',
        purpose: 'Deliver fair, data-driven comparisons across multiple dimensions.',
        tips: 'Use a visual scoreboard that updates after each category. This gamifies the video and gives viewers a reason to watch every section.',
      },
      {
        section: 'Final Verdict (7:45-8:30)',
        purpose: 'Deliver the promised answer with nuance that helps different viewers make the right choice for them.',
        tips: 'Always provide a "buy A if... buy B if..." recommendation. One-size-fits-all verdicts feel less helpful than personalized guidance.',
      },
      {
        section: 'CTA (8:30-9:00)',
        purpose: 'Drive affiliate clicks and future content suggestions.',
        tips: 'Comparison viewers are usually deciding what to buy, so put the product links where the decision is being made.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20]
"MacBook Air M3 vs Dell XPS 14: I used both as my daily driver for 3 months, and there's a clear winner."
Show both laptops side by side on a clean desk.
"But the answer depends on one thing: whether you value battery life or port selection."

[WHY THIS COMPARISON - 0:20 to 0:50]
"These are the two most recommended laptops in the $1,200-$1,400 range. If you're choosing between them, you're probably stuck on the Apple vs Windows question."
"I'm comparing across 5 categories: Build Quality, Display, Performance, Battery Life, and Value."

[CATEGORY 1: BUILD QUALITY - 0:50 to 2:30]
"The MacBook Air is 2.7 pounds of unibody aluminum. The XPS 14 is 3.8 pounds with a carbon fiber palm rest and aluminum lid."
Close-ups of hinges, keyboards, trackpads. "In daily use, the MacBook feels noticeably more portable. The XPS keyboard has better travel though: 1.3mm vs 1.0mm."
"Winner for Build Quality: MacBook Air." Scoreboard: MacBook 1, XPS 0.

[CATEGORY 2: DISPLAY - 2:30 to 4:30]
Side-by-side display test with identical images. "The XPS 14 has a 14.5-inch 3.2K OLED panel. The MacBook Air has a 13.6-inch Liquid Retina IPS. The XPS wins on contrast ratio. It's not even close with OLED blacks."
Show color accuracy test results. "MacBook: Delta E 1.2. XPS: Delta E 0.8. Both excellent, but the OLED pops more."
"Winner for Display: Dell XPS 14." Scoreboard: MacBook 1, XPS 1.

[CATEGORIES 3-5 abbreviated for length]

[FINAL VERDICT - 7:45 to 8:30]
"Final score: MacBook Air M3: 3 points. Dell XPS 14: 2 points."
"But here's the nuance: buy the MacBook Air if you prioritize battery life and portability: 18 hours is unmatched. Buy the XPS 14 if you need an OLED display, more ports, and don't mind carrying a charger."
"For most people, I recommend the MacBook Air because battery life affects every single day of ownership."

[CTA - 8:30 to 9:00]
"Links to the best prices on both are in the description."
"What laptop comparison do you want next? Drop it in the comments."
"Subscribe for more head-to-head tech reviews."`,
    commonMistakes: [
      'Being obviously biased toward one product from the start, which makes viewers distrust your recommendation.',
      'Comparing specs on paper instead of showing real-world side-by-side tests and demonstrations.',
      'Not providing a nuanced verdict. Viewers with different needs require different recommendations.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 9 minutes', value: `About ${fills(540)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(540)}` },
    ],
    faqs: [
      {
        question: 'How do I structure a fair product comparison video?',
        answer: 'Define clear comparison categories upfront, test both products under identical conditions, use a visual scoreboard, and provide a nuanced final verdict with different recommendations for different use cases. Transparency about your testing methodology builds trust.',
      },
      {
        question: 'Should I reveal the winner at the beginning of a comparison video?',
        answer: 'No. Tease that there IS a clear winner in the hook, but do not reveal which product wins. The open question about which one comes out on top is what carries a comparison video. Save the final verdict for the last 60 to 90 seconds.',
      },
      {
        question: 'How many categories should a comparison video include?',
        answer: 'Between 4 and 6 categories works best. Fewer than 4 feels too shallow for a purchase decision. More than 6 makes the video drag and dilutes the importance of each category. Choose categories that map to the actual buying criteria your audience cares about.',
      },
    ],
    keywords: [
      'comparison video script template',
      'youtube vs video template',
      'product comparison format',
      'A vs B video structure',
      'head to head review template',
      'youtube comparison video script',
    ],
    relatedTemplates: ['science-explainer', 'educational-explainer', 'product-review', 'interview-episode'],
    relatedGuides: ['script-structure-guide', 'youtube-retention-guide'],
  },

  'podcast-clip': {
    slug: 'podcast-clip',
    title: 'Podcast Clip',
    metaTitle: 'Podcast Clip Script Template for YouTube (Free, Copy-Paste)',
    metaDescription: 'Free YouTube podcast clip script template for repurposing podcast highlights. Structure clips that stand alone and drive viewers to the full episode.',
    category: 'Podcasts',
    templateText: `[HOOK CLIP - 0:00 to 0:10]
[THE MOST PROVOCATIVE OR SURPRISING 10-SECOND CLIP from the conversation]
Text overlay: "[GUEST NAME] on [TOPIC]"

[CONTEXT CARD - 0:10 to 0:20]
Brief text or voiceover: "This is [GUEST NAME], [ONE-LINE CREDIBILITY]. On [PODCAST NAME], they revealed [TEASE]."
Transition into the conversation.

[SETUP QUESTION - 0:20 to 0:45]
Host: "[QUESTION that leads to the compelling answer]."
Show the guest listening / thinking.

[CORE CLIP - 0:45 to 5:00]
The main conversational segment, edited for clarity:
- Remove filler words ("um," "uh," "like") with jump cuts
- Keep the best 3-4 minutes of a longer conversation
- Add text overlays for key stats, names, or terms mentioned
- Include natural back-and-forth; don't cut out all host reactions

Guest: "[KEY QUOTE 1: the insightful or controversial statement]."
Host: "[BRIEF REACTION or FOLLOW-UP]."
Guest: "[KEY QUOTE 2: the deeper explanation or story]."
Guest: "[KEY QUOTE 3: the unexpected reveal or conclusion]."

[REACTION / HIGHLIGHT - 5:00 to 5:30]
Host: "[YOUR GENUINE REACTION to what was said]."
"That's [ASSESSMENT: surprising/fascinating/controversial] because [BRIEF CONTEXT]."

[CTA TO FULL EPISODE - 5:30 to 6:00]
"This is from episode [NUMBER] of [PODCAST NAME] with [GUEST]."
"The full conversation covers [2-3 OTHER TOPICS from the episode]."
"Link to the full episode is in the description, or watch it right here [END SCREEN]."
"Subscribe for more clips every [FREQUENCY]."`,
    annotatedBreakdown: [
      {
        section: 'Hook Clip (0:00-0:10)',
        purpose: 'Lead with the single most compelling moment to stop viewers from scrolling past.',
        tips: 'Choose a clip where the guest says something surprising, controversial, or emotionally charged. Avoid clips that require context to understand.',
      },
      {
        section: 'Context Card (0:10-0:20)',
        purpose: 'Quickly establish who is speaking and why their opinion matters.',
        tips: 'One line of credibility is enough: job title, achievement, or follower count. Do not over-explain.',
      },
      {
        section: 'Setup Question (0:20-0:45)',
        purpose: 'Frame the conversation so viewers who missed the beginning can follow along.',
        tips: 'If the original question was long or rambling, re-record a cleaner version or add it as a text overlay.',
      },
      {
        section: 'Core Clip (0:45-5:00)',
        purpose: 'Deliver the substantive content: the insights, stories, or debate that make this clip worth watching.',
        tips: 'Edit aggressively. Remove filler words, tangents, and dead air. Keep the host\'s reactions to maintain conversational flow but cut extended host monologues.',
      },
      {
        section: 'Reaction / Highlight (5:00-5:30)',
        purpose: 'Add the host\'s perspective to punctuate the clip and provide editorial framing.',
        tips: 'This is your chance to tell viewers why what they just heard matters. A brief reaction gives the clip a satisfying ending.',
      },
      {
        section: 'CTA to Full Episode (5:30-6:00)',
        purpose: 'Drive traffic to the full episode and convert clip viewers into podcast subscribers.',
        tips: 'Mention 2-3 OTHER topics from the full episode that are different from the clip topic. This gives viewers a new reason to watch the full version.',
      },
    ],
    filledExample: `[HOOK CLIP - 0:00 to 0:10]
Guest clip: "Every founder I know who raised last year regrets the terms. Every single one."
Text overlay: "A venture partner, on the fundraising market"

[CONTEXT CARD - 0:10 to 0:20]
"This is a venture partner who has sat on both sides of a Series A negotiation. On The Startup Pod, she explained why she is telling founders to slow down."

[SETUP QUESTION - 0:20 to 0:45]
Host: "There is a narrative that last year was a recovery for venture capital. But you are saying the reality on the ground is different?"

[CORE CLIP - 0:45 to 5:00]
Guest: "The narrative and the term sheets are two different things. More checks went out, and the terms got harsher."
Host: "Like what specifically?"
Guest: "Participating preferred with a full ratchet. For a Series A. That means the founders see nothing until the investors are paid first, so a modest exit leaves the team with nothing."
Text overlay: "Participating preferred = investors get paid first"
Guest: "Every founder I talked to who took those terms is now stuck. They cannot raise again at a higher valuation, and a small sale would not help the team."
Host: "So what should founders do instead?"
Guest: "Wait longer. Get to real revenue before taking institutional money. The founders doing well right now either never raised, or raised a small round and got to profitability."

[REACTION / HIGHLIGHT - 5:00 to 5:30]
Host: "That claim about founders regretting it? That is a strong statement from someone who sits on both sides of those negotiations. It says a lot about where the market actually is versus the press releases."

[CTA TO FULL EPISODE - 5:30 to 6:00]
"This is from an episode of The Startup Pod with this guest."
"In the full episode, she also talks about how she evaluates founding teams, and the one question she asks before every investment."
"Full episode link is in the description. Subscribe for clips every Tuesday and Thursday.`,
    commonMistakes: [
      'Choosing clips that require too much context from the full episode, making them confusing as standalone content.',
      'Not editing out filler words and tangents, which makes the clip feel slow compared to native YouTube content.',
      'Forgetting to add text overlays for key terms, names, and statistics mentioned only in audio.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 6 minutes', value: `About ${fills(360)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:10' },
      { metric: 'Words per minute of finished video', value: `${paceNote(360)}` },
    ],
    faqs: [
      {
        question: 'How long should a YouTube podcast clip be?',
        answer: 'The ideal podcast clip is 5-8 minutes. Under 5 minutes often lacks enough substance to feel satisfying. Over 8 minutes starts competing with the full episode. Choose a single topic or story arc and edit it tightly to that window.',
      },
      {
        question: 'How do I choose which podcast moments to clip?',
        answer: 'Look for moments with strong emotional reactions, surprising statements, practical advice, or story climaxes. The best clips can be understood without any prior context. If you need more than 20 seconds of setup, the clip probably does not stand alone well enough.',
      },
      {
        question: 'Should podcast clips be different from the full episode edit?',
        answer: 'Yes. Podcast clips need tighter editing, text overlays for key information, a hook clip at the start, and a standalone narrative arc. Simply trimming a timestamp from the full episode is not enough; treat the clip as its own piece of content.',
      },
    ],
    keywords: [
      'podcast clip script template',
      'youtube podcast highlight format',
      'podcast clip editing structure',
      'how to clip podcast for youtube',
      'podcast repurposing template',
    ],
    relatedTemplates: ['interview-episode', 'faceless-narration', 'storytime', 'comparison-video'],
    relatedGuides: ['youtube-hook-examples', 'youtube-pattern-interrupts'],
  },

  'educational-explainer': {
    slug: 'educational-explainer',
    title: 'Educational Explainer',
    metaTitle: 'Educational Explainer Script Template (Free, Copy-Paste)',
    metaDescription: 'A section by section educational explainer script template with a 10 minute structure, word budgets, an annotated example, and common mistakes.',
    category: 'Education',
    templateText: `[HOOK - 0:00 to 0:20 | about ${words(20, 600)} words]
"[THE COUNTERINTUITIVE FACT or QUESTION this video answers]"
Show the most visual version of the idea on screen: a diagram, a zoom, or a side by side.

[PROMISE AND ROAD MAP - 0:20 to 1:00 | about ${words(40, 600)} words]
"By the end of this video you will understand [CORE IDEA] well enough to [CONCRETE USE]."
"Here is the path: first [PART 1], then [PART 2], and finally [PART 3]."
Name the payoff so the viewer knows the video is finite and worth finishing.

[SET UP THE PROBLEM - 1:00 to 2:30 | about ${words(90, 600)} words]
Define every term the rest of the video depends on, before you use it.
"Most explanations start with [COMMON FRAMING]. That framing breaks because [REASON]."
Give one everyday example before you touch any abstraction.

[CORE IDEA ONE - 2:30 to 4:30 | about ${words(120, 600)} words]
State it plainly: "[IDEA ONE IN ONE SENTENCE]"
Then the mechanism: "This happens because [CAUSE]."
Then the proof on screen: "[DEMONSTRATION, DATA POINT, or WORKED EXAMPLE]."
Add a check-yourself line: "If that sounds odd, think of it like [ANALOGY]."

[CORE IDEA TWO - 4:30 to 6:30 | about ${words(120, 600)} words]
State it plainly: "[IDEA TWO IN ONE SENTENCE]"
Connect it back: "Now that we know [IDEA ONE], this follows because [LINK]."
On screen: the same diagram with the new piece added, so the viewer sees the build.

[CORE IDEA THREE OR THE EDGE CASE - 6:30 to 8:00 | about ${words(90, 600)} words]
"The part that surprises people is [EXCEPTION, LIMIT, or EDGE CASE]."
"Notice that this still fits, because [RECONCILIATION]."

[SYNTHESIS - 8:00 to 9:00 | about ${words(60, 600)} words]
Say the entire idea back in one plain paragraph with no jargon.
"Put simply: [ONE PARAGRAPH SUMMARY]."

[RECAP AND NEXT QUESTION - 9:00 to 10:00 | about ${words(60, 600)} words]
List the three ideas as one line each.
"Next question worth asking: [FOLLOW ON TOPIC]. That is the video I am making next."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'Explainer audiences arrive with a question. Answer with the surprising part of the answer first, then earn the right to explain it slowly.',
        tips: 'Lead with the fact, not the topic. "Ice is slippery because of a layer of water" stops the scroll; "today we are talking about ice" does not.',
      },
      {
        section: 'Promise and Road Map (0:20-1:00)',
        purpose: 'Tell the viewer what they will be able to do at the end, so they can decide the video is worth their time.',
        tips: 'Number the parts out loud. A stated structure gives viewers a reason to reach the next section instead of leaving at the first slow moment.',
      },
      {
        section: 'Set Up the Problem (1:00-2:30)',
        purpose: 'Define terms and show why the common explanation fails. This is the section that makes the rest of the video land.',
        tips: 'Use one concrete example before the first abstraction. Every undefined term you use early becomes confusion later.',
      },
      {
        section: 'Core Ideas (2:30-8:00)',
        purpose: 'Deliver the explanations in a fixed rhythm: plain statement, mechanism, proof, analogy.',
        tips: 'Keep the same order in every block so the viewer learns the rhythm. Put the visual on screen while you say the sentence, not after.',
      },
      {
        section: 'Synthesis (8:00-9:00)',
        purpose: 'Compress the whole video into one paragraph a viewer could repeat to a friend.',
        tips: 'If you cannot say it in one paragraph without notes, the script is not finished yet.',
      },
      {
        section: 'Recap and Next Question (9:00-10:00)',
        purpose: 'Close the loop on the promise and open the next video.',
        tips: 'Restate the three ideas in one line each, then name the follow on question instead of a generic sign off.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20 | about ${words(20, 600)} words]
"Scientists argued for 150 years about why ice is slippery, and the answer is stranger than the one in your textbook."
Show a slow motion skate blade, then a microscope view of ice.

[PROMISE AND ROAD MAP - 0:20 to 1:00 | about ${words(40, 600)} words]
"By the end of this video you will understand why you can walk on a frozen lake but not on your kitchen floor after a spill, and it is not because ice is smooth."
"First, why the textbook answer fails. Then, what really happens at the surface. Finally, why the same physics makes a curling stone curl."

[SET UP THE PROBLEM - 1:00 to 2:30 | about ${words(90, 600)} words]
"Your first science class probably told you pressure melts ice. That story has a problem: you slip on ice in flat shoes at minus ten degrees, where the pressure is nowhere near enough."
The term we need is premelting, meaning the top layer of a solid behaves like a liquid before it melts.
"Think of a bowling ball resting on a mattress. The mattress sags under it without becoming a liquid."

[CORE IDEA ONE - 2:30 to 4:30 | about ${words(120, 600)} words]
"Ice has a liquid like layer on its surface even below freezing."
"This happens because water molecules at the surface have nothing above them to bond to, so they stay mobile."
On screen: a simulation of a crystal lattice with loose molecules on the top row.

[CORE IDEA TWO - 4:30 to 6:30 | about ${words(120, 600)} words]
"Friction removes that liquid layer."
"Now that we know the surface is already slippery, we can see why stopping on ice is so hard: your skate pushes the loose molecules aside and briefly touches solid, then slides again."
On screen: the same lattice with a blade passing over it.

[CORE IDEA THREE OR THE EDGE CASE - 6:30 to 8:00 | about ${words(90, 600)} words]
"The part that surprises people is that cold ice is slipperier than ice near melting."
"Notice that this still fits, because the colder the surface, the thinner the liquid layer, and the more the blade sits on solid crystal."

[SYNTHESIS - 8:00 to 9:00 | about ${words(60, 600)} words]
"Put simply: ice is slippery because its surface never fully freezes, friction is what turns that soft surface into water, and pressure plays a smaller role than your textbook claimed."

[RECAP AND NEXT QUESTION - 9:00 to 10:00 | about ${words(60, 600)} words]
"No textbook pressure story. A liquid like surface that exists before anything melts. And friction doing the real work."
"Next question worth asking: why does a curling stone curl? That is the video I am making next."`,
    commonMistakes: [
      'Opening with the topic instead of the surprising part of the answer. Explainer viewers decide in the first sentence whether you know something they do not.',
      'Using a term before defining it, then defining it five minutes later. Definitions belong before first use, not after.',
      'Showing the diagram after the explanation instead of while it is spoken, which forces viewers to hold the idea in their head with nothing to look at.',
      'Ending without a one paragraph synthesis, leaving viewers with a list of facts rather than one idea they can repeat.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 10 minutes', value: `About ${fills(600)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(600)}` },
    ],
    faqs: [
      {
        question: 'How long should an educational explainer script be?',
        answer: `For a 10 minute explainer, plan for about ${fills(600)} words of narration, at ${paceProse(600)}. Shorter explainers work better for one idea, while a full topic survey usually needs 12 to 18 minutes. Match the runtime to how many distinct ideas you actually have, and cut sections rather than speeding up the delivery.`,
      },
      {
        question: 'Should I explain the structure of my video at the start?',
        answer: 'Yes. Naming the parts out loud, in order, tells viewers the video is finite and gives them a reason to reach the next section. Keep it to two sentences and use concrete section names rather than a vague promise to cover everything.',
      },
      {
        question: 'How do I keep an explainer from feeling like a lecture?',
        answer: 'Change what is on screen every 20 to 30 seconds, and alternate between statement, mechanism, and proof inside each block. A lecture is one voice and one visual for minutes at a time. An explainer keeps the spoken explanation steady while the visual evidence moves.',
      },
      {
        question: 'How many ideas should one explainer cover?',
        answer: 'Three core ideas plus a synthesis fits comfortably in 10 minutes. Beyond that, the synthesis stops being sayable in one paragraph, which is the signal to split the topic into two videos.',
      },
    ],
    keywords: [
      'educational explainer script template',
      'explainer video script structure',
      'how to write an explainer video',
      'educational video script format',
      'explainer narration word budget',
    ],
    relatedTemplates: ['software-tutorial', 'science-explainer', 'beginner-guide', 'tutorial'],
    relatedGuides: ['how-to-write-a-youtube-script', 'script-structure-guide'],
  },

  'video-essay': {
    slug: 'video-essay',
    title: 'Video Essay',
    metaTitle: 'Video Essay Script Example + Template (Full Script)',
    metaDescription: 'A complete video essay script example, written beat by beat, plus the template: cold open, thesis, three evidence blocks, counterargument, synthesis.',
    category: 'Documentary',
    templateText: `[COLD OPEN - 0:00 to 0:45 | about ${words(45, 900)} words]
Open on the strangest concrete detail in your subject. No introduction, no name, no context.
"[SPECIFIC MOMENT, NUMBER, or SCENE that the whole essay is about]"
Let the first 20 seconds sit on one image with narration over it.

[THESIS - 0:45 to 2:00 | about ${words(75, 900)} words]
"Everyone says [THE COMMON READING]. I think that is wrong, and here is why."
State the claim in one sentence the viewer can disagree with.
"To show you, I am going to look at [THREE ANGLE ONE], [ANGLE TWO], and [ANGLE THREE]."

[EVIDENCE ONE - 2:00 to 5:00 | about ${words(180, 900)} words]
"[THE FIRST PIECE OF EVIDENCE]"
Walk through it in order, quoting or showing the source on screen.
"So what does this tell us? [INTERPRETATION]"
"This matters because [WHY IT SUPPORTS THE THESIS]."

[EVIDENCE TWO - 5:00 to 8:00 | about ${words(180, 900)} words]
"[THE SECOND PIECE OF EVIDENCE, which should complicate the first]"
Show the tension out loud: "Notice that this cuts against what we just saw."
"Both can be true at once, and that is the interesting part."

[EVIDENCE THREE - 8:00 to 11:00 | about ${words(180, 900)} words]
"[THE THIRD PIECE, ideally a case nobody applies your thesis to yet]"

[COUNTERARGUMENT - 11:00 to 13:30 | about ${words(150, 900)} words]
"The strongest case against me is [BEST OPPOSING ARGUMENT], and it is a good one."
Here is where it fails: "[SPECIFIC LIMIT, NOT A STRAW MAN]."

[SYNTHESIS AND OUTRO - 13:30 to 15:00 | about ${words(90, 900)} words]
Bring the three pieces together into one paragraph.
"Which leaves the question I started with: [RESTATE THE OPENING MOMENT]."
"There is a version of this argument I did not get to, and that is [NEXT ESSAY]."`,
    annotatedBreakdown: [
      {
        section: 'Cold Open (0:00-0:45)',
        purpose: 'Buy the viewer with a concrete detail before you tell them what the video is about. Essays that open with a title card lose the people who would have loved the argument.',
        tips: 'Pick the single strangest fact you found in research and open there. Resist explaining it for the first 45 seconds.',
      },
      {
        section: 'Thesis (0:45-2:00)',
        purpose: 'Give the viewer a claim they can hold, agree with, or argue against. A thesis is what separates an essay from a compilation of clips.',
        tips: 'One sentence, stated as an opinion. If it cannot be disagreed with, it is a topic, not a thesis.',
      },
      {
        section: 'Evidence Blocks (2:00-11:00)',
        purpose: 'Build the argument in three moves, where each piece of evidence keeps the previous one from being the whole story.',
        tips: 'Show sources on screen while you narrate them. Order matters: put the most surprising evidence third, once trust is established.',
      },
      {
        section: 'Counterargument (11:00-13:30)',
        purpose: 'State the strongest opposing case and answer it. This is what earns an essay its credibility.',
        tips: 'Argue the other side better than its own supporters would. A straw man reads as fear, not confidence.',
      },
      {
        section: 'Synthesis and Outro (13:30-15:00)',
        purpose: 'Close the loop on the opening image and leave the viewer with the claim, not a summary of clips.',
        tips: 'Return to the exact detail you opened with, now reframed by everything the viewer has seen.',
      },
    ],
    // The copyable Example tab holds the same full example, beat headers included.
    filledExample: videoEssayExample.beats
      .map((beat) => `[${beat.beat.toUpperCase()} - ${beat.timing} | about ${beat.budget} words]\n${beat.script}`)
      .join('\n\n'),
    fullExample: videoEssayExample,
    commonMistakes: [
      'Opening with a title card and an agenda instead of the most striking concrete detail you found. The cold open is the essay self selecting its audience.',
      'Writing a topic instead of a thesis, so the video has no claim a viewer could disagree with and nothing to resolve.',
      'Presenting three pieces of evidence that all point the same way, which reads as a pile of clips rather than an argument.',
      'Attacking a weak version of the opposing view, which tells the audience you avoided the strong one.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 15 minutes', value: `About ${fills(900)} words` },
      { metric: 'Cold open block to write first', value: '0:00 to 0:45' },
      { metric: 'Words per minute of finished video', value: `${paceNote(900)}` },
    ],
    faqs: [
      {
        question: 'What does a full video essay script example look like?',
        answer: `The example on this page is a complete script about why a to-do list never gets finished, written by us as an illustration. It runs ${fullExampleStats(videoEssayExample).words.toLocaleString('en-US')} words of narration across the ${videoEssayExample.beats.length} beats of the template, with on-screen directions in square brackets. At ${paceProse(videoEssayExample.runtimeSeconds)}, that fills about ${fullExampleStats(videoEssayExample).minutes} minutes.`,
      },
      {
        question: 'How do you write a script for a video essay?',
        answer: 'Write the thesis first, as one sentence a viewer could disagree with. Then pick three pieces of evidence where each one complicates the last, find the strongest argument against you, and only then write the cold open from the most concrete detail your research turned up. Read the draft aloud and cut any line that restates the thesis without moving it forward.',
      },
      {
        question: 'How long should a video essay be?',
        answer: `As long as the argument needs, and no longer. A 15 minute essay is about ${fills(900)} words of narration at ${paceProse(900)}, before any pauses you leave for archive footage. Longer essays need more evidence, not slower talking.`,
      },
      {
        question: 'Do I need a thesis for a video essay?',
        answer: 'Yes. A thesis is a one sentence claim someone could disagree with. Without it you have a compilation, and compilations have nothing for the viewer to resolve, which makes the middle of the video very hard to sit through.',
      },
      {
        question: 'How do I handle copyright when using clips in an essay?',
        answer: 'Use the shortest excerpt that makes your point, keep it inside your own commentary, and credit the source on screen. Commentary changes the clip into part of your argument, which is different from playing it as the content itself. This template is a writing scaffold and not legal advice.',
      },
      {
        question: 'How many pieces of evidence should an essay include?',
        answer: 'Three substantial ones, arranged so that each complicates the last. More than that and the thesis tends to blur; fewer and the argument rests on a single reading.',
      },
    ],
    keywords: [
      'video essay script example',
      'video essay script template',
      'how to write a video essay',
      'documentary script structure',
      'video essay outline',
      'essay video narration format',
    ],
    relatedTemplates: ['history-story', 'educational-explainer', 'true-crime', 'storytime'],
    relatedGuides: ['youtube-storytelling-narrative-structures', 'youtube-pattern-interrupts'],
  },

  'software-tutorial': {
    slug: 'software-tutorial',
    title: 'Software Tutorial',
    metaTitle: 'Software Tutorial Script Template (Screen Recording)',
    metaDescription: 'A software tutorial script template for screen recordings: setup, click by click steps, error handling, and a recap, with word budgets per step.',
    category: 'Education',
    templateText: `[HOOK - 0:00 to 0:20 | about ${words(20, 480)} words]
Show the finished result in the app, on screen, doing the thing the viewer wants.
"In this video I will [OUTCOME] in [SOFTWARE], and I will show every click."
State the version number on screen so nobody follows an outdated interface.

[WHAT YOU NEED - 0:20 to 0:50 | about ${words(30, 480)} words]
"To follow along you need [ACCOUNT TYPE], [PLAN TIER], and about [BLOCK OF TIME]."
If a paid tier is required, say so now rather than at step four.

[SETUP - 0:50 to 1:30 | about ${words(40, 480)} words]
Show the starting state: the empty project, the default settings, the file you begin from.
"[SETTING] starts on [DEFAULT]. Change it to [VALUE] before we begin, or step two will not work."

[STEP ONE - 1:30 to 3:00 | about ${words(90, 480)} words]
"Step one: [MENU] > [ITEM]."
Perform the clicks on screen while you say the path, then pause on the result.
"You should now see [EXPECTED STATE] on your screen. If you see [WRONG STATE], you are on the wrong [TAB or LAYER]."
Explain why the step exists in one sentence, then move on.

[STEP TWO - 3:00 to 4:30 | about ${words(90, 480)} words]
"Step two: [MENU] > [ITEM] > [VALUE]."
Zoom in on the control you are clicking.
"Everything in this step is reversible, so do not worry about getting it exactly right the first time."

[STEP THREE - 4:30 to 6:00 | about ${words(90, 480)} words]
"Step three: [MENU] > [ITEM]."
This is usually the step people get wrong, so slow down and repeat the path twice.
"If yours does not look like mine at this point, check [SPECIFIC SETTING] before continuing."

[WHEN IT GOES WRONG - 6:00 to 7:00 | about ${words(60, 480)} words]
"If you hit [COMMON ERROR MESSAGE], it means [CAUSE]. Fix it by [FIX]."
"Second most common issue: [ERROR]. Same fix, different menu."

[VERIFY AND RECAP - 7:00 to 8:00 | about ${words(60, 480)} words]
"Here is the finished result, side by side with where we started."
"To recap: [STEP ONE], [STEP TWO], [STEP THREE]. That is the whole process."
"The next thing to learn from here is [FOLLOW ON FEATURE]."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'Screen recording viewers come from search with a task. Show the finished result immediately so they know this video solves it.',
        tips: 'Show the end state before you touch the first menu. State the software version on screen, since interface labels move between releases.',
      },
      {
        section: 'What You Need (0:20-0:50)',
        purpose: 'Prevent the biggest source of tutorial abandonment: following five minutes of steps and then discovering a required plan or account.',
        tips: 'Name any paid tier, region restriction, or prerequisite tool up front. It costs 30 seconds and saves your comments section.',
      },
      {
        section: 'Setup (0:50-1:30)',
        purpose: 'Put every viewer on the same starting state, so the exact click paths match what they see.',
        tips: 'Show the defaults you are changing. Skip the account creation screen unless it is unusual.',
      },
      {
        section: 'Steps (1:30-6:00)',
        purpose: 'Deliver the procedure in a fixed pattern: say the path, perform it, confirm the expected result.',
        tips: 'Say the menu path out loud as well as clicking it, because viewers pause, look away, and come back. Repeat the hardest path twice.',
      },
      {
        section: 'When It Goes Wrong (6:00-7:00)',
        purpose: 'Answer the issues that would otherwise become support requests, while the viewer still has the app open.',
        tips: 'Quote the exact error message text. Viewers search error strings, so put the wording on screen as well as saying it.',
      },
      {
        section: 'Verify and Recap (7:00-8:00)',
        purpose: 'Confirm success and hand the viewer the next logical thing to learn.',
        tips: 'Recap the three step path in one breath so it can be remembered without rewatching.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20 | about ${words(20, 480)} words]
Show a finished spreadsheet with a live filter dropdown that updates three charts at once.
"In this video I will build a filterable dashboard in Google Sheets, and I will show every click. This is the version as of early 2026."

[WHAT YOU NEED - 0:20 to 0:50 | about ${words(30, 480)} words]
"You need a free Google account and about 20 minutes. No add ons, no scripts, nothing to install."

[SETUP - 0:50 to 1:30 | about ${words(40, 480)} words]
"Open a blank sheet and paste your data into columns A through D, with a header row like the one on screen."
"Freeze the header row first: View, Freeze, one row. Step two assumes it is frozen."

[STEP ONE - 1:30 to 3:00 | about ${words(90, 480)} words]
"Step one: select the whole range, then Data, Create a filter."
"I am clicking the corner of the header row so the range selects automatically. You should now see the filter arrows on every header. If you do not see them, your range stopped short, usually because row one was not selected."

[STEP TWO - 3:00 to 4:30 | about ${words(90, 480)} words]
"Step two: Insert, Chart. It defaults to a column chart, so in the setup panel change it to a line chart."
"Everything in this step is reversible, so do not worry about the exact chart type yet."
"I am now pointing the X axis at the date column and the series at the revenue column."

[STEP THREE - 4:30 to 6:00 | about ${words(90, 480)} words]
"Step three: repeat the chart twice and point each one at a different series."
"This is the step people get wrong, so I will say the path again: Insert, Chart, then in the setup panel pick the series from the drop down."
"If yours does not look like mine, check that each chart is reading the same range before continuing."

[WHEN IT GOES WRONG - 6:00 to 7:00 | about ${words(60, 480)} words]
"If you see 'Cannot create a chart from the selected range', it means the selection includes a blank column. Fix it by re selecting from A1 to D1 and dragging down."
"Second most common issue: the chart goes blank when you filter. That usually means the range is not the full data set."

[VERIFY AND RECAP - 7:00 to 8:00 | about ${words(60, 480)} words]
"Here is the finished dashboard next to the blank sheet we started with."
"To recap: filter on, chart on, then repeat the chart per series. That is the whole process."
"The next thing to learn from here is making the dropdown control every chart at once."`,
    commonMistakes: [
      'Not showing the software version or interface, so the tutorial ages invisibly and viewers blame themselves when menu labels have moved.',
      'Saying what you are doing instead of the menu path. Viewers who look away for two seconds cannot rewind to find a click they never heard described.',
      'Skipping the prerequisites, then revealing at step four that the feature needs a paid tier.',
      'Recording at full screen scale where menu text is unreadable after compression. Zoom the interface before you start.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 8 minutes', value: `About ${fills(480)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(480)}` },
    ],
    faqs: [
      {
        question: 'Do software tutorials need a full script?',
        answer: `A script is more useful here than in most formats, because the steps have to be in a workable order and the click paths have to be exact. Write the steps and their expected results in full, then narrate them live while recording. An 8 minute tutorial is roughly ${fills(480)} words of narration.`,
      },
      {
        question: 'How long should a screen recording tutorial be?',
        answer: 'Keep it to one task. Most single task tutorials run 5 to 12 minutes, which leaves room to demonstrate each step and the common errors without padding. If you need two tasks, that is two videos.',
      },
      {
        question: 'Should I edit out my mistakes in a tutorial?',
        answer: 'Keep the ones that teach something, such as a wrong menu or an error message, and cut the ones that do not, such as a slow save or a misclick you immediately correct. A tutorial where nothing ever goes wrong is harder to follow, not more impressive.',
      },
      {
        question: 'How do I make a tutorial findable in search?',
        answer: 'Put the software name, the version year, and the exact task in the title, and use the same phrasing in the first sentence of the script. Tutorial searches are task shaped, so match the task wording rather than the feature name.',
      },
    ],
    keywords: [
      'software tutorial script template',
      'screen recording script template',
      'how to script a tutorial video',
      'software demo video structure',
      'technical tutorial narration',
    ],
    relatedTemplates: ['tutorial', 'beginner-guide', 'educational-explainer', 'product-review'],
    relatedGuides: ['youtube-tutorial-scripts', 'script-structure-guide'],
  },

  'unboxing': {
    slug: 'unboxing',
    title: 'Unboxing and First Impressions',
    metaTitle: 'Unboxing Video Script Template: Free Outline and Example',
    metaDescription: 'An unboxing video script template with a first impressions structure: box reveal, what is in the box, first use, and an honest verdict.',
    category: 'Reviews',
    templateText: `[HOOK - 0:00 to 0:20 | about ${words(20, 480)} words]
Open on the closed box, then cut straight to the single detail that surprised you.
"[ONE THING ABOUT THIS PRODUCT nobody expects]"
Do not say the brand name twice in the first ten seconds.

[THE BOX - 0:20 to 1:30 | about ${words(70, 480)} words]
Show the packaging from all sides before opening anything.
"First thing: [PACKAGING OBSERVATION, weight, size, or a deliberate omission]."
Open it in real time, but cut the dead air of tape and foam.
"Inside, the first layer is [ITEM]. Underneath that, [ITEM]."

[EVERYTHING IN THE BOX - 1:30 to 3:00 | about ${words(90, 480)} words]
Lay every item out in one frame, then name each one against that shot.
"[ITEM ONE] is the product itself. [ITEM TWO] is the [PURPOSE]. [ITEM THREE] is not mentioned anywhere on the box."
"Note what is missing: [ABSENT ACCESSORY]."

[FIRST LOOK AND BUILD - 3:00 to 5:00 | about ${words(120, 480)} words]
Pick the product up and give it to the camera, not to the audience.
"It weighs [SPECIFIC COMPARISON]. The [MATERIAL] feels [ASSESSMENT], and the [PART] flexes slightly here."
Compare against something the viewer already owns: "It is about the size of [EVERYDAY OBJECT]."

[FIRST USE - 5:00 to 6:30 | about ${words(90, 480)} words]
Set it up on camera without a cut where the setup is the interesting part.
"Setup took [TIME], and the app asked for [PERMISSION]."
Here is what happened the first time: "[FIRST USE RESULT, including any confusion]."

[FIRST IMPRESSIONS - 6:30 to 7:30 | about ${words(60, 480)} words]
"First impressions: [TWO SENTENCE HONEST REACTION]."
"Two things I would change: [CHANGE ONE] and [CHANGE TWO]."
Be explicit that this is first impressions, not a long term verdict.

[WHO IT IS FOR AND CTA - 7:30 to 8:00 | about ${words(30, 480)} words]
"This looks right for [USE CASE]. I would hold off if [REASON]."
"I will come back to this after [TIME PERIOD] with a full review. Subscribe if you want that."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'Unboxing videos live or die on curiosity. Open with the unexpected detail so the reveal has a reason to exist beyond the box itself.',
        tips: 'The box is not the hook; the surprise is. Say the surprising thing, then let the box follow.',
      },
      {
        section: 'The Box (0:20-1:30)',
        purpose: 'Satisfy the tactile curiosity that brought the viewer here, while cutting the slow parts of physically opening a package.',
        tips: 'Shoot the packaging cleanly and in good light. Cut the tape, not the first reveal.',
      },
      {
        section: 'Everything in the Box (1:30-3:00)',
        purpose: 'Give a complete inventory in one frame, which is the single most rewatched and screenshotted part of an unboxing.',
        tips: 'Lay everything out at once and name items against the wide shot. Also name what is missing.',
      },
      {
        section: 'First Look and Build (3:00-5:00)',
        purpose: 'Move from packaging to the physical product and translate it into comparisons the viewer can picture.',
        tips: 'Use size and weight comparisons to objects people already own. Hands in frame with a reference object do more than measurements spoken aloud.',
      },
      {
        section: 'First Use (5:00-6:30)',
        purpose: 'Make the first minutes of use real, including friction, because that is what separates first impressions from an advertisement.',
        tips: 'Do not hide a confusing setup. Show where you hesitated and what you had to look up.',
      },
      {
        section: 'First Impressions (6:30-7:30)',
        purpose: 'Deliver an honest early read without pretending to know things only months of use would show.',
        tips: 'Say plainly that this is a first look. Name two specific things you would change.',
      },
      {
        section: 'Who It Is For and CTA (7:30-8:00)',
        purpose: 'Help viewers self select and set up the follow up review.',
        tips: 'Promise a specific return point, such as a 6 month review, rather than a vague future video.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20 | about ${words(20, 480)} words]
Open on a sealed box, then cut to a phone sized device sitting in a metal tray.
"The cheapest model in this line has no screen, and that turns out to be the whole point."

[THE BOX - 0:20 to 1:30 | about ${words(70, 480)} words]
Show the box from all four sides. It is smaller than expected and surprisingly heavy.
"First thing: the box weighs more than the device feels like it should, and the reason is at the bottom."
Cut the seal and open it. "Inside, the first layer is the device itself, sitting in a molded tray. Underneath that, a braided cable and a card with a QR code."

[EVERYTHING IN THE BOX - 1:30 to 3:00 | about ${words(90, 480)} words]
Lay all four items on the table against a plain backdrop.
"The device, a braided USB cable that is about a meter long, a quick start card, and a sticker. There is no wall adapter, and nothing on the box tells you that."

[FIRST LOOK AND BUILD - 3:00 to 5:00 | about ${words(120, 480)} words]
Pick it up and turn it in one slow rotation.
"It weighs about the same as a deck of cards. The body is aluminum and cool to the touch, and the seam where the two halves meet catches a fingernail on one corner."
"It is about the size of a paperback, so it fits a jacket pocket but not a jeans pocket."

[FIRST USE - 5:00 to 6:30 | about ${words(90, 480)} words]
Set it up with a phone camera, no cuts, on screen.
"Setup took about four minutes, and the app asked for location before it would pair, which I denied and it still worked."
"The first connection took two attempts. The second attempt connected in a couple of seconds."

[FIRST IMPRESSIONS - 6:30 to 7:30 | about ${words(60, 480)} words]
"First impressions: the build is better than the price suggests, and the missing wall adapter is the worst part of the whole package."
"Two things I would change: include an adapter, and smooth that seam."
"Remember this is a first look, not a long term verdict."

[WHO IT IS FOR AND CTA - 7:30 to 8:00 | about ${words(30, 480)} words]
"This looks right for someone who wants the simplest version of this product and already owns a charger."
"I will come back to this after 30 days of daily use with a full review. Subscribe if you want that."`,
    commonMistakes: [
      'Reading the marketing copy off the box, which turns the video into an advertisement the viewer could have watched anywhere.',
      'Leaving in minutes of tape, foam, and silence. Cut the physical opening down to the moments where something new appears.',
      'Skipping the inventory shot, which is the part of an unboxing viewers pause and rewatch most.',
      'Calling a first look a review. Claiming a verdict on day one, before you have used the product, is the fastest way to lose trust.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 8 minutes', value: `About ${fills(480)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(480)}` },
    ],
    faqs: [
      {
        question: 'How long should an unboxing video be?',
        answer: `Enough to show the box, the inventory, the build, and a first use, with nothing repeated. That is usually 6 to 10 minutes, or about ${fills(480)} words of narration for 8 minutes. If the inventory and build take two minutes, do not stretch them to six.`,
      },
      {
        question: 'What is the difference between an unboxing and a review?',
        answer: 'An unboxing covers what arrives and how it feels in the first session. A review covers how it performs over time. Keep the two separate, and say clearly which one the viewer is watching so nobody mistakes first impressions for a verdict.',
      },
      {
        question: 'What should I show first in an unboxing video?',
        answer: 'The most surprising detail you noticed about the product, before the box itself. Leading with the packaging gives the viewer no reason to keep watching once the box is open, which is usually within the first minute.',
      },
      {
        question: 'Should I film the unboxing in one take?',
        answer: 'No. Shoot continuously so reactions stay honest, then cut the physical pauses where nothing new appears. Keep the first reveal of each item uncut, and cut the tape, foam, and dead air around it.',
      },
    ],
    keywords: [
      'unboxing video script template',
      'first impressions video script',
      'how to script an unboxing',
      'unboxing video structure',
      'product first look template',
    ],
    relatedTemplates: ['product-review', 'comparison-video', 'before-after-transformation', 'top-10-list'],
    relatedGuides: ['youtube-hook-examples', 'script-structure-guide'],
  },

  'travel-vlog': {
    slug: 'travel-vlog',
    title: 'Travel Vlog',
    metaTitle: 'Travel Vlog Script Template: Story Arc and Full Example',
    metaDescription: 'A travel vlog script template built as a story arc: cold open, place setup, three beats, and a closing reflection, with word budgets.',
    category: 'Vlogging',
    templateText: `[COLD OPEN - 0:00 to 0:30 | about ${words(30, 720)} words]
Open on the single best shot of the trip, with the sound from that moment, not music.
"[ONE LINE THAT PROMISES A STORY, not a destination]"
No "welcome back to the channel." The place is the character, so introduce it mid action.

[WHERE WE ARE - 0:30 to 1:30 | about ${words(60, 720)} words]
"[PLACE], [WHY YOU ARE HERE], [HOW LONG YOU HAVE]."
Give one concrete anchor the viewer can picture: a price, a distance, a temperature, a local custom.
"Here is what I planned, and here is the first thing that already went differently."

[ARRIVAL AND FIRST IMPRESSION - 1:30 to 3:30 | about ${words(120, 720)} words]
Show the journey, not just the destination: the transport, the walk, the first doorway.
"First impression: [SENSORY DETAIL, sound, smell, or texture]."
Name one thing that is different from what you expected.

[BEAT ONE - 3:30 to 6:00 | about ${words(150, 720)} words]
The first real activity, told with a small problem and a resolution.
"[WHAT YOU TRIED] and [WHAT WENT WRONG or RIGHT]."
Let some footage breathe without narration: 10 seconds of ambient sound every few minutes.

[BEAT TWO - 6:00 to 8:30 | about ${words(150, 720)} words]
The part the thumbnail promised. Deliver the payoff here.
"Here is the thing I came for: [PAYOFF]."
React honestly, including if it was underwhelming.

[WHAT WENT WRONG - 8:30 to 10:30 | about ${words(120, 720)} words]
Every trip has one. Name it plainly instead of hiding it.
"[THE PROBLEM], and here is how I handled it."
This section is what makes a vlog a story instead of a highlight reel.

[REFLECTION - 10:30 to 11:30 | about ${words(60, 720)} words]
"Would I come back? [HONEST ANSWER]."
"One thing I would do differently: [SPECIFIC CHANGE]."

[CLOSE AND NEXT STOP - 11:30 to 12:00 | about ${words(30, 720)} words]
"[WHAT YOU ARE TAKING AWAY FROM THIS PLACE]."
"Next stop: [NEXT PLACE]. Subscribe if you want to come along."`,
    annotatedBreakdown: [
      {
        section: 'Cold Open (0:00-0:30)',
        purpose: 'Travel footage is abundant and interchangeable. A story promise in the first line is what makes this trip different from every other video of the same place.',
        tips: 'Use the location audio from the shot. Music over a wide landscape is the most skippable opening in the format.',
      },
      {
        section: 'Where We Are (0:30-1:30)',
        purpose: 'Orient the viewer with concrete anchors instead of adjectives, so the place becomes real rather than scenic.',
        tips: 'One price, one distance, one local detail. Skip the flight itinerary unless the flight itself is the story.',
      },
      {
        section: 'Arrival (1:30-3:30)',
        purpose: 'Show the transition into the place, which is where the viewer builds their own expectations.',
        tips: 'Film the walk in and the first doorway. First impressions are more useful than polished panoramas.',
      },
      {
        section: 'Beats (3:30-8:30)',
        purpose: 'Structure the day as two or three scenes with a small problem and resolution each, so the video has shape beyond chronology.',
        tips: 'Alternate narration with ambient sound. Ten seconds of real audio resets attention better than a music change.',
      },
      {
        section: 'What Went Wrong (8:30-10:30)',
        purpose: 'Give the video a low point. Without one, a vlog has no arc and viewers feel like they are watching someone else have a nice time.',
        tips: 'Do not dramatize a minor inconvenience. Pick a genuine problem and show how you actually handled it.',
      },
      {
        section: 'Reflection and Close (10:30-12:00)',
        purpose: 'Answer the question the viewer is asking by the end: was it worth it, and would you go back.',
        tips: 'Name one thing you would do differently. It makes the advice usable and the video memorable.',
      },
    ],
    filledExample: `[COLD OPEN - 0:00 to 0:30 | about ${words(30, 720)} words]
Open on rain hammering a tin roof, ambient sound, no music.
"I planned four days in a mountain town for the views, and spent most of the first one inside a hardware store."

[WHERE WE ARE - 0:30 to 1:30 | about ${words(60, 720)} words]
"Kalimpong, in the eastern Himalayas, in the middle of the monsoon. I have three days and one pair of dry shoes."
"A room here costs about what a coffee costs in my city, and the town closes at eight."
"Here is what I planned, and here is the first thing that already went differently: the viewpoint road is closed."

[ARRIVAL AND FIRST IMPRESSION - 1:30 to 3:30 | about ${words(120, 720)} words]
Show the shared jeep, the walk uphill, the first doorway with a hand painted sign.
"First impression: it is loud. Not traffic, water. Every roof is a drum in this season."
"Nothing here is set up for visitors. That is either the whole appeal or the whole problem, depending on the day."

[BEAT ONE - 3:30 to 6:00 | about ${words(150, 720)} words]
The closed road, the detour, asking directions from a shopkeeper who draws a map on a paper bag.
"The road was washed out, so I took the old footpath, which turned out to be the better walk anyway."
Let the next 10 seconds play as ambient sound: boots in mud, birds.

[BEAT TWO - 6:00 to 8:30 | about ${words(150, 720)} words]
The cloud break at the viewpoint, which is the shot the video was built around.
"Here is the thing I came for: about ninety seconds where the clouds opened and I could see the whole valley."
"It was genuinely worth the walk, and it lasted less than two minutes."

[WHAT WENT WRONG - 8:30 to 10:30 | about ${words(120, 720)} words]
"The guesthouse had no hot water on the second night, and no one to ask about it until morning."
"I boiled water on the kettle, which is what the family in the next room was already doing, so apparently this is normal."

[REFLECTION - 10:30 to 11:30 | about ${words(60, 720)} words]
"Would I come back? Not in the monsoon, but yes, and for longer."
"One thing I would do differently: carry a second pair of shoes and check the road status before booking."

[CLOSE AND NEXT STOP - 11:30 to 12:00 | about ${words(30, 720)} words]
"The views are real, and so is the water, in every sense."
"Next stop: the tea estates two valleys over. Subscribe if you want to come along."`,
    commonMistakes: [
      'Opening on a scenic drone shot with music and no narration, which gives the viewer no reason to keep watching past the first few seconds.',
      'Telling the day in strict chronological order, which produces a list of events instead of a story with a low point and a payoff.',
      'Hiding the things that went wrong, leaving a highlight reel with nothing at stake.',
      'Narrating over every second of footage. Ambient sound is what makes a place feel like a place.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 12 minutes', value: `About ${fills(720)} words` },
      { metric: 'Cold open block to write first', value: '0:00 to 0:30' },
      { metric: 'Words per minute of finished video', value: `${paceNote(720)}` },
    ],
    faqs: [
      {
        question: 'Do travel vlogs need a script?',
        answer: `Not word for word, but they need a written arc. Plan the cold open, the two or three beats, the low point, and the reflection before you film, then narrate live. A 12 minute vlog carries roughly ${fills(720)} words of narration, and most of that should be recorded on location, not in a studio.`,
      },
      {
        question: 'How do I make a travel vlog different from everyone else filming the same place?',
        answer: 'Give it a plan that can go wrong. A specific goal, a constraint, or a small problem turns footage of a place into a story about a trip. The location supplies the backdrop; the plan supplies the video.',
      },
      {
        question: 'How long should a travel vlog be?',
        answer: 'Most travel vlogs work between 8 and 15 minutes per location. Longer videos need a journey with real progression, such as a multi day route, rather than one more day in the same town.',
      },
    ],
    keywords: [
      'travel vlog script template',
      'how to script a travel vlog',
      'travel video story structure',
      'vlog narration template',
      'travel vlog outline',
    ],
    relatedTemplates: ['day-in-the-life-vlog', 'storytime', 'video-essay', 'podcast-clip'],
    relatedGuides: ['youtube-storytelling-narrative-structures', 'youtube-watch-time-psychology'],
  },

  'recipe-video': {
    slug: 'recipe-video',
    title: 'Recipe Video',
    metaTitle: 'Recipe Video Script Template: Free Outline and Example',
    metaDescription: 'A recipe video script template with an ingredient list, timed steps, a failure section, and a plated finish, plus word budgets.',
    category: 'Food',
    templateText: `[RESULT FIRST - 0:00 to 0:15 | about ${words(15, 480)} words]
Open on the finished dish, moving, with the texture the viewer can almost hear.
"[DISH NAME] in [TOTAL TIME], and [THE ONE THING THAT MAKES IT WORK]."
No channel greeting. The food is the hook.

[WHAT YOU ARE MAKING - 0:15 to 0:45 | about ${words(30, 480)} words]
"Here is what it is: [TWO SENTENCE DESCRIPTION]."
"Here is what makes it different from the usual version: [KEY TECHNIQUE]."
Say the total and active time on screen.

[INGREDIENTS - 0:45 to 1:45 | about ${words(60, 480)} words]
Show every ingredient laid out once, in one frame, at final quantities.
"[INGREDIENT], [AMOUNT]. The [SUBSTITUTION] works if you cannot find it."
"Two things worth buying well here: [INGREDIENT ONE] and [INGREDIENT TWO]."

[STEP BY STEP - 1:45 to 5:30 | about ${words(210, 480)} words]
Step with the heat, the texture, and the time visible on screen in every shot.
"Step one: [ACTION] until [VISIBLE CUE], about [TIME]."
"Step two: [ACTION]. You are looking for [TEXTURE OR COLOR], not [TIME ALONE]."
"Step three: [ACTION]. This is the step people rush, and rushing it is the difference between [RESULT A] and [RESULT B]."
Repeat the cue at each transition so viewers cooking along do not have to rewind.

[WHAT GOES WRONG - 5:30 to 6:30 | about ${words(60, 480)} words]
"If yours is [PROBLEM], it is because [CAUSE]. Fix it by [FIX]."
"If it tastes flat, you probably need [SEASONING OR ACID], not more salt."

[PLATE AND TEST - 6:30 to 7:30 | about ${words(60, 480)} words]
Plate it the way you would serve it, with the camera at eating height.
"Texture check: [SOUND OR DESCRIPTION]."
"Taste: [HONEST ASSESSMENT, including one thing you would change]."

[STORAGE AND CTA - 7:30 to 8:00 | about ${words(30, 480)} words]
"It keeps for [TIME] in the fridge and reheats [HOW]."
"Full written recipe is in the description. Tell me in the comments what to cook next."`,
    annotatedBreakdown: [
      {
        section: 'Result First (0:00-0:15)',
        purpose: 'Food viewers decide on the image. Show the finished dish before any ingredients appear so the video has a payoff to watch toward.',
        tips: 'Film the dish moving: steam, a pull, a pour. A still photo of food does not carry the first five seconds.',
      },
      {
        section: 'What You Are Making (0:15-0:45)',
        purpose: 'Set expectations about time, difficulty, and what makes this version worth following.',
        tips: 'State total time and active time separately, because they are usually different and viewers plan around them.',
      },
      {
        section: 'Ingredients (0:45-1:45)',
        purpose: 'Give the complete shopping list in one frame so a viewer can decide to cook along before investing in the steps.',
        tips: 'Lay everything out at final quantities and name substitutions out loud. Viewers screenshot this shot.',
      },
      {
        section: 'Step by Step (1:45-5:30)',
        purpose: 'Teach the technique, not just the sequence, so the viewer can repeat it without the video next time.',
        tips: 'Give a visible cue for every timed instruction. Texture and color tell the cook more than a clock does.',
      },
      {
        section: 'What Goes Wrong (5:30-6:30)',
        purpose: 'Rescue the viewers whose dish is already off track, while they still have it in the pan.',
        tips: 'Name the two most common failures and their causes. Flat flavor is the one almost everyone hits.',
      },
      {
        section: 'Plate, Serve, Storage (6:30-8:00)',
        purpose: 'Close the loop with the finished dish and give the practical information a written recipe usually carries.',
        tips: 'Include one honest thing you would change. It keeps the video from sounding like an advertisement for your own cooking.',
      },
    ],
    filledExample: `[RESULT FIRST - 0:00 to 0:15 | about ${words(15, 480)} words]
Open on a spoon pulling through a glossy pan sauce, then a plate with the sauce poured over.
"Crispy chicken thighs in 30 minutes, and the trick is to start them in a cold pan."

[WHAT YOU ARE MAKING - 0:15 to 0:45 | about ${words(30, 480)} words]
"It is a one pan weeknight dinner: crisp skin, a pan sauce, and whatever greens you have."
"The usual version has you sear in a hot pan and finish in the oven. This one starts cold and never goes in the oven, which is why the skin comes out even."
"Thirty minutes total, about ten of them active."

[INGREDIENTS - 0:45 to 1:45 | about ${words(60, 480)} words]
Everything laid out in one frame, in small bowls.
"Four bone in thighs, salt, a lemon, two cloves of garlic, a knob of butter, and a splash of stock. Shallot works if you have no garlic."
"Two things worth buying well here: the chicken, with the skin on, and a real lemon. Bottled juice will taste flatter."

[STEP BY STEP - 1:45 to 5:30 | about ${words(210, 480)} words]
"Step one: salt the skin and lay the thighs skin down in a cold pan, then turn the heat to medium."
"Step two: leave them alone until the skin releases, about twelve minutes. You are looking for a deep gold, not a timer."
"Step three: flip, add the garlic and butter, and baste for two minutes. This is the step people rush, and rushing it is the difference between a nutty brown butter and a bitter one."
"Step four: move the chicken to a plate, add stock and lemon to the pan, and reduce until it coats a spoon."

[WHAT GOES WRONG - 5:30 to 6:30 | about ${words(60, 480)} words]
"If your skin stuck, the pan was not cold when the chicken went in. Fix it by letting the pan cool, wiping it, and starting again."
"If it tastes flat, you probably need acid, not more salt. Another squeeze of lemon usually does it."

[PLATE AND TEST - 6:30 to 7:30 | about ${words(60, 480)} words]
Plate it with the sauce poured over and the greens tucked under the chicken, camera at eating height.
"Texture check: listen to that." Cut the sound of the fork cracking the skin.
"Taste: the sauce is bright and the skin held up. Next time I would add the garlic thirty seconds later, it caught a little."

[STORAGE AND CTA - 7:30 to 8:00 | about ${words(30, 480)} words]
"It keeps three days in the fridge and reheats best in a dry pan, skin down."
"Full written recipe is in the description. Tell me in the comments what to cook next."`,
    commonMistakes: [
      'Opening on the ingredients instead of the finished dish. Food viewers decide from the image, and a pile of raw onions is not a hook.',
      'Giving times without visual cues, so viewers following along cannot tell whether their pan is where yours is.',
      'Cutting between steps so fast that viewers cooking along have to rewind constantly to find the transition.',
      'Naming ingredients as you add them instead of showing the full list first, which stops anyone from shopping before they start.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 8 minutes', value: `About ${fills(480)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:15' },
      { metric: 'Words per minute of finished video', value: `${paceNote(480)}` },
    ],
    faqs: [
      {
        question: 'Should I script a cooking video or just film and talk?',
        answer: `Script the structure and the cues, and leave the tasting to the moment. A written plan keeps the ingredient shot, the step order, and the failure section in place, which is what viewers cooking along depend on. An 8 minute recipe video carries roughly ${fills(480)} words of narration, though ambient cooking sound replaces much of it.`,
      },
      {
        question: 'How long should a recipe video be?',
        answer: 'Most recipe videos work between 4 and 12 minutes. Quick recipes are better short and brisk; anything with resting, proofing, or multiple components justifies the longer runtime. Do not stretch a 20 minute recipe into a 20 minute video.',
      },
      {
        question: 'How do I show ingredient amounts on screen?',
        answer: 'Lay everything out once, at final quantities, in a single overhead shot, and repeat the amount on screen as you add each one. Viewers screenshot the overhead shot, so make the labels readable at phone size.',
      },
      {
        question: 'What should I do if a step goes wrong on camera?',
        answer: 'Show it and explain the cause. A pan that caught, a sauce that split, or dough that tore teaches more than a flawless take, and it is the part viewers comment on.',
      },
    ],
    keywords: [
      'recipe video script template',
      'cooking video script structure',
      'how to film a recipe video',
      'food video narration template',
      'recipe video outline',
    ],
    relatedTemplates: ['day-in-the-life-vlog', 'tutorial', 'beginner-guide', 'travel-vlog'],
    relatedGuides: ['youtube-hook-examples', 'script-structure-guide'],
  },

  'workout-video': {
    slug: 'workout-video',
    title: 'Fitness Workout',
    metaTitle: 'Fitness Workout Video Script Template (Free, Copy-Paste)',
    metaDescription: 'A workout video script template with a session preview, warm up, timed blocks, form cues, and a cooldown, plus word budgets.',
    category: 'Fitness',
    templateText: `[HOOK AND PREVIEW - 0:00 to 0:30 | about ${words(30, 1200)} words]
Show the hardest 10 seconds of the session, then the shape of the whole thing.
"[SESSION TYPE], [TOTAL TIME], [WHAT YOU NEED]."
Put the full block list on screen so a viewer can see the end before starting.

[WHAT YOU NEED AND HOW TO SCALE - 0:30 to 1:30 | about ${words(60, 1200)} words]
"All you need is [EQUIPMENT or NONE] and about a metre of floor."
"Every move has a [EASIER] version and a [HARDER] version. I will call both out as we go."
Safety line, said plainly: "If anything pinches, stop and take the easier version."

[WARM UP - 1:30 to 4:00 | about ${words(90, 1200)} words]
Lead the warm up in real time with no cuts, so viewers can follow without pausing.
"[MOVE] for [TIME], then [MOVE]."
"Keep this at about [EFFORT LEVEL]. This is preparation, not the workout."

[BLOCK ONE - 4:00 to 7:30 | about ${words(120, 1200)} words]
"[EXERCISE], [REPS OR TIME]. [CUE], [CUE]."
Count the work out loud for timed sets so nobody has to look at the screen.
Between sets: "Rest here. Take the full [REST TIME], it is part of the plan."

[BLOCK TWO - 7:30 to 11:00 | about ${words(120, 1200)} words]
"[EXERCISE], [REPS OR TIME]."
"Form cue that matters most here: [SINGLE CUE]."
"Easier version: [SCALE DOWN]. Harder version: [SCALE UP]."

[BLOCK THREE - 11:00 to 14:30 | about ${words(120, 1200)} words]
"[EXERCISE], [REPS OR TIME]."
Flag the point where most people quit: "The last few reps are where this block actually happens."

[FINISHER - 14:30 to 16:30 | about ${words(90, 1200)} words]
"[SHORT HIGH EFFORT PIECE], [TIME ON], [TIME OFF], [ROUNDS]."
"Same work, same rest, no extra credit for going fast."

[COOLDOWN - 16:30 to 19:00 | about ${words(90, 1200)} words]
Hold each position long enough to matter: "[STRETCH], [TIME]."
Breathe with the viewer: "In through the nose for four, out for six."

[RECAP AND NEXT SESSION - 19:00 to 20:00 | about ${words(45, 1200)} words]
Name the three blocks in one line each.
"Next session is [FOCUS]. Do this one twice this week before adding load."`,
    annotatedBreakdown: [
      {
        section: 'Hook and Preview (0:00-0:30)',
        purpose: 'Follow along viewers need to know immediately whether they can do this session and how long it takes before they commit.',
        tips: 'Show the hardest moment, then the full block list on screen. A workout video that reveals its shape at minute five loses everyone who planned a short session.',
      },
      {
        section: 'Scaling (0:30-1:30)',
        purpose: 'Give every viewer a version they can actually perform, which is what makes a session replayable across weeks.',
        tips: 'Name the easier and harder version for every movement, and say the safety line plainly rather than burying it.',
      },
      {
        section: 'Warm Up (1:30-4:00)',
        purpose: 'Bring the viewer to the same state you are in, so the timed blocks land the same way for them.',
        tips: 'Do not cut a warm up. Cuts make it impossible to follow without pausing, which breaks the along with format entirely.',
      },
      {
        section: 'Blocks (4:00-14:30)',
        purpose: 'Deliver the session with the work, the rest, and the cue for each movement in a predictable rhythm.',
        tips: 'Count timed work out loud. Remove the need to look at the screen and you remove the reason to pause.',
      },
      {
        section: 'Finisher (14:30-16:30)',
        purpose: 'Give the session a clear peak, so the whole video has a top and a bottom rather than a flat middle.',
        tips: 'Keep the format identical to the blocks, just faster, and say plainly that speed is not the goal.',
      },
      {
        section: 'Cooldown and Recap (16:30-20:00)',
        purpose: 'Bring the heart rate down with the viewer still watching, then hand them the next session.',
        tips: 'Hold stretches long enough to be worth filming. End by naming the next focus so the session becomes a program.',
      },
    ],
    filledExample: `[HOOK AND PREVIEW - 0:00 to 0:30 | about ${words(30, 1200)} words]
Open on the last 10 seconds of the finisher: a plank row at speed, breathing hard.
"Twenty minute full body session, no equipment, no jumping, and apartment friendly."
The full block list fades in on screen: warm up, three blocks, finisher, cooldown.

[WHAT YOU NEED AND HOW TO SCALE - 0:30 to 1:30 | about ${words(60, 1200)} words]
"All you need is a mat and about a metre of floor."
"Every move has an easier and a harder version. Knee push ups instead of full, or elevate your hands on a chair for the easier version, feet on the chair for the harder one."
"If anything pinches, stop and take the easier version."

[WARM UP - 1:30 to 4:00 | about ${words(90, 1200)} words]
No cuts. Lead the whole warm up in real time.
"Inchworm walk into a hamstring stretch, two reps. Then arm circles, thirty seconds each direction."
"Keep this at about a three out of ten. This is preparation, not the workout."

[BLOCK ONE - 4:00 to 7:30 | about ${words(120, 1200)} words]
"Push ups, twelve reps, then a forty five second hold at the top of a squat."
"Elbows at forty five degrees, ribs down, and squeeze at the top."
Counting the hold out loud: "Thirty. Twenty. Ten."
"Rest here. Take the full forty five seconds, it is part of the plan."

[BLOCK TWO - 7:30 to 11:00 | about ${words(120, 1200)} words]
"Split squats, ten per side, then a bent over row, twelve reps."
"Form cue that matters most here: keep the front knee stacked over the ankle."
"Easier version: hold the wall. Harder version: hold a shopping bag in each hand."

[BLOCK THREE - 11:00 to 14:30 | about ${words(120, 1200)} words]
"Glute bridges, fifteen reps, then a dead bug, twenty seconds per side."
"Keep the ribs down and the low back quiet, that is the whole point of this one."
"The last few reps are where this block actually happens."

[FINISHER - 14:30 to 16:30 | about ${words(90, 1200)} words]
"Plank rows, twenty seconds on, twenty seconds off, four rounds."
"Same work, same rest, no extra credit for going fast."

[COOLDOWN - 16:30 to 19:00 | about ${words(90, 1200)} words]
Hold each position long enough to matter: "Chest opener against the wall, forty five seconds. Hip flexor stretch, forty five seconds each side."
Breathe with me: "In through the nose for four, out for six."

[RECAP AND NEXT SESSION - 19:00 to 20:00 | about ${words(45, 1200)} words]
"Push, legs, core, then the finisher. That is the whole session."
"Next session is upper body pulling. Do this one twice this week before adding load."`,
    commonMistakes: [
      'Cutting the warm up and the work intervals into a highlight reel, which makes it impossible to follow along without pausing every few seconds.',
      'Forgetting to name scaling options, so anyone who cannot do the main movement leaves immediately.',
      'Talking through the work rather than counting it, which forces viewers to look at the screen and lose their position.',
      'Ending without naming the next session, so the video is a one off instead of part of a plan.',
    ],
    retentionBenchmarks: [
      { metric: 'Cueing to plan for a 20 minute session (about half of it is talk)', value: `About ${words(600, 1200)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:30' },
      { metric: 'Words per minute of finished video', value: `${paceNote(1200)}` },
    ],
    faqs: [
      {
        question: 'Should a workout video have a script?',
        answer: `Script the block order, the times, and the cues, then coach live over them. Cues matter more than narration in this format, and because roughly half a session is counting rather than talking, a 20 minute session carries about ${words(600, 1200)} words of cueing. Silence during work is normal and expected.`,
      },
      {
        question: 'How long should a follow along workout video be?',
        answer: 'Match the promised session length exactly. If the video says 20 minutes, deliver 20 minutes of real time. Viewers plan their day around that number, so a 12 minute session in a 20 minute video costs you the reviews.',
      },
      {
        question: 'How do I make a workout video work for beginners and advanced viewers?',
        answer: 'Name an easier version and a harder version for every movement, once, at the start, then call out which one you are doing as you go. That keeps one video useful for both without a second demonstration section.',
      },
      {
        question: 'Do I need cuts in a follow along workout?',
        answer: 'Not in the working sections. Real time is the point. Cut only between blocks, and never inside a timed interval, because a cut hides how much time passed and the viewer loses their place.',
      },
    ],
    keywords: [
      'workout video script template',
      'fitness video script',
      'follow along workout structure',
      'how to plan a workout video',
      'exercise video narration cues',
    ],
    relatedTemplates: ['challenge-30-days', 'before-after-transformation', 'beginner-guide', 'tutorial'],
    relatedGuides: ['youtube-retention-guide', 'script-structure-guide'],
  },

  'personal-finance': {
    slug: 'personal-finance',
    title: 'Personal Finance Explainer',
    metaTitle: 'Personal Finance Script Template: Free Outline and Example',
    metaDescription: 'A personal finance video script template with a plain language promise, worked numbers, honest caveats, and a next step, plus word budgets.',
    category: 'Finance',
    templateText: `[HOOK - 0:00 to 0:20 | about ${words(20, 600)} words]
"[THE NUMBER OR DECISION the viewer is actually weighing]"
Show the arithmetic on screen rather than a stock chart.
"No jargon in this video. Every number gets a worked example."

[WHAT YOU WILL BE ABLE TO DO - 0:20 to 1:00 | about ${words(40, 600)} words]
"By the end you will be able to [SPECIFIC DECISION], using your own numbers."
Say early and plainly what this video is not: "This is general information, not advice for your situation."

[THE NUMBERS - 1:00 to 3:00 | about ${words(120, 600)} words]
Define each term the moment you use it: "Compound interest just means [PLAIN DEFINITION]."
Put a formula on screen and read it once, slowly.
"Everything in this video is built from these three numbers: [A], [B], [C]."

[WORKED EXAMPLE - 3:00 to 5:30 | about ${words(150, 600)} words]
Use one invented household and one real decision, with round numbers.
"Say you earn [AMOUNT] and set aside [PERCENT]. Over [PERIOD], that is [ARITHMETIC], and here is the sum on screen."
Change one input and show what moves: "Now change [INPUT]. The answer moves to [NEW NUMBER], which is the whole point."

[WHAT CHANGES THE ANSWER - 5:30 to 7:30 | about ${words(120, 600)} words]
"Three things move this number: [FACTOR ONE], [FACTOR TWO], [FACTOR THREE]."
"Here is the case where the usual advice is wrong: [COUNTEREXAMPLE]."

[THE CAVEAT - 7:30 to 8:30 | about ${words(60, 600)} words]
"These are rounded figures for illustration. Your [TAX, RATE, or FEES] will differ."
"Check [TYPE OF PROFESSIONAL or SOURCE] before you make a decision with real money."

[NEXT STEP - 8:30 to 9:30 | about ${words(60, 600)} words]
Give one small action the viewer can take today: "[ACTION], which takes [TIME]."
"If you take one thing from this video: [SINGLE SENTENCE]."

[CTA - 9:30 to 10:00 | about ${words(30, 600)} words]
"Tell me in the comments which of the three factors you want a full video on."
"Subscribe for the next one in this series."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'Finance viewers arrive with a specific decision in mind. Open on that decision and the arithmetic, not on market news.',
        tips: 'Promise plain language and keep it. A jargon free promise in the first line is the reason a finance video is watchable.',
      },
      {
        section: 'What You Will Be Able To Do (0:20-1:00)',
        purpose: 'Set the scope and put the limits on record before any numbers appear.',
        tips: 'Say plainly that the video is general information rather than advice. It is honest, and it also keeps you from sounding like a sales pitch.',
      },
      {
        section: 'The Numbers (1:00-3:00)',
        purpose: 'Define the terms once, then build every later section from the same three inputs so the arithmetic stays followable.',
        tips: 'Define each term at first use and put it on screen. A viewer who misses one definition cannot follow the rest.',
      },
      {
        section: 'Worked Example (3:00-5:30)',
        purpose: 'Turn an abstract rule into a household with round numbers, then show what changes when one input moves.',
        tips: 'Use an invented example and round figures. Show the sum on screen as you say it, and change exactly one input at a time.',
      },
      {
        section: 'What Changes the Answer (5:30-7:30)',
        purpose: 'Show that the rule is conditional, which is what separates an explainer from a slogan.',
        tips: 'Name one case where the usual advice fails. This is the section finance viewers quote back in the comments.',
      },
      {
        section: 'Caveat and Next Step (7:30-9:30)',
        purpose: 'Be explicit about the limits of rounded figures, then give one action small enough to do today.',
        tips: 'One action, with a time estimate. A list of five actions is the same as none.',
      },
      {
        section: 'CTA (9:30-10:00)',
        purpose: 'Turn the caveat list into the series plan.',
        tips: 'Ask which factor to cover next rather than a generic subscription request.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20 | about ${words(20, 600)} words]
"I am going to settle the rent versus buy question with one spreadsheet and round numbers, and I am not going to use the word equity as a synonym for winning."
Show a simple two column sum on screen.

[WHAT YOU WILL BE ABLE TO DO - 0:20 to 1:00 | about ${words(40, 600)} words]
"By the end you will be able to run this same comparison with your own rent, your own deposit, and your own local prices."
"Everything here is general information with rounded figures. It is not advice for your situation."

[THE NUMBERS - 1:00 to 3:00 | about ${words(120, 600)} words]
"Opportunity cost just means what the same money would have done somewhere else. Here that is a savings rate you would otherwise earn."
"Everything in this video is built from three numbers: monthly rent, monthly ownership cost excluding the mortgage, and the deposit."
The three inputs stay pinned on screen for the rest of the video.

[WORKED EXAMPLE - 3:00 to 5:30 | about ${words(150, 600)} words]
"Say rent is 1,200 a month and the equivalent ownership cost excluding the mortgage is 900 a month, with a deposit of 40,000."
"Over five years, the rent total is 72,000 and the ownership total is 54,000, before the deposit is counted at all."
"Now change the deposit. If it is 80,000 instead, the same five years leaves a larger gap between the two, and here is the sum on screen."

[WHAT CHANGES THE ANSWER - 5:30 to 7:30 | about ${words(120, 600)} words]
"Three things move this number: how long you stay, what the deposit would otherwise earn, and the costs the mortgage figure leaves out."
"Here is the case where the usual advice is wrong: if you move within three years, the buying side rarely catches up, because the early payments are mostly interest."

[THE CAVEAT - 7:30 to 8:30 | about ${words(60, 600)} words]
"These are rounded figures for illustration. Your rates, taxes, and fees will differ, sometimes a lot."
"Talk to a qualified professional before you make a decision with real money."

[NEXT STEP - 8:30 to 9:30 | about ${words(60, 600)} words]
"Take your own three numbers and put them in the same two columns, which takes about fifteen minutes."
"If you take one thing from this video: the length of stay matters more than the interest rate."

[CTA - 9:30 to 10:00 | about ${words(30, 600)} words]
"Tell me in the comments which of the three factors you want a full video on."
"Subscribe for the next one in this series."`,
    commonMistakes: [
      'Opening on market news or a chart instead of the decision the viewer is actually weighing.',
      'Using jargon in the first minute and defining it later, which loses the viewers who needed the explanation most.',
      'Presenting rounded example figures without saying they are rounded, which reads as a promise about real returns.',
      'Ending with five actions instead of one, which usually means the viewer takes none of them.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 10 minutes', value: `About ${fills(600)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(600)}` },
    ],
    faqs: [
      {
        question: 'How long should a personal finance video be?',
        answer: `One decision per video, usually 8 to 15 minutes. A 10 minute explainer is about ${fills(600)} words of narration, which is plenty of room for a definition section, one worked example, and a caveat. Longer videos usually mean you have bundled two decisions together.`,
      },
      {
        question: 'How do I explain money topics without giving advice?',
        answer: 'Explain the mechanism with an invented example and round numbers, state plainly that the figures are illustrative, and point viewers to a qualified professional for decisions with real money. Never state a specific return, rate, or outcome as what a viewer will get.',
      },
      {
        question: 'Should I use real numbers or example numbers in a finance video?',
        answer: 'Example numbers you invented, clearly labelled. Real figures go out of date quickly and invite viewers to treat them as a promise. Round invented figures teach the same arithmetic and stay accurate.',
      },
      {
        question: 'How do I keep a finance explainer from sounding like a lecture?',
        answer: 'Change one input at a time and show what moves. A worked example where the viewer can see the effect of their own numbers is what holds attention in this format, more than production value.',
      },
    ],
    keywords: [
      'personal finance video script template',
      'finance explainer script',
      'money video script structure',
      'how to explain finance on youtube',
      'finance youtube video outline',
    ],
    relatedTemplates: ['educational-explainer', 'science-explainer', 'beginner-guide', 'book-summary'],
    relatedGuides: ['how-to-write-a-youtube-script', 'script-structure-guide'],
  },

  'true-crime': {
    slug: 'true-crime',
    title: 'True Crime Story',
    metaTitle: 'True Crime Video Script Template: Build Suspense and Example',
    metaDescription: 'A true crime video script template with a cold open, timeline, sourced evidence, and a clear resolution, plus word budgets and sourcing rules.',
    category: 'Documentary',
    templateText: `[COLD OPEN - 0:00 to 1:00 | about ${words(60, 1080)} words]
Open on one small, specific, verifiable detail: a timestamp, a receipt, a phone log.
"[THE DETAIL], and nobody could explain it for [PERIOD]."
Name no suspect yet. Say what the case is and how the video will treat it.

[THE CASE IN ONE MINUTE - 1:00 to 2:30 | about ${words(90, 1080)} words]
"[WHO, WHERE, WHEN] and the one question the whole case turns on."
State your sourcing rule out loud: "Everything in this video comes from [COURT RECORDS, POLICE STATEMENTS, INTERVIEWS]. Where sources disagree, I will say so."

[THE PEOPLE - 2:30 to 4:00 | about ${words(90, 1080)} words]
Introduce each person with their role, not their reputation.
"[NAME], who [ROLE IN THE CASE]."
Use full names only where the record does, and say plainly when someone was never charged.

[THE TIMELINE - 4:00 to 8:00 | about ${words(240, 1080)} words]
Walk the timeline in order, with the date on screen at each step.
"[DATE]: [EVENT], per [SOURCE]."
Where two accounts conflict: "One account says [A]. Another says [B]. Here is why that matters."
Do not editorialize on each line. Let the sequence do the work.

[THE TURNING POINT - 8:00 to 11:00 | about ${words(180, 1080)} words]
"The case changes here, on [DATE]."
Show the document, message, or footage that changed it, then explain what it proves and what it does not.
"It proves [X]. It does not prove [Y], and that distinction matters."

[EVIDENCE AND DISPUTE - 11:00 to 14:00 | about ${words(180, 1080)} words]
Lay out what each side argues, with sources named on screen.
"Supporters of [POSITION A] point to [EVIDENCE]. [POSITION B] counters with [EVIDENCE]."
"Here is what I could not verify: [OPEN QUESTION]."

[WHERE IT STANDS - 14:00 to 16:30 | about ${words(150, 1080)} words]
"Today the case stands at [STATUS]."
No speculation about guilt beyond what the record states.
"Anything I have said here about a named person is drawn from [SOURCE TYPE], and they have the right of reply."

[SOURCES AND CLOSE - 16:30 to 18:00 | about ${words(90, 1080)} words]
List every source on screen, with dates, and hold the final frame long enough to read.
"Next case in this series: [NEXT CASE], and the question nobody has answered."`,
    annotatedBreakdown: [
      {
        section: 'Cold Open (0:00-1:00)',
        purpose: 'A small verifiable detail creates more suspense than a summary of the crime, and it establishes that the video is built on records rather than mood.',
        tips: 'Pick a timestamp, a document, or a physical object. Do not name a suspect in the first minute.',
      },
      {
        section: 'The Case in One Minute (1:00-2:30)',
        purpose: 'Orient the viewer and state the sourcing rule before any claim is made, which is what distinguishes this from rumor content.',
        tips: 'Name the sources you are working from out loud. Say where they conflict rather than smoothing the conflict away.',
      },
      {
        section: 'The People (2:30-4:00)',
        purpose: 'Introduce participants by role and legal status, so the audience is not primed to assume guilt.',
        tips: 'Use full names only as the public record does, and state plainly when someone was never charged.',
      },
      {
        section: 'The Timeline (4:00-8:00)',
        purpose: 'The sequence is the story. Dates on screen let the viewer follow and check as they watch.',
        tips: 'Cite the source for each event as you say it. Resist adding commentary to each line.',
      },
      {
        section: 'The Turning Point (8:00-11:00)',
        purpose: 'Give the case a hinge and be precise about what the key evidence establishes and what it leaves open.',
        tips: 'Separate what is proven from what is claimed. That distinction is the most quoted part of a good case video.',
      },
      {
        section: 'Where It Stands and Sources (14:00-18:00)',
        purpose: 'Close with the current status and a full source list, which protects both the people in the case and the channel.',
        tips: 'Hold the source frame long enough to read. Note the right of reply for anyone named.',
      },
    ],
    filledExample: `[COLD OPEN - 0:00 to 1:00 | about ${words(60, 1080)} words]
Open on a fuel receipt with a timestamp, held up to camera.
"Eleven forty seven at night, on a road with one petrol station and no cameras, and this receipt places someone there who said they were forty kilometres away."
This is an invented case used to show the structure. No real person is described.

[THE CASE IN ONE MINUTE - 1:00 to 2:30 | about ${words(90, 1080)} words]
"A delivery van disappears between two towns on a mountain road, and the whole case turns on whether it ever made the second checkpoint."
"Everything in this video comes from the public incident report, the two witness statements, and the phone records released at the inquest. Where the two accounts disagree, I will say so."

[THE PEOPLE - 2:30 to 4:00 | about ${words(90, 1080)} words]
"The driver, named in the report, who was never charged with any offence."
"The depot manager, who gave the only account of the van leaving."
"One witness, who we will call W, because the report withholds their name."

[THE TIMELINE - 4:00 to 8:00 | about ${words(240, 1080)} words]
"Nine forty: the van leaves the depot, per the gate log."
"Eleven forty seven: the fuel receipt. One account says the van was already at the second checkpoint by eleven thirty."
"Two accounts conflict here. The gate log says one thing. W says another. Here is why that matters: the gap is the entire case."
"One o'clock: the van is found, unlocked, eleven kilometres past the turn."

[THE TURNING POINT - 8:00 to 11:00 | about ${words(180, 1080)} words]
"The case changes here, on the phone records."
Show the columns of the call log on screen.
"It proves a call was placed at midnight from a cell tower near the fuel station. It does not prove who was holding the phone, and that distinction matters."

[EVIDENCE AND DISPUTE - 11:00 to 14:00 | about ${words(180, 1080)} words]
"One reading says the van never reached the second checkpoint, which fits the missing hour."
"The other reading says the checkpoint clock was wrong, which the depot disputes."
"Here is what I could not verify: whether the receipt time is from the pump or the till."

[WHERE IT STANDS - 14:00 to 16:30 | about ${words(150, 1080)} words]
"Today the case stands open, with no charges brought against anyone named."
"Everything I have said about the driver comes from the released report. He has the right of reply, and this channel will publish it."

[SOURCES AND CLOSE - 16:30 to 18:00 | about ${words(90, 1080)} words]
Source list on screen: incident report, two witness statements, inquest transcript, gate log.
"Next case in this series: a missing shipment and the question nobody has answered."`,
    commonMistakes: [
      'Naming a suspect in the first minute on the strength of atmosphere rather than evidence, which turns a case video into rumor.',
      'Skipping the sourcing line, which leaves the viewer unable to tell record from speculation.',
      'Presenting a timeline without dates on screen, so the viewer cannot follow the sequence or check it later.',
      'Speculating about guilt beyond what the public record states, which is both unfair to the people named and a legal risk.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 18 minutes', value: `About ${fills(1080)} words` },
      { metric: 'Cold open block to write first', value: '0:00 to 1:00' },
      { metric: 'Words per minute of finished video', value: `${paceNote(1080)}` },
    ],
    faqs: [
      {
        question: 'How do I write a true crime video without risking defamation?',
        answer: 'Use the public record, name people only as the record names them, state each source as you rely on it, keep an explicit line between what is proven and what is alleged, and note that anyone named has the right of reply. This template is a writing scaffold, not legal advice.',
      },
      {
        question: 'How long should a true crime video be?',
        answer: `A single case usually needs 15 to 25 minutes to carry a timeline and the disputes inside it. An 18 minute case video is roughly ${fills(1080)} words of narration. If your case fits in 8 minutes, that is a segment, not a case.`,
      },
      {
        question: 'How do I build suspense without sensationalizing?',
        answer: 'Withhold the case status, not the facts. Open on one small verifiable detail, present the timeline in order, and let the conflict between sources carry the tension. Mood music over a summary of the crime is sensationalism; a dated receipt is suspense.',
      },
      {
        question: 'Should I cover unsolved cases?',
        answer: 'Unsolved cases can be covered responsibly if you stay within the released record and say plainly what is unresolved. Be more careful, not less, when nobody has been convicted, and avoid anything resembling an accusation.',
      },
    ],
    keywords: [
      'true crime video script template',
      'true crime youtube structure',
      'how to script a true crime video',
      'mystery video script format',
      'documentary crime narration',
    ],
    relatedTemplates: ['storytime', 'video-essay', 'history-story', 'podcast-clip'],
    relatedGuides: ['youtube-storytelling-narrative-structures', 'youtube-watch-time-psychology'],
  },

  'news-commentary': {
    slug: 'news-commentary',
    title: 'News Commentary',
    metaTitle: 'News Commentary Script Template: Free Outline and Example',
    metaDescription: 'A news commentary script template with a claim first structure: source, what happened, what it means, and where you could be wrong.',
    category: 'Commentary',
    templateText: `[CLAIM FIRST - 0:00 to 0:20 | about ${words(20, 600)} words]
State your conclusion in one sentence, before any context.
"[WHAT I THINK HAPPENED], and here is the part most coverage is skipping."
Name the story and the date on screen so the video stays datable.

[WHAT ACTUALLY HAPPENED - 0:20 to 1:30 | about ${words(90, 600)} words]
Give the plainest possible account of the facts, with no opinion in it at all.
"Here is what the report says, in its own words."
Put the original source on screen: headline, date, and outlet.

[WHY I READ IT THAT WAY - 1:30 to 4:00 | about ${words(150, 600)} words]
"The reason this is bigger than it looks: [MECHANISM OR PRECEDENT]."
Walk the inference in visible steps, one sentence each.
"Step one: [FACT]. Step two: [INFERENCE]. Step three: [WHAT FOLLOWS]."

[THE STRONGEST OBJECTION - 4:00 to 6:00 | about ${words(120, 600)} words]
"Here is the best argument against me: [STRONGEST OPPOSING CASE]."
Take it seriously for two sentences before you answer it.
"If that is right, then [WHAT WOULD HAVE TO BE TRUE]."

[WHERE I COULD BE WRONG - 6:00 to 7:30 | about ${words(90, 600)} words]
Name the specific thing that would change your mind.
"If [SPECIFIC EVENT] happens, I am wrong about this, and I will say so on this channel."
"Here is what I do not know yet: [OPEN QUESTION]."

[WHAT TO WATCH NEXT - 7:30 to 9:00 | about ${words(90, 600)} words]
"Three things to watch over the next [PERIOD]: [SIGNAL ONE], [SIGNAL TWO], [SIGNAL THREE]."
Keep them checkable, so the viewer can hold you to them.

[CLOSE - 9:00 to 10:00 | about ${words(60, 600)} words]
Restate the claim in one sentence, then hand the floor over.
"Tell me in the comments where the inference breaks for you. I read those."`,
    annotatedBreakdown: [
      {
        section: 'Claim First (0:00-0:20)',
        purpose: 'Commentary viewers want your read, not a recap. Stating the conclusion up front makes the rest of the video an argument rather than a slow reveal.',
        tips: 'One sentence, in your own voice, with the story and the date on screen so the video can be placed in time later.',
      },
      {
        section: 'What Actually Happened (0:20-1:30)',
        purpose: 'Separate the facts from the opinion, so a viewer who disagrees with you can still use the video.',
        tips: 'Quote the source, show it, and keep the section free of interpretation. Put the outlet and date on screen.',
      },
      {
        section: 'Why I Read It That Way (1:30-4:00)',
        purpose: 'Show the reasoning as visible steps, which is what makes commentary discussable instead of assertable.',
        tips: 'Number the steps out loud. If a step is an inference rather than a fact, say which one.',
      },
      {
        section: 'The Strongest Objection (4:00-6:00)',
        purpose: 'Answer the best version of the opposing case. This is what earns an audience that includes people who disagree.',
        tips: 'Argue it properly for two sentences before you answer. A weak version of the other side reads as avoidance.',
      },
      {
        section: 'Where I Could Be Wrong (6:00-7:30)',
        purpose: 'Commit in advance to the evidence that would change your position, which is what makes a claim checkable later.',
        tips: 'Name a specific, checkable event, not a vague possibility.',
      },
      {
        section: 'What to Watch Next (7:30-9:00)',
        purpose: 'Give the viewer three checkable signals, so the video has a life after publication.',
        tips: 'Keep the signals concrete enough that a follow up video can confirm or deny each one.',
      },
      {
        section: 'Close (9:00-10:00)',
        purpose: 'Restate the claim and invite the disagreement that makes the comments worth reading.',
        tips: 'Ask where the reasoning breaks, not whether people agree.',
      },
    ],
    filledExample: `[CLAIM FIRST - 0:00 to 0:20 | about ${words(20, 600)} words]
"Every outlet covered the new platform rule as a change to creators. It is actually a change to advertisers, and the difference matters for anyone paid per view."
Story headline and date pinned on screen. This is an invented example for illustration.

[WHAT ACTUALLY HAPPENED - 0:20 to 1:30 | about ${words(90, 600)} words]
"The rule says brands must disclose when a creator is paid directly rather than through the platform."
Show the announcement page on screen.
"Here is what the announcement says, in its own words. Note that it does not mention payouts at all."

[WHY I READ IT THAT WAY - 1:30 to 4:00 | about ${words(150, 600)} words]
"The reason this is bigger than it looks: the platform is moving settlement risk from itself to the brand."
"Step one: disclosure was already required. Step two: the platform now verifies it. Step three: when verification fails, the brand carries the penalty, so brands will start paying creators through the platform instead."
"Watch who complains first. If my reading is right, it will be the brands with their own payment systems."

[THE STRONGEST OBJECTION - 4:00 to 6:00 | about ${words(120, 600)} words]
"Here is the best argument against me: this is enforcement of an existing rule, not a policy change, and nothing about payment actually moves."
"A disclosure rule that is suddenly enforced does change behavior, but not necessarily the plumbing."
"If that is right, then the brand complaints will never arrive, because nothing about their incentives changed."

[WHERE I COULD BE WRONG - 6:00 to 7:30 | about ${words(90, 600)} words]
"If brands are still paying creators directly six months from now, I am wrong about this, and I will say so on this channel."
"Here is what I do not know yet: whether the platform takes a fee on in platform payments, which the announcement does not say."

[WHAT TO WATCH NEXT - 7:30 to 9:00 | about ${words(90, 600)} words]
"Three things to watch over the next quarter: whether big brands switch their payment systems, whether the platform publishes a fee schedule, and whether any creator contract changes its payment clause."

[CLOSE - 9:00 to 10:00 | about ${words(60, 600)} words]
"It is a payout structure change wearing a disclosure rule's clothes, and the people it moves first are the brands."
"Tell me in the comments where the inference breaks for you. I read those."`,
    commonMistakes: [
      'Recapping the news for the first two minutes before saying anything, which is what every other outlet already did.',
      'Mixing facts and opinion in the same breath, so a viewer cannot tell which parts are reported and which parts are yours.',
      'Answering a weak version of the opposing case, which makes the argument look like it needs protection.',
      'Making a claim you will never revisit, instead of naming the evidence that would change your mind.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 10 minutes', value: `About ${fills(600)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(600)}` },
    ],
    faqs: [
      {
        question: 'Should I state my opinion at the start of a commentary video?',
        answer: 'Yes, in one sentence. Commentary viewers come for your read of the story, and a video that recaps first and opines later is competing with every news outlet on the part of the job you are least needed for.',
      },
      {
        question: 'How long should a commentary video be?',
        answer: `Most commentary works between 8 and 15 minutes: long enough for the facts, the reasoning, and a real objection. A 10 minute video is about ${fills(600)} words of narration. If the story has no objection worth answering, it is a post, not a video.`,
      },
      {
        question: 'How do I comment on news without spreading misinformation?',
        answer: 'Quote the primary source on screen, keep the fact section free of interpretation, label your inferences as inferences, and state what you could not verify. If you later find you were wrong, name the specific thing that changed and correct it on the channel.',
      },
      {
        question: 'How do I handle a story that develops after I publish?',
        answer: 'Name checkable signals in the video so a follow up can confirm or deny them, and put the story date on screen. That way the video stays honest about when it was made rather than aging into a false claim.',
      },
    ],
    keywords: [
      'news commentary video script template',
      'commentary youtube script',
      'how to script a commentary video',
      'opinion video structure',
      'reaction to news script',
    ],
    relatedTemplates: ['reaction', 'video-essay', 'gaming-lets-play', 'podcast-clip'],
    relatedGuides: ['youtube-hook-examples', 'youtube-pattern-interrupts'],
  },

  'challenge-30-days': {
    slug: 'challenge-30-days',
    title: '30 Day Challenge',
    metaTitle: '30 Day Challenge Script Template: Free Outline and Example',
    metaDescription: 'An I tried X for 30 days script template with rules, honest logs, a low point, and measured results, plus word budgets and example scripts.',
    category: 'Challenge',
    templateText: `[HOOK - 0:00 to 0:20 | about ${words(20, 720)} words]
Open on the single most dramatic day of the month, not day one.
"[THE RULE I SET], and by day [NUMBER] I was ready to quit."
Put the final measurement on screen in the first seconds.

[THE RULES AND WHY - 0:20 to 1:30 | about ${words(90, 720)} words]
"I am doing [SPECIFIC VERSION OF THE CHALLENGE] for 30 days."
"The rules are: [RULE ONE], [RULE TWO], [RULE THREE]. Nothing vague, everything checkable."
"Here is the starting measurement: [BASELINE], taken on [DATE]."

[WEEK ONE AND TWO - 1:30 to 4:30 | about ${words(180, 720)} words]
Compress the early period into the two or three moments that show the pattern.
"Days one to three: [WHAT IT FELT LIKE]."
"By day six, [FIRST REAL CHANGE OR PROBLEM]."
Show a real log: a note, a screenshot, a scale reading, a photo from the date.

[THE LOW POINT - 4:30 to 7:00 | about ${words(150, 720)} words]
"Day [NUMBER] is where this almost ended, and here is exactly what happened."
Do not dramatize a minor inconvenience. Use the moment you genuinely considered stopping.
"Here is what I changed to keep going: [THE ADJUSTMENT]."

[WEEK THREE AND FOUR - 7:00 to 9:30 | about ${words(150, 720)} words]
Show the adjustment working or failing, in the same logged format as the start.
"After the change, [MEASURED OR OBSERVED EFFECT]."

[THE MEASUREMENT - 9:30 to 11:00 | about ${words(90, 720)} words]
Compare the start and end on the same measure, taken the same way.
"Day one: [NUMBER]. Day thirty: [NUMBER]."
Say plainly what the difference could be explained by: "This could be [ALTERNATIVE EXPLANATION], not the challenge."

[WHAT I KEPT AND WHAT I DROPPED - 11:00 to 11:45 | about ${words(45, 720)} words]
"Staying: [SPECIFIC HABIT], because [REASON]. Dropping: [SPECIFIC THING], because [REASON]."
"Would I do it again? [HONEST ANSWER]."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'The challenge format is judged on whether the results are real. Opening on the most difficult day, with the measurement on screen, sets that expectation immediately.',
        tips: 'Open on the payoff day, not day one. Put the final number on screen before you explain anything.',
      },
      {
        section: 'Rules and Baseline (0:20-1:30)',
        purpose: 'A challenge without fixed rules is an anecdote. Checkable rules and a dated baseline are what make the result mean something.',
        tips: 'Write the rules as three lines the viewer could apply themselves, and show the baseline measurement being taken.',
      },
      {
        section: 'Weeks One and Two (1:30-4:30)',
        purpose: 'Establish the pattern quickly, so the middle of the video can be spent on the parts that were not smooth.',
        tips: 'Compress hard. Show the log entries with their dates, not a narrated summary.',
      },
      {
        section: 'The Low Point (4:30-7:00)',
        purpose: 'Give the month a real turning point. A challenge with no failure reads as a promotion.',
        tips: 'Pick a genuine low and show the adjustment you made. Do not inflate a small inconvenience into a crisis.',
      },
      {
        section: 'The Measurement (9:30-11:00)',
        purpose: 'Compare like with like, and name the other explanations for the change.',
        tips: 'Same measure, same method, and an honest alternative explanation. That honesty is what viewers stay for.',
      },
      {
        section: 'What I Kept (11:00-11:45)',
        purpose: 'Turn the experiment into something the viewer can use, and close the promise made in the hook.',
        tips: 'Name one thing you are keeping and one you are dropping. A clean answer at the end is what makes the video quotable.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20 | about ${words(20, 720)} words]
Open on a hand writing "day 19" on a notebook, then a photo of a dark kitchen at six in the morning.
"I gave up coffee for 30 days but kept the six a.m. wake up. Day 19 is the one that nearly broke it."
Final measurement on screen: a sleep log with a straight average line.

[THE RULES AND WHY - 0:20 to 1:30 | about ${words(90, 720)} words]
"No coffee, no tea, no cola, for 30 days. Everything else about my day stays the same."
"The rules are: no caffeine at all, same wake time as usual, and one honest log line every night. Nothing vague, everything checkable."
"Here is the starting measurement: an average of five hours fifty of sleep over the seven days before I started, taken from my watch."

[WEEK ONE AND TWO - 1:30 to 4:30 | about ${words(180, 720)} words]
"Days one to three: headaches in the afternoon and a strange flatness around two p.m."
"By day six, the headaches were gone and I was falling asleep about forty minutes earlier."
Show a photo of the notebook with the daily lines and the dates visible.

[THE LOW POINT - 4:30 to 7:00 | about ${words(150, 720)} words]
"Day 19 is where this almost ended. A deadline moved, I worked until midnight, and the only thing in the kitchen was a jar of instant coffee."
"I stood there for about a minute. Here is what I changed to keep going: I decided the rule was about caffeine, not about willpower, so I made tea the next morning and logged it. It was the wrong call and I logged that too."

[WEEK THREE AND FOUR - 7:00 to 9:30 | about ${words(150, 720)} words]
"After that, the log shows something I did not expect: the early nights stopped feeling like a sacrifice and started being the part of the day I looked forward to."

[THE MEASUREMENT - 9:30 to 11:00 | about ${words(90, 720)} words]
"Day one: five hours fifty average. Day thirty: six hours forty average, same watch, same measure."
"This could be the season, or the earlier nights, or the fact that a deadline ended. The challenge is only one explanation."

[WHAT I KEPT AND WHAT I DROPPED - 11:00 to 11:45 | about ${words(45, 720)} words]
"Staying: the six a.m. wake up, because the mornings got easier. Dropping: the absolute rule, because day 19 showed me it was about caffeine, not about being perfect."
"Would I do it again? Yes, for two weeks, not 30 days."`,
    commonMistakes: [
      'Opening on day one and counting up, which spends the hook on the least interesting part of the month.',
      'Leaving the rules vague, so the viewer cannot tell whether the result came from the challenge or from everything else that changed.',
      'Skipping the failure. A 30 day challenge with no low point reads as an advertisement for the idea.',
      'Presenting the final measurement as proof of cause, when a dozen other things also changed over a month.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 12 minutes', value: `About ${fills(720)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(720)}` },
    ],
    faqs: [
      {
        question: 'How do I keep a 30 day challenge video from being repetitive?',
        answer: `Compress the first two weeks into the three moments that show the pattern, then spend most of the runtime on the low point and the adjustment. A daily recap for 30 days is the same beat 30 times. A 12 minute video is about ${fills(720)} words, and most of it should be around the turning point.`,
      },
      {
        question: 'Should I finish the challenge even if I fail?',
        answer: 'You do not have to complete the challenge to make a good video, but you do have to be honest about where it stopped and why. A video that ends on an unplanned day, with the log to show it, is usually more useful than one that was quietly restarted.',
      },
      {
        question: 'How do I measure results credibly?',
        answer: 'Pick one measure before you start, record the baseline on camera, and use the same method at the end. Name the other things that changed over the month, because a challenge video that claims sole credit for a result is the one viewers distrust.',
      },
      {
        question: 'How long should an I tried X for 30 days video be?',
        answer: 'Most run 10 to 20 minutes. The format needs enough room for the rules, the pattern, the low point, and the measurement. If you only have the rules and the result, you have a short, and it should be published as one.',
      },
    ],
    keywords: [
      '30 day challenge video script template',
      'i tried x for 30 days script',
      'challenge video structure',
      'experiment video script',
      '30 day challenge youtube format',
    ],
    relatedTemplates: ['gaming-lets-play', 'before-after-transformation', 'workout-video', 'day-in-the-life-vlog'],
    relatedGuides: ['youtube-storytelling-narrative-structures', 'youtube-watch-time-psychology'],
  },

  'before-after-transformation': {
    slug: 'before-after-transformation',
    title: 'Before and After Transformation',
    metaTitle: 'Before and After Script Template: Free Outline and Example',
    metaDescription: 'A before and after video script template with an honest starting point, the process, the reveal, and what did not work, plus word budgets.',
    category: 'Transformation',
    templateText: `[BEFORE - 0:00 to 0:30 | about ${words(30, 600)} words]
Open on the honest before, unfiltered, same lighting and same angle as the after will use.
"[WHAT THIS LOOKED LIKE AT THE START], and [THE ONE THING THAT WAS WORST ABOUT IT]."
Put the elapsed time and the total cost on screen here, not at the end.

[WHY IT WAS LIKE THIS - 0:30 to 1:30 | about ${words(60, 600)} words]
"[SPECIFIC CAUSE], not [THE OBVIOUS BUT WRONG CAUSE]."
"This is not a skill problem, it is [THE ACTUAL CONSTRAINT]."
Give the viewer a reason this applies to their own version of the problem.

[THE PLAN - 1:30 to 3:00 | about ${words(90, 600)} words]
"Our plan: [THREE STEPS]."
"Budget: [AMOUNT]. Time: [DURATION]. Tools: [SHORT LIST]."
Show the plan on screen so the viewer can follow the same order.

[THE PROCESS - 3:00 to 6:30 | about ${words(210, 600)} words]
Work through each step with the failure in it.
"Step one: [ACTION]. This took [TIME], and it went [HOW]."
"Step two: [ACTION]. Here is where we got it wrong the first time: [THE MISTAKE]."
"Step three: [ACTION]. The fix was [THE FIX], which we only found by [HOW]."
Show the ugly middle: half finished, taped, clamped, and messy.

[THE REVEAL - 6:30 to 8:00 | about ${words(90, 600)} words]
Cut to the after from the exact same angle and lighting as the first shot.
"Same camera, same corner, [TIME] later."
Say the number that matters: "[COST] and [DURATION] total."

[WHAT DID NOT WORK - 8:00 to 9:00 | about ${words(60, 600)} words]
"Three things did not work: [ITEM ONE], [ITEM TWO], [ITEM THREE]."
"Two things I would do differently: [CHANGE ONE] and [CHANGE TWO]."

[WHAT IT COST AND CTA - 9:00 to 10:00 | about ${words(60, 600)} words]
Full cost breakdown, item by item, on screen.
"Full parts list is in the description. Tell me what to transform next."`,
    annotatedBreakdown: [
      {
        section: 'Before (0:00-0:30)',
        purpose: 'The before shot is the whole video. If it is flattering, cropped, or shot in better light, the reveal has nothing to reveal.',
        tips: 'Lock the camera position for the before and keep it for the after. Put time and total cost on screen in the first 30 seconds.',
      },
      {
        section: 'Why It Was Like This (0:30-1:30)',
        purpose: 'Diagnose the cause so the transformation becomes a method rather than a lucky result.',
        tips: 'Name the real constraint, not the obvious one. Viewers apply the fix when they recognize their own version of the cause.',
      },
      {
        section: 'The Plan (1:30-3:00)',
        purpose: 'State the budget, timeline, and steps before the work starts, so the viewer can judge whether they could do it too.',
        tips: 'Three steps, a hard number for money, and a hard number for time. Vague plans make the process unwatchable.',
      },
      {
        section: 'The Process (3:00-6:30)',
        purpose: 'Show the work including the mistakes, which is where the value of the video actually lives.',
        tips: 'Keep the messy middle in frame. A transformation that skips the mistakes is a before and after photo with narration.',
      },
      {
        section: 'The Reveal (6:30-8:00)',
        purpose: 'Pay off the promise from the opening with a direct comparison from the same position.',
        tips: 'Same camera, same lighting, same framing. Cut between the two shots more than once so the difference is readable.',
      },
      {
        section: 'What Did Not Work and Cost (8:00-10:00)',
        purpose: 'Give the honest accounting, which is what makes the video usable for someone planning the same project.',
        tips: 'List the failed items and the full cost breakdown on screen. It is the section viewers screenshot.',
      },
    ],
    filledExample: `[BEFORE - 0:00 to 0:30 | about ${words(30, 600)} words]
Show a small balcony with a rusted railing, a cracked tile, and a dead plant. Fixed camera, flat afternoon light.
"This is a two square metre balcony, it has been unusable for three years, and the worst part was the railing, which was rusted through in two places."
Time and cost on screen: two weekends, and a running total that will be revealed at the end.

[WHY IT WAS LIKE THIS - 0:30 to 1:30 | about ${words(60, 600)} words]
"The previous owner tiled directly onto concrete with no drainage slope, so every winter the water sat against the railing."
"This is not a skill problem, it is a drainage problem that looks like a maintenance problem, so the usual advice about repainting railings does not help."

[THE PLAN - 1:30 to 3:00 | about ${words(90, 600)} words]
"Our plan: strip the tile and the adhesive, fix the slope, then reseal and re tile."
"Budget: 900. Time: two weekends. Tools: an angle grinder, a tile chisel, a spirit level, and a bucket of self levelling compound."
Plan on screen with the three steps in order.

[THE PROCESS - 3:00 to 6:30 | about ${words(210, 600)} words]
"Step one: remove the tile. This took four hours and it went badly, because the old adhesive had bonded to the original screed."
"Step two: fix the slope. Here is where we got it wrong the first time. We laid the compound too thin at the door, so it pooled at the railing."
"Step three: the fix was to add a second thin layer at the high side, which we only found by pouring water on it and watching where it went."
Show the messy middle: half the tiles off, dust everywhere, the bucket of compound.

[THE REVEAL - 6:30 to 8:00 | about ${words(90, 600)} words]
Cut to the after from the exact same camera position: new tile, clear drainage channel, a low planter along the wall.
"Same camera, same corner, two weekends later."
"920 total, and that includes the tools I did not already own, which is about 180 of it."

[WHAT DID NOT WORK - 8:00 to 9:00 | about ${words(60, 600)} words]
"Three things did not work: the first grout choice, which shrank, a cheap tile cutter that broke on the second tile, and the sealing tape along the wall."
"Two things I would do differently: buy the cutter and start the slope from the door rather than the middle."

[WHAT IT COST AND CTA - 9:00 to 10:00 | about ${words(60, 600)} words]
Cost breakdown on screen, item by item, including the tools and the failed purchases.
"Full parts list is in the description. Tell me what to transform next."`,
    commonMistakes: [
      'Shooting the before in worse light or from a worse angle than the after, which breaks the comparison the video is built on.',
      'Skipping the failed attempts, leaving a process video with no process in it.',
      'Hiding the cost until the end, so viewers cannot decide early whether the project is for them.',
      'Naming the obvious cause instead of the real one, which makes the fix look like luck rather than method.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 10 minutes', value: `About ${fills(600)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:30' },
      { metric: 'Words per minute of finished video', value: `${paceNote(600)}` },
    ],
    faqs: [
      {
        question: 'How do I film a before and after so the comparison is fair?',
        answer: 'Lock the camera position, keep the same lens, and shoot both at the same time of day if the space depends on daylight. Write down the settings so the after matches the before exactly. An unfair comparison is the fastest way to lose a transformation audience.',
      },
      {
        question: 'How long should a before and after video be?',
        answer: `Long enough to show the plan, the process with its mistakes, and the reveal. That is usually 8 to 15 minutes, or about ${fills(600)} words of narration for 10 minutes. A quick project with one step should be a short instead.`,
      },
      {
        question: 'Should I include the failures in a transformation video?',
        answer: 'Yes. The failed attempts are where the useful information lives, and viewers tell you so in the comments. A reveal with no mistakes is a photograph, not a project.',
      },
      {
        question: 'How do I decide how much of the process to show?',
        answer: 'Show every step that a viewer would have to do themselves, and cut the waiting. Keep the ordering intact, because a process video is only usable if the viewer can follow the sequence.',
      },
    ],
    keywords: [
      'before and after video script template',
      'transformation video script',
      'makeover video structure',
      'diy transformation script',
      'before after reveal template',
    ],
    relatedTemplates: ['unboxing', 'challenge-30-days', 'workout-video', 'tutorial'],
    relatedGuides: ['youtube-hook-examples', 'script-structure-guide'],
  },

  'channel-trailer': {
    slug: 'channel-trailer',
    title: 'Channel Trailer',
    metaTitle: 'Channel Trailer Script Template: Free Outline and Example',
    metaDescription: 'A channel trailer script template for 60 to 90 seconds: promise, proof, and one subscribe ask, with a beat by beat breakdown.',
    category: 'Channel Growth',
    templateText: `[COLD OPEN - 0:00 to 0:08 | about ${words(8, 75)} words]
Start on your single best piece of footage, mid action. No logo, no music sting.
"[ONE LINE THAT STATES WHAT THIS CHANNEL IS ABOUT]"
Every frame after this must be worth staying for, because there is no time to recover from a slow start.

[THE PROMISE - 0:08 to 0:20 | about ${words(12, 75)} words]
"Every [FREQUENCY], I [WHAT YOU DO] so you can [WHAT THE VIEWER GETS]."
Name the format: "It is usually [LENGTH], and it is always [THE ONE THING YOU ALWAYS DO]."

[THE PROOF - 0:20 to 0:45 | about ${words(25, 75)} words]
Three clips, two to three seconds each, from three different videos.
Cut one: "[SPECIFIC RESULT OR MOMENT]."
Cut two: "[DIFFERENT KIND OF PAYOFF]."
Cut three: "[THE MOST EXTREME VERSION of what you do]."
Let the clips speak. One line of narration over each at most.

[WHO THIS IS FOR - 0:45 to 1:00 | about ${words(15, 75)} words]
"If you [SPECIFIC PROBLEM OR INTEREST], this channel is for you."
"And if you [THE OTHER REASON], start with [SPECIFIC VIDEO]."

[THE ASK - 1:00 to 1:10 | about ${words(10, 75)} words]
"Subscribe if you want [SPECIFIC THING] every [FREQUENCY]."
One ask. Do not also ask for comments, likes, and a bell in the same breath.

[END CARD - 1:10 to 1:15 | about ${words(5, 75)} words]
Hold the last frame with the channel name and one video card.
"[SHORT SIGN OFF THAT MATCHES YOUR OPENER]."`,
    annotatedBreakdown: [
      {
        section: 'Cold Open (0:00-0:08)',
        purpose: 'A channel trailer runs in front of people who have never seen you. The first shot has to be the strongest thing you have ever filmed.',
        tips: 'No logo animation and no intro music. Both spend the only seconds you have on something that is not you.',
      },
      {
        section: 'The Promise (0:08-0:20)',
        purpose: 'Tell the viewer what they will get, how often, and in what form, so subscribing becomes a decision they can make.',
        tips: 'State frequency and typical length. A viewer who knows the shape of the channel is far more likely to subscribe than one who only knows the topic.',
      },
      {
        section: 'The Proof (0:20-0:45)',
        purpose: 'Show three different kinds of payoff rather than three versions of the same one, so the channel does not look like one repeated video.',
        tips: 'Pick clips with different energy levels. Two to three seconds each is enough; a montage longer than 25 seconds stops feeling like proof.',
      },
      {
        section: 'Who This Is For (0:45-1:00)',
        purpose: 'Help the right viewer recognise themselves, and give the wrong viewer permission to leave.',
        tips: 'Name a specific problem or interest, not a demographic. Point to one named video as a starting place.',
      },
      {
        section: 'The Ask (1:00-1:10)',
        purpose: 'One clear action, tied to the promise rather than to a favour.',
        tips: 'Ask once, and connect it to the frequency promise: subscribe for the thing you already said you make.',
      },
      {
        section: 'End Card (1:10-1:15)',
        purpose: 'Close on a frame that can carry a video card and a readable channel name.',
        tips: 'Keep the final five seconds visually still. Anything moving fights the end card.',
      },
    ],
    filledExample: `[COLD OPEN - 0:00 to 0:08 | about ${words(8, 75)} words]
Open mid action: hands lifting a rusted bolt out of a doorway, dust falling.
"This channel is about fixing the parts of a house that other people cover up."
This is an invented channel used to show the structure.

[THE PROMISE - 0:08 to 0:20 | about ${words(12, 75)} words]
"Every other Saturday, I take one small problem in an old house and fix it properly, so you can do the same one without guessing."
"It is usually about twelve minutes, and it always ends with the full cost on screen."

[THE PROOF - 0:20 to 0:45 | about ${words(25, 75)} words]
Cut one, two seconds: a drain unblocking and water running clear. "Two hundred year old pipe, running again."
Cut two, two seconds: a door hanging straight on new hinges. "No more draught."
Cut three, two seconds: a wall coming down to reveal a bricked in window. "The best one yet."

[WHO THIS IS FOR - 0:45 to 1:00 | about ${words(15, 75)} words]
"If you own an old building and every tradesperson tells you it is fine when it is obviously not, this channel is for you."
"If you just like watching old houses get put right, start with the damp episode."

[THE ASK - 1:00 to 1:10 | about ${words(10, 75)} words]
"Subscribe if you want one of these every other Saturday."

[END CARD - 1:10 to 1:15 | about ${words(5, 75)} words]
Hold a still frame with the channel name and one video card.
"Go and fix one small thing."`,
    commonMistakes: [
      'Opening with a logo or an animated intro, which spends the first seconds on branding instead of the strongest footage.',
      'Making the trailer longer than 90 seconds. A trailer competes with the video the viewer actually clicked on.',
      'Showing three clips that are all the same kind of moment, which makes the channel look like one video on repeat.',
      'Asking for a subscribe, a like, a comment, and a notification in the same sentence, which usually produces none of them.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills a 75 second trailer', value: `About ${fills(75)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:08' },
      { metric: 'Words per minute of finished video', value: `${paceNote(75)}` },
    ],
    faqs: [
      {
        question: 'How long should a channel trailer be?',
        answer: `Between 60 and 90 seconds. A 75 second trailer is roughly ${fills(75)} words of narration at ${paceProse(75)}, which is very little, so every line has to carry weight. Anything longer stops being a trailer and starts being a video.`,
      },
      {
        question: 'Should I put a channel trailer on my channel homepage?',
        answer: 'Yes, if you have footage strong enough to open with. The trailer is what a new visitor sees first, so it should show your best work rather than explain your upload schedule.',
      },
      {
        question: 'Should I update my channel trailer when my channel changes?',
        answer: 'Update it when your format changes, not when your subscriber count does. A trailer that promises a weekly series you no longer make is worse than no trailer, because it sets an expectation the channel does not meet.',
      },
      {
        question: 'How many subscribe asks should a channel trailer have?',
        answer: 'One, near the end, tied to the promise you made at the start. Repeated asks inside a 90 second trailer make the viewer feel sold to rather than shown something.',
      },
    ],
    keywords: [
      'channel trailer script template',
      'youtube trailer script',
      'channel intro video template',
      'how to write a channel trailer',
      'youtube channel trailer format',
    ],
    relatedTemplates: ['video-intro', 'youtube-shorts', 'storytime', 'top-10-list'],
    relatedGuides: ['first-30-seconds', 'youtube-hook-examples'],
  },

  'video-intro': {
    slug: 'video-intro',
    title: 'YouTube Video Intro',
    metaTitle: 'Free YouTube Intro Script Template: First 30 Seconds',
    metaDescription: 'A YouTube intro script template for the first 30 seconds: hook, promise, and preview beats, with word budgets and three openings.',
    category: 'Structure',
    templateText: `[BEAT ONE: THE HOOK - 0:00 to 0:05 | about ${words(5, 30)} words]
One sentence, no greeting, no channel name.
"[THE MOST INTERESTING TRUE THING ABOUT THIS VIDEO]"
Say it over the single most relevant shot you have.

[BEAT TWO: THE PROMISE - 0:05 to 0:15 | about ${words(10, 30)} words]
"[WHAT THE VIEWER WILL KNOW OR SEE BY THE END]"
Make it specific and finite: "By the end of this video you will [CONCRETE RESULT]."
No life story. If a sentence does not add information, cut it.

[BEAT THREE: THE REASON TO STAY - 0:15 to 0:22 | about ${words(7, 30)} words]
"[THE ONE THING THAT MAKES THIS VERSION DIFFERENT]"
"The part that surprised me is [SURPRISING DETAIL]."

[BEAT FOUR: THE PREVIEW - 0:22 to 0:30 | about ${words(8, 30)} words]
Show, do not tell: two or three seconds of the payoff moment, then cut into the video proper.
"[ONE LINE OVER THE PREVIEW CLIP]"
Cut on the last word. Do not fade.

[IF YOU NEED A COMPROMISE VERSION - 0:00 to 0:30 total]
Hook sentence, promise sentence, then straight into content with the preview placed at the first natural break instead.`,
    annotatedBreakdown: [
      {
        section: 'Beat One: The Hook (0:00-0:05)',
        purpose: 'Give the viewer a reason to keep watching before they have any reason to trust you.',
        tips: 'One sentence, no greeting, no channel name. The most common first 5 seconds you could write, such as "hey guys, welcome back", is also the most skipped.',
      },
      {
        section: 'Beat Two: The Promise (0:05-0:15)',
        purpose: 'Tell the viewer what they will get and that it is finite, which is what converts curiosity into commitment.',
        tips: 'Use a concrete result, not a topic. "You will be able to remove any background" beats "today we are talking about Photoshop".',
      },
      {
        section: 'Beat Three: The Reason to Stay (0:15-0:22)',
        purpose: 'Differentiate this video from the other ones on the same topic that the viewer has already seen.',
        tips: 'Name the surprise. If you cannot find one, the hook is not ready yet.',
      },
      {
        section: 'Beat Four: The Preview (0:22-0:30)',
        purpose: 'Show the payoff so the viewer knows what they are waiting for, then get out of the way.',
        tips: 'Two or three seconds, cut on the last word of the line, no fade. Intros that linger cost you the middle of the video.',
      },
      {
        section: 'Compromise Version (0:00-0:30 total)',
        purpose: 'Some videos have a payoff that needs the whole runtime to build, so the preview clip would spoil it. The compromise keeps the beats and moves the preview.',
        tips: 'Keep the hook and the promise unchanged, cut straight into the content, and place the preview at the first natural break instead of the opening.',
      },
    ],
    filledExample: `[BEAT ONE: THE HOOK - 0:00 to 0:05 | about ${words(5, 30)} words]
Over a shot of a spreadsheet scrolling to a blank row.
"I built this whole dashboard with three formulas and no add ons."

[BEAT TWO: THE PROMISE - 0:05 to 0:15 | about ${words(10, 30)} words]
"By the end of this video you will have the same three formulas in your own sheet, in about ten minutes of work, with nothing to install."
This is an invented example used to show the beats.

[BEAT THREE: THE REASON TO STAY - 0:15 to 0:22 | about ${words(7, 30)} words]
"The part that surprised me is that the third formula does the work people usually install a plugin for."

[BEAT FOUR: THE PREVIEW - 0:22 to 0:30 | about ${words(8, 30)} words]
Show three seconds of the finished dashboard updating live, then cut straight into the screen recording.
"No plugins, no scripts, just the sheet."`,
    commonMistakes: [
      'Opening with a greeting and a channel name, which costs the first five seconds and gives the viewer nothing.',
      'Explaining the backstory of how you came to make the video before saying what the video is about.',
      'Writing a hook that describes the topic instead of the payoff, so the viewer cannot tell what they will get.',
      'Letting the intro run past 30 seconds, which pushes the actual content past the point where many viewers have already decided.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills a 30 second intro', value: `About ${fills(30)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:05' },
      { metric: 'Words per minute of finished video', value: `${paceNote(30)}` },
    ],
    faqs: [
      {
        question: 'How long should a YouTube intro be?',
        answer: `Thirty seconds at the outside, and often less. The first 30 seconds hold about ${fills(30)} words at ${paceProse(30)}, which is roughly four short sentences. If your intro needs more than that, the material after it is starting too late.`,
      },
      {
        question: 'Should I greet my viewers at the start?',
        answer: 'A greeting is a habit from live or episodic formats. For a video found through search, the greeting uses the seconds that decide whether the viewer stays. Greet returning viewers in the first comment or at the end instead.',
      },
      {
        question: 'Do I need a preview of what is coming in the intro?',
        answer: 'A two or three second preview of the payoff works well, because it tells the viewer what they are waiting for. A full montage of the whole video does not, because it removes the reason to keep watching.',
      },
      {
        question: 'Can I use the same intro structure for every video?',
        answer: 'Yes on a channel, no in the wording. A consistent beat structure helps returning viewers, but the sentences themselves have to change, or the opening stops carrying information.',
      },
    ],
    keywords: [
      'youtube intro script template',
      'first 30 seconds script',
      'youtube video hook template',
      'how to write a video intro',
      'youtube opening script',
    ],
    relatedTemplates: ['channel-trailer', 'youtube-shorts', 'educational-explainer', 'faceless-narration'],
    relatedGuides: ['first-30-seconds', 'youtube-hook-examples'],
  },

  'faceless-narration': {
    slug: 'faceless-narration',
    title: 'Faceless Channel Narration',
    metaTitle: 'Faceless Channel Script Template: Free Voiceover Example',
    metaDescription: 'A faceless channel script template written for voiceover: narration beats, b roll cues, and on screen text, with word budgets per minute.',
    category: 'Voiceover',
    templateText: `[HOOK - 0:00 to 0:20 | about ${words(20, 480)} words]
Write the narration first, then choose the shot. The words carry this format.
"[ONE STRONG CLAIM OR QUESTION]"
[B ROLL: the single strongest visual you have for this topic, mid motion]
[TEXT ON SCREEN: the claim, in five words or fewer]

[PROMISE - 0:20 to 0:50 | about ${words(30, 480)} words]
"[WHAT THE VIEWER GETS BY THE END]"
[B ROLL: a second angle on the same subject]

[BODY PART ONE - 0:50 to 3:00 | about ${words(120, 480)} words]
"[POINT ONE, stated plainly]"
Then the mechanism: "[WHY IT WORKS]"
Then the example: "[CONCRETE CASE]"
[B ROLL: change the shot every 10 to 15 seconds. Never repeat the same clip twice.]
[TEXT ON SCREEN: any number or name the viewer should remember]

[BODY PART TWO - 3:00 to 5:30 | about ${words(150, 480)} words]
"[POINT TWO]"
"[THE PART PEOPLE GET WRONG]"
[B ROLL: show the mistake, not just describe it, where you can]

[BODY PART THREE - 5:30 to 7:30 | about ${words(120, 480)} words]
"[POINT THREE]"
"[HOW THE THREE FIT TOGETHER]"
[B ROLL: return to the opening shot, now that it means something different]

[CLOSE - 7:30 to 8:00 | about ${words(30, 480)} words]
"[ONE SENTENCE SUMMARY]"
"[WHERE TO GO FOR MORE, or THE NEXT VIDEO]"
[B ROLL: end on a still frame that can hold an end card without competing with it]`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'With no face and no studio, the writing is the entire personality of the channel. The first sentence has to do the work a presenter would otherwise do.',
        tips: 'Write the narration before you look for footage. If the line is not interesting with your eyes closed, no b roll will fix it.',
      },
      {
        section: 'Promise (0:20-0:50)',
        purpose: 'Establish what the video delivers, because a faceless channel has no recognisable host to hold attention on trust alone.',
        tips: 'Keep it to two sentences and move on. Long intros without a face are the hardest part of any video to sit through.',
      },
      {
        section: 'Body (0:50-7:30)',
        purpose: 'Carry the whole video on the writing, with visuals changing often enough to keep the screen alive.',
        tips: 'Change the shot every 10 to 15 seconds, put numbers on screen rather than reading them aloud twice, and never reuse a clip in the same video.',
      },
      {
        section: 'B Roll Discipline (0:50-7:30)',
        purpose: 'With no presenter on screen, the visuals are the only thing keeping the frame alive, so the shot plan has to be as deliberate as the script.',
        tips: 'Plan the shot list before editing, change the shot every 10 to 15 seconds, and never repeat a clip inside one video. Keep on screen text to the numbers and names a viewer might want to write down.',
      },
      {
        section: 'Close (7:30-8:00)',
        purpose: 'Land the summary in one sentence and hand the viewer a next step.',
        tips: 'Return to the opening image so the video closes visually where it started. End on a still frame that can carry an end card.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20 | about ${words(20, 480)} words]
"Every city has a street that floods first, and the reason is usually decided a hundred years before anyone builds on it."
[B ROLL: drone shot of a street under shallow water, moving slowly]
[TEXT ON SCREEN: "The street that floods first"]

[PROMISE - 0:20 to 0:50 | about ${words(30, 480)} words]
"By the end of this video you will be able to look at any neighbourhood and tell which streets were built on old waterways."
[B ROLL: an old map fading over a modern street view]
This is an invented example used to show the structure.

[BODY PART ONE - 0:50 to 3:00 | about ${words(120, 480)} words]
"Most cities grew over a network of streams that were buried as the city expanded."
"When a stream is covered, the ground above it stays low, and water still finds it."
"Look at the street names. If a long curved street sits between two straight ones, there was very likely a watercourse there."
[B ROLL: alternate between the map, the modern street, and a curved street sign]
[TEXT ON SCREEN: "Buried water still runs"]

[BODY PART TWO - 3:00 to 5:30 | about ${words(150, 480)} words]
"The mistake is assuming the flood is about the rain."
"Almost always it is about the surface. Paving a field two kilometres uphill moves the water faster and gives it less ground to soak into."
[B ROLL: split screen of a paved lot and a field in heavy rain]

[BODY PART THREE - 5:30 to 7:30 | about ${words(120, 480)} words]
"The third thing to look for is where the newer buildings are."
"Developers build where land is cheap, and land is cheap where it floods, so the newest construction is often the most exposed."
"Put the three together and you can read a neighbourhood in about ten minutes."
[B ROLL: return to the opening flooded street shot, now with a map overlay]

[CLOSE - 7:30 to 8:00 | about ${words(30, 480)} words]
"Low ground, curved streets, and new buildings: that is the whole method."
"The next video looks at what happens when a city decides to uncover a buried river."
[B ROLL: hold a still frame with room at the right for an end card]`,
    commonMistakes: [
      'Writing the script after collecting footage, which produces narration that describes the visuals instead of saying something.',
      'Leaving one clip on screen for a minute or more, which makes a voiceover video feel static.',
      'Reading numbers aloud and also putting them on screen, which wastes words the viewer could have spent on the argument.',
      'Sounding like an encyclopedia article, because the narration was written to be read rather than said.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 8 minutes', value: `About ${fills(480)} words` },
      { metric: 'Shot change cadence to plan for', value: 'Every 10 to 15 seconds' },
      { metric: 'Words per minute of finished video', value: `${paceNote(480)}` },
    ],
    faqs: [
      {
        question: 'How do I write a faceless channel script?',
        answer: `Write the narration as if it were the only thing the viewer had, then add b roll cues and on screen text. An 8 minute voiceover video is about ${fills(480)} words. If the script is not interesting read aloud with nothing on screen, the visuals will not save it.`,
      },
      {
        question: 'How often should the visuals change in a faceless video?',
        answer: 'Every 10 to 15 seconds is a practical planning cadence. It is fast enough to keep the screen alive and slow enough that each shot can register. Never reuse the same clip twice in one video, because viewers notice and it reads as padding.',
      },
      {
        question: 'Should I use my own voice or a synthetic voice?',
        answer: 'Either can work, but a script written for a synthetic voice has to be simpler, because synthetic delivery flattens long sentences. Whichever you use, test any synthetic narration against your platform rules before publishing at scale.',
      },
      {
        question: 'Do faceless videos need an intro?',
        answer: 'A very short one. With no presenter to greet, the hook and the promise should fit inside about 50 seconds, then the content starts. Anything longer gives the viewer time to remember they were doing something else.',
      },
    ],
    keywords: [
      'faceless channel script template',
      'voiceover script template',
      'faceless youtube video script',
      'b roll script format',
      'narration script structure',
    ],
    relatedTemplates: ['video-intro', 'educational-explainer', 'history-story', 'true-crime'],
    relatedGuides: ['faceless-youtube-channel-scripts', 'youtube-watch-time-psychology'],
  },

  'history-story': {
    slug: 'history-story',
    title: 'History Story',
    metaTitle: 'History Story Script Template for YouTube (Free, Copy-Paste)',
    metaDescription: 'A history story script template with a dated cold open, timeline, primary sources, and a closing argument, plus budgets and sourcing notes.',
    category: 'Documentary',
    templateText: `[COLD OPEN - 0:00 to 0:45 | about ${words(45, 720)} words]
Open on one dated object or document, held on screen for a beat before you speak.
"[DATE] and [ONE CONCRETE DETAIL]."
Do not summarize the period. Start on the smallest true thing you found.

[WHY THIS MATTERS NOW - 0:45 to 1:45 | about ${words(60, 720)} words]
"[THE MODERN QUESTION this story answers]."
"To answer it, we need to go back to [STARTING POINT]."
Say what your sources are, once: "[ARCHIVES, LETTERS, or RECORDS]."

[THE SETUP - 1:45 to 4:00 | about ${words(135, 720)} words]
"[WHO WAS INVOLVED, and WHAT THEY WANTED]."
"At this point, [THE STATE OF THINGS], which is the detail that makes what follows surprising."
[B ROLL or GRAPHIC: a map or a simple diagram of the situation, not a wall of text]

[THE TIMELINE - 4:00 to 8:00 | about ${words(240, 720)} words]
"[DATE]: [EVENT], per [SOURCE]."
"[DATE]: [EVENT], which is where the plan changed."
"[DATE]: [TURNING POINT]."
Put every date on screen as you say it, and cite the source for the contested events.

[THE CONSEQUENCE - 8:00 to 10:30 | about ${words(150, 720)} words]
"[WHAT CHANGED AS A RESULT], and for whom."
"Here is what the record does not tell us: [UNCERTAINTY]."

[WHAT THIS MEANS FOR THE OPENING QUESTION - 10:30 to 12:00 | about ${words(90, 720)} words]
Return to the question you opened with and answer it directly.
"[YOUR ANSWER, in one paragraph, marked as your reading rather than the record]."

[SOURCES AND CLOSE - 12:00 to 12:30 | about ${words(45, 720)} words]
List the sources on screen with dates.
"Next in this series: [NEXT PERIOD OR EVENT], and the document that changed how I read it."`,
    annotatedBreakdown: [
      {
        section: 'Cold Open (0:00-0:45)',
        purpose: 'A dated object makes the past feel specific rather than decorative, and it signals that the video is built on records.',
        tips: 'Hold the image before speaking. One concrete detail beats a sweeping summary of the era.',
      },
      {
        section: 'Why This Matters Now (0:45-1:45)',
        purpose: 'Give the viewer the question the story is answering, so the timeline has a destination.',
        tips: 'State the modern question in one sentence and name your source types once, early.',
      },
      {
        section: 'The Setup (1:45-4:00)',
        purpose: 'Establish who wanted what and what the situation was, so the events that follow can be surprising.',
        tips: 'Use a map or a simple diagram. A screen of dense text is the fastest way to lose a history audience.',
      },
      {
        section: 'The Timeline (4:00-8:00)',
        purpose: 'The sequence of dated events is the spine of the story, and dates on screen let viewers follow and check.',
        tips: 'Cite the source for each dated event, especially contested ones. Keep commentary out of the timeline.',
      },
      {
        section: 'The Consequence (8:00-10:30)',
        purpose: 'Show what changed and for whom, and be explicit about what the record leaves unknown.',
        tips: 'Name the uncertainty. History videos that admit gaps are trusted more than ones that fill them.',
      },
      {
        section: 'Answer and Sources (10:30-12:30)',
        purpose: 'Answer the opening question, label it as your reading, and put the sources on screen.',
        tips: 'Distinguish your interpretation from the record in the wording itself. Hold the source frame long enough to read.',
      },
    ],
    filledExample: `[COLD OPEN - 0:00 to 0:45 | about ${words(45, 720)} words]
Hold on a shipping register page with a single line ruled through it, then the date in the margin.
"March 1809, and someone has crossed out a cargo of grain after it was already loaded."
This is an invented example used to show the structure; no real voyage is described.

[WHY THIS MATTERS NOW - 0:45 to 1:45 | about ${words(60, 720)} words]
"We still argue about who pays when a shipment fails, and the first version of that argument was written into port ledgers like this one."
"To answer it, we need to go back to a single harbour and a single winter."
"Everything here comes from the port register, the harbourmaster's letter book, and two contemporary newspaper notices."

[THE SETUP - 1:45 to 4:00 | about ${words(135, 720)} words]
"The harbourmaster had been told to clear the quay before the ice set in, and the merchant who owned the grain wanted it held until the price rose."
"At this point the harbour was already two weeks behind, and three ships were waiting outside, which is the detail that makes what follows surprising."
[B ROLL: a simple map of the harbour with the three waiting ships marked]

[THE TIMELINE - 4:00 to 8:00 | about ${words(240, 720)} words]
"January 12: the register shows grain loaded onto the third ship, per the loading log."
"January 19: the same line is struck through, per the register margin."
"February 2: the harbourmaster writes that he acted on his own authority, per the letter book."
"February 9: a newspaper notice names the merchant, which is where the plan changed."

[THE CONSEQUENCE - 8:00 to 10:30 | about ${words(150, 720)} words]
"The grain was sold at a lower price and the loss was carried by the merchant, and the harbourmaster kept his post."
"Here is what the record does not tell us: whether the merchant was told before the line was struck, and the letter book has a gap in exactly that week."

[WHAT THIS MEANS FOR THE OPENING QUESTION - 10:30 to 12:00 | about ${words(90, 720)} words]
"The question was who pays when a shipment fails. My reading is that the answer was settled by whoever could act first, not by any rule, and the ledger is the evidence."

[SOURCES AND CLOSE - 12:00 to 12:30 | about ${words(45, 720)} words]
Source list on screen: port register, letter book, two newspaper notices, with dates.
"Next in this series: the winter the harbour froze twice, and the document that changed how I read it."`,
    commonMistakes: [
      'Opening with a broad summary of the era instead of a dated, specific object or document.',
      'Building a timeline without dates on screen, so the viewer loses the sequence exactly when it matters most.',
      'Filling gaps in the record with invented detail rather than naming the uncertainty.',
      'Presenting your interpretation as though it were the record, which is the difference between history and historical fiction.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 12 minutes', value: `About ${fills(720)} words` },
      { metric: 'Cold open block to write first', value: '0:00 to 0:45' },
      { metric: 'Words per minute of finished video', value: `${paceNote(720)}` },
    ],
    faqs: [
      {
        question: 'How long should a history video be?',
        answer: `One story per video, usually 10 to 20 minutes. A 12 minute history story is about ${fills(720)} words, which is enough for a setup, a dated timeline, and a consequence. Larger periods should be split by question, not by century.`,
      },
      {
        question: 'How do I cite sources in a history video?',
        answer: 'Name the source aloud the first time you rely on it, put contested dates and documents on screen as you say them, and close with a readable source list. Quoting a primary document briefly, with your own commentary around it, is what makes the video an argument rather than a recital.',
      },
      {
        question: 'What do I do when sources disagree?',
        answer: 'Say so, and show both. Name which account you find more convincing and why. A history video that admits a conflict is more credible than one that quietly picks a version.',
      },
      {
        question: 'Can I use historical images and footage?',
        answer: 'Use material that is in the public domain where you can, credit everything on screen, and keep any copyrighted clip inside your own commentary. Check the rules for your platform and jurisdiction before you publish.',
      },
    ],
    keywords: [
      'history video script template',
      'history story script',
      'documentary history structure',
      'how to script a history video',
      'historical narrative template',
    ],
    relatedTemplates: ['video-essay', 'true-crime', 'educational-explainer', 'faceless-narration'],
    relatedGuides: ['youtube-storytelling-narrative-structures', 'youtube-watch-time-psychology'],
  },

  'science-explainer': {
    slug: 'science-explainer',
    title: 'Science Explainer',
    metaTitle: 'Science Explainer Script Template: Free Outline and Example',
    metaDescription: 'A science explainer script template with a result first hook, method, findings, and a clear limits section, plus word budgets and citations.',
    category: 'Education',
    templateText: `[RESULT FIRST - 0:00 to 0:30 | about ${words(30, 540)} words]
Open on the finding, stated carefully, with the paper on screen.
"[WHAT WAS FOUND], according to a [YEAR] study in [JOURNAL]."
Say the scale immediately: "[SAMPLE SIZE], [TYPE OF STUDY]."

[WHAT WE ALREADY KNEW - 0:30 to 1:30 | about ${words(60, 540)} words]
"[THE ESTABLISHED BACKGROUND], in two sentences."
"Here is the gap this study was trying to close: [OPEN QUESTION BEFORE THE STUDY]."

[HOW THEY TESTED IT - 1:30 to 3:30 | about ${words(120, 540)} words]
"[METHOD, in plain language]."
"Think of it like [ANALOGY]."
"Here is what the method can and cannot detect: [LIMIT OF THE METHOD]."

[WHAT THEY FOUND - 3:30 to 5:30 | about ${words(120, 540)} words]
"[THE HEADLINE RESULT, with the number on screen]."
"[THE SECONDARY FINDING, which is often more interesting]."
"[WHAT THE AUTHORS THEMSELVES FLAGGED as uncertain]."

[WHAT IT DOES NOT MEAN - 5:30 to 7:00 | about ${words(90, 540)} words]
"This does not mean [OVERREACH ONE], and it does not mean [OVERREACH TWO]."
"The study looked at [POPULATION OR CONDITION], so anything outside that is a different question."

[WHAT WOULD CHANGE IT - 7:00 to 8:00 | about ${words(60, 540)} words]
"The result would be weaker if [SPECIFIC REPLICATION TEST] came back different."
"Here is what I would want to see next: [NEXT STUDY], and why."

[SOURCES AND CLOSE - 8:00 to 9:00 | about ${words(60, 540)} words]
Show the citation, the year, and the sample size on screen and hold it long enough to read.
"If you take one thing from this: [ONE SENTENCE RESTATEMENT OF WHAT IS ACTUALLY KNOWN]."`,
    annotatedBreakdown: [
      {
        section: 'Result First (0:00-0:30)',
        purpose: 'Science viewers are skeptical, so leading with the finding and its scale signals that the video is about evidence rather than wonder.',
        tips: 'Name the study, the year, and the sample size before any explanation. Quantify the claim in the first 30 seconds.',
      },
      {
        section: 'What We Already Knew (0:30-1:30)',
        purpose: 'Place the study in context, so the viewer can tell what is new from what is a restatement of known results.',
        tips: 'Two sentences of background, then the open question the study addressed. Do not teach the whole field first.',
      },
      {
        section: 'How They Tested It (1:30-3:30)',
        purpose: 'Explain the method in plain language, because the method is what makes a result believable or not.',
        tips: 'Use one analogy and then name what the method cannot detect. Skip the analogy rather than stack two.',
      },
      {
        section: 'What They Found (3:30-5:30)',
        purpose: 'Report the result, the secondary finding, and the authors own stated uncertainty, which is the most underused part of science coverage.',
        tips: 'Put the numbers on screen as you say them. Quote the authors uncertainty rather than softening it.',
      },
      {
        section: 'What It Does Not Mean (5:30-7:00)',
        purpose: 'Stop the overreach before the viewer makes it or finds it in the comments.',
        tips: 'Name two specific overreaches and the population the study actually covered.',
      },
      {
        section: 'What Would Change It and Sources (7:00-9:00)',
        purpose: 'Make the claim falsifiable and put the citation in front of the viewer.',
        tips: 'Hold the citation frame long enough to read. End with what is actually known, not what might be true.',
      },
    ],
    filledExample: `[RESULT FIRST - 0:00 to 0:30 | about ${words(30, 540)} words]
Open on a paper page with the results table highlighted.
"A 2024 study found that a five minute walk after a meal changed the blood sugar curve more than the same walk before it, in a group of 40 office workers."
This is an invented study used to show the structure. No real paper is described.
"Forty participants, a crossover design."

[WHAT WE ALREADY KNEW - 0:30 to 1:30 | about ${words(60, 540)} words]
"We already knew that walking after eating blunts the rise in blood sugar compared with sitting."
"Here is the gap: almost all of that work compared walking with sitting, not walking after with walking before."

[HOW THEY TESTED IT - 1:30 to 3:30 | about ${words(120, 540)} words]
"Each participant did both conditions on separate days, so they acted as their own comparison."
"Think of it like testing two umbrellas in the same storm instead of two different storms."
"Here is what the method cannot detect: anything smaller than the sensor's margin, and anything about long term health, because the study ran for two weeks."

[WHAT THEY FOUND - 3:30 to 5:30 | about ${words(120, 540)} words]
"The after meal walk produced a lower peak reading in 32 of the 40 participants."
"The secondary finding is more interesting: the difference was largest for the participants who ate the largest breakfast."
"The authors flagged that the sensor readings were self reported for the first two days, which weakens those specific days."

[WHAT IT DOES NOT MEAN - 5:30 to 7:00 | about ${words(90, 540)} words]
"This does not mean the timing of exercise matters more than exercise itself, and it does not mean five minutes is enough for everyone."
"The study looked at healthy office workers under 50, so anything about other groups is a different question."

[WHAT WOULD CHANGE IT - 7:00 to 8:00 | about ${words(60, 540)} words]
"The result would be weaker if a larger trial with continuous sensors found the difference inside the sensor's margin."
"Here is what I would want to see next: the same comparison in older adults, and a longer window."

[SOURCES AND CLOSE - 8:00 to 9:00 | about ${words(60, 540)} words]
Citation on screen: authors, year, journal, sample size, held for five seconds.
"If you take one thing from this: in this sample, walking after a meal beat walking before it on one measure, and nothing here says anything about long term health."`,
    commonMistakes: [
      'Leading with a general introduction to the field instead of the specific finding and its scale.',
      'Skipping the method, which leaves the viewer unable to judge whether the result is worth believing.',
      'Reporting a correlation as a cause, or a small sample as a general rule.',
      'Leaving the citation off screen, so a viewer who wants to check the study has nowhere to go.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 9 minutes', value: `About ${fills(540)} words` },
      { metric: 'Result block to write first', value: '0:00 to 0:30' },
      { metric: 'Words per minute of finished video', value: `${paceNote(540)}` },
    ],
    faqs: [
      {
        question: 'How do I explain a study without overstating it?',
        answer: `Name the sample size and the study design in the first 30 seconds, describe what the method cannot detect, quote the authors own stated uncertainty, and add a section on what the result does not mean. A 9 minute science explainer is about ${fills(540)} words, which is enough room for all four without padding.`,
      },
      {
        question: 'Should I cite the study on screen?',
        answer: 'Yes. Put the authors, year, journal, and sample size on screen, and hold the frame long enough to read. Viewers who want to check the claim should not have to search for it.',
      },
      {
        question: 'How much background should a science video include?',
        answer: 'Two sentences, then the open question the study addressed. Leading with a full introduction to the field loses the viewers who already know it and buries the finding for everyone else.',
      },
      {
        question: 'How do I handle a study that contradicts earlier work?',
        answer: 'Say that it contradicts earlier work, and explain the difference in method or sample that might account for it. Contradiction is the most interesting part of a science story, and pretending the field is settled is the fastest way to lose a knowledgeable audience.',
      },
    ],
    keywords: [
      'science explainer script template',
      'science video script structure',
      'how to explain a study on youtube',
      'research video script',
      'science communication template',
    ],
    relatedTemplates: ['educational-explainer', 'video-essay', 'personal-finance', 'faceless-narration'],
    relatedGuides: ['how-to-write-a-youtube-script', 'youtube-pattern-interrupts'],
  },

  'book-summary': {
    slug: 'book-summary',
    title: 'Book Summary',
    metaTitle: 'Book Summary Script Template for YouTube (Free, Copy-Paste)',
    metaDescription: 'A book summary video script template with a promise, the core argument, three ideas, and an honest verdict, plus word budgets and page mapping.',
    category: 'Education',
    templateText: `[HOOK - 0:00 to 0:20 | about ${words(20, 600)} words]
Open on the single most useful line in the book, read aloud.
"[THE LINE], from [BOOK], and it changes how you read [COMMON SITUATION]."
Show the book, closed, then the page it comes from.

[WHO THIS IS FOR - 0:20 to 0:50 | about ${words(30, 600)} words]
"This book is for [SPECIFIC READER], and it is a waste of time if [WHO SHOULD SKIP IT]."
"Read time is about [HOURS], and it could have been [SHORTER]."

[THE CORE ARGUMENT - 0:50 to 2:00 | about ${words(70, 600)} words]
"The whole book is one argument: [CORE CLAIM]."
"Everything else is evidence for that claim, arranged in [STRUCTURE: parts, chapters, or a single long case]."
"That is the map. Here are the three ideas worth keeping."

[IDEA ONE - 2:00 to 4:00 | about ${words(120, 600)} words]
"[IDEA ONE, in the author's terms]."
"Here is what it looks like in practice: [CONCRETE EXAMPLE]."
Where useful, add your own counterexample and say it is yours.

[IDEA TWO - 4:00 to 6:00 | about ${words(120, 600)} words]
"[IDEA TWO]."
"[THE PART THAT IS EASY TO MISREAD]."
"Page [NUMBER] is where this is argued most clearly."

[IDEA THREE - 6:00 to 8:00 | about ${words(120, 600)} words]
"[IDEA THREE]."
"[HOW THE THREE IDEAS FIT TOGETHER]."

[WHERE THE BOOK IS WEAK - 8:00 to 9:00 | about ${words(60, 600)} words]
"The weakest part is [SPECIFIC SECTION OR CLAIM], because [REASON]."
"Two other summaries skip this. I think it matters, because [WHY]."

[VERDICT AND NEXT READ - 9:00 to 10:00 | about ${words(60, 600)} words]
"Verdict: [WHO SHOULD READ IT] should read it, and [WHO ELSE] can skip it."
"If you liked the third idea, [RELATED BOOK] goes further on that one thing."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'Open on the most useful line rather than the title. The line is the reason someone would want the summary.',
        tips: 'Read the sentence slowly, then show its page. It proves you read the book rather than the blurb.',
      },
      {
        section: 'Who This Is For (0:20-0:50)',
        purpose: 'Tell viewers whether this book is for them before spending their time, which is the difference between a summary and a sales pitch.',
        tips: 'Name who should skip it. A summary that recommends everything recommends nothing.',
      },
      {
        section: 'The Core Argument (0:50-2:00)',
        purpose: 'Reduce the book to one claim plus its structure, so the three ideas later have somewhere to attach.',
        tips: 'State the claim in the author\'s terms, then describe the structure in one sentence.',
      },
      {
        section: 'Three Ideas (2:00-8:00)',
        purpose: 'Deliver the parts a reader would actually use, each with a concrete example and a page reference.',
        tips: 'Keep the author\'s terminology where it is useful and translate it where it is not. Cite the page so viewers can check.',
      },
      {
        section: 'Where the Book Is Weak (8:00-9:00)',
        purpose: 'Name the weak section. This is the section that makes the summary yours instead of a publisher blurb.',
        tips: 'Be specific about the claim and the reason. "It drags in the middle" is not a criticism, it is a mood.',
      },
      {
        section: 'Verdict and Next Read (9:00-10:00)',
        purpose: 'Close with a decision and a direction, so the viewer leaves with something actionable.',
        tips: 'Match the next book to the specific idea the viewer is likely to want more of.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20 | about ${words(20, 600)} words]
Open on a page with a single sentence underlined in pencil.
"The most expensive sentence in this book is on page 74: most people buy convenience and then complain about the price."
This is an invented book used to show the structure.

[WHO THIS IS FOR - 0:20 to 0:50 | about ${words(30, 600)} words]
"This is for anyone who buys from small suppliers and wonders why the price is what it is. It is a waste of time if you wanted a negotiation manual."
"Read time is about five hours, and the argument could have been made in two."

[THE CORE ARGUMENT - 0:50 to 2:00 | about ${words(70, 600)} words]
"The whole book is one argument: a supplier's price is a record of everything they chose not to automate."
"Everything else is evidence for that claim, arranged as three parts, one per stage of a small business."
"That is the map. Here are the three ideas worth keeping."

[IDEA ONE - 2:00 to 4:00 | about ${words(120, 600)} words]
"The first idea is that cheapness and simplicity are different products, and customers pick one while believing they picked the other."
"Here is what it looks like in practice: a bakery that sells one loaf and charges more than the supermarket, and stays full, because it is selling the choice not to decide."
I would push back slightly here: that only works in a dense enough neighbourhood, and the book does not say so.

[IDEA TWO - 4:00 to 6:00 | about ${words(120, 600)} words]
"The second idea is that a supplier's fixed costs are the thing they hide, not their margin."
"The part that is easy to misread is the word fixed. The book means the costs that do not change when you order one more unit, not the costs that never change."
"Page 112 is where this is argued most clearly."

[IDEA THREE - 6:00 to 8:00 | about ${words(120, 600)} words]
"The third idea is that price is a promise about what the supplier will refuse to do."
"Together the three ideas say: the price is a record of decisions, the fixed costs explain the floor, and the promise explains why the customer stays."

[WHERE THE BOOK IS WEAK - 8:00 to 9:00 | about ${words(60, 600)} words]
"The weakest part is the chapter on scaling, which assumes a second location is affordable, because the case studies are all in one expensive city."
"Two other summaries skip this. I think it matters, because it is the chapter most readers will act on."

[VERDICT AND NEXT READ - 9:00 to 10:00 | about ${words(60, 600)} words]
"Verdict: anyone pricing a handmade product should read the first three chapters, and most others can take the ideas and skip the rest."
"If you liked the third idea, the next book goes further on what a price signals."`,
    commonMistakes: [
      'Summarizing every chapter instead of reducing the book to one claim plus the ideas worth keeping.',
      'Reciting the publisher blurb rather than quoting a specific line and its page.',
      'Never saying who should skip the book, which turns the summary into a promotion.',
      'Skipping the weak section, which leaves the viewer with no way to tell which parts to trust.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 10 minutes', value: `About ${fills(600)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(600)}` },
    ],
    faqs: [
      {
        question: 'How long should a book summary video be?',
        answer: `Ten to fifteen minutes for a non fiction book with three ideas worth keeping. A 10 minute summary is about ${fills(600)} words. Summaries that run 25 minutes usually mean the video is recapping the book rather than summarizing it.`,
      },
      {
        question: 'Should I cover every chapter in a book summary?',
        answer: 'No. Reduce the book to its core argument and the two or three ideas a reader would use, then name where the argument is weak. Chapter by chapter recaps lose viewers because the video becomes a table of contents with commentary.',
      },
      {
        question: 'Do I need permission to summarize a book on video?',
        answer: 'Summarizing and commenting on a book is normal critical use, but keep quotes short, credit the author and title on screen, and do not reproduce long passages or the structure of a whole chapter. Rules differ, so check the requirements that apply to you.',
      },
      {
        question: 'How do I make a book summary different from the ten others on the same title?',
        answer: 'Quote a specific line with its page, add one counterexample of your own, and name the weakest section of the book. The last one is almost always missing from other summaries, and it is the part viewers stay for.',
      },
    ],
    keywords: [
      'book summary video script template',
      'book review video script',
      'how to summarize a book on youtube',
      'book recap video structure',
      'nonfiction summary template',
    ],
    relatedTemplates: ['top-10-list', 'educational-explainer', 'video-essay', 'history-story'],
    relatedGuides: ['script-structure-guide', 'youtube-watch-time-psychology'],
  },

  'interview-episode': {
    slug: 'interview-episode',
    title: 'Interview / Guest Episode',
    metaTitle: 'Interview Video Script Template: Guest Episode and Example',
    metaDescription: 'An interview video script template with a cold open, guest introduction, question ladder, and close, plus word budgets and prep notes.',
    category: 'Podcasts',
    templateText: `[COLD OPEN - 0:00 to 0:30 | about ${words(30, 1080)} words]
Open on the single best 15 seconds of the guest speaking, then cut to the host.
"[THE GUEST'S MOST SURPRISING CLAIM, in their own words]"
Say who they are in one line, then get out of the way.

[GUEST INTRODUCTION - 0:30 to 1:30 | about ${words(60, 1080)} words]
"[NAME] is [ROLE], and the reason I wanted this conversation is [THE SPECIFIC QUESTION]."
"Here is what we cover: [TOPIC ONE], [TOPIC TWO], [TOPIC THREE]."
Set expectations for length and format.

[THE EASY OPENER - 1:30 to 4:00 | about ${words(150, 1080)} words]
Ask the question the guest is most comfortable with, so they settle in on camera.
"[QUESTIONS THAT LET THEM TELL THEIR OWN STORY]"
Let them finish. Do not fill the pause.

[THE CORE QUESTION - 4:00 to 10:00 | about ${words(360, 1080)} words]
"[THE MAIN QUESTION the whole episode is built around]."
Ask it once. If the answer drifts, the follow up is: "You said [THEIR WORD]. Go slower on that part."
Second follow up: "What does that look like on a Tuesday?"

[THE HARD QUESTION - 10:00 to 14:00 | about ${words(240, 1080)} words]
"[THE QUESTION THEY WILL NOT HAVE BEEN ASKED]."
Ask it plainly, without softening it into a compliment.
If they dodge: "Let me ask it differently. [SAME QUESTION, NARROWER]."

[THE PRACTICAL TURN - 14:00 to 17:00 | about ${words(180, 1080)} words]
"What should somebody listening do tomorrow if they want to start on this?"
"Walk me through the first step as if I know nothing."

[CLOSE - 17:00 to 18:00 | about ${words(60, 1080)} words]
"[ONE SUMMARY LINE FOR THE GUEST]."
"[WHERE TO FIND THEM, said once, on screen and aloud]."
"[YOUR OWN ONE SENTENCE TAKEAWAY, then the next episode tease]."`,
    annotatedBreakdown: [
      {
        section: 'Cold Open (0:00-0:30)',
        purpose: 'The best 15 seconds of the guest is the reason a stranger clicks. Leading with the host instead buries the only thing that is unique to this episode.',
        tips: 'Pick the clip that stands alone with no context. Cut into it, do not fade.',
      },
      {
        section: 'Guest Introduction (0:30-1:30)',
        purpose: 'Give just enough biography to make the conversation worth staying for, and state the question the episode answers.',
        tips: 'One line of credentials, then the reason for the conversation. Save the career history for a question, not the intro.',
      },
      {
        section: 'The Easy Opener (1:30-4:00)',
        purpose: 'Let the guest become themselves on camera before you ask for anything difficult.',
        tips: 'Do not interrupt. The first long answer is where the audience decides whether they like the guest.',
      },
      {
        section: 'The Core Question (4:00-10:00)',
        purpose: 'Spend the largest block on the one question the episode is actually about, with follow ups that go deeper rather than sideways.',
        tips: 'Quote the guest\'s own words back to them. Ask what it looks like on a normal day, which turns abstractions into specifics.',
      },
      {
        section: 'The Hard Question (10:00-14:00)',
        purpose: 'Ask the thing they have not been asked. This is what separates an interview from a promotional appearance.',
        tips: 'Ask it plainly, then give them room. If they dodge, narrow the same question rather than moving on.',
      },
      {
        section: 'Practical and Close (14:00-18:00)',
        purpose: 'Turn the conversation into something the listener can act on, then close cleanly.',
        tips: 'One first step, said slowly. Say the guest\'s details once, aloud and on screen, and end on your own takeaway.',
      },
    ],
    filledExample: `[COLD OPEN - 0:00 to 0:30 | about ${words(30, 1080)} words]
Cut straight into the guest mid sentence: "We threw away the first two hundred loaves and that was the cheapest part of the whole thing."
Cut to the host. "That is a baker from a two person shop who has priced a loaf at four times the supermarket, and stayed open for nine years."
This is an invented conversation used to show the structure.

[GUEST INTRODUCTION - 0:30 to 1:30 | about ${words(60, 1080)} words]
"She runs a bakery with one employee, and the reason I wanted this conversation is that she has never once discounted, and I wanted to know what that costs her."
"Here is what we cover: pricing a single product, saying no to wholesale, and what she would do differently with a second shop."

[THE EASY OPENER - 1:30 to 4:00 | about ${words(150, 1080)} words]
"Start with the first year. What did you actually sell, and to whom?"
Let her answer at length. Follow up: "Who came back the second week?"

[THE CORE QUESTION - 4:00 to 10:00 | about ${words(360, 1080)} words]
"You said the throwaway loaves were the cheapest part. What did you mean by that?"
"You said the word waste. Go slower on that part."
Follow up: "What does that look like on a Tuesday, when nobody is posting about you?"

[THE HARD QUESTION - 10:00 to 14:00 | about ${words(240, 1080)} words]
"At four times the supermarket price, who exactly can afford this, and are you comfortable with that?"
Held the pause after she answered. Then: "Let me ask it differently. What would you say to someone in a neighbourhood where nobody can?"

[THE PRACTICAL TURN - 14:00 to 17:00 | about ${words(180, 1080)} words]
"What should somebody listening do tomorrow if they want to start on this?"
"Walk me through the first step as if I know nothing."

[CLOSE - 17:00 to 18:00 | about ${words(60, 1080)} words]
"One product, one price, and a nine year refusal to discount."
"Her shop and the newsletter are on screen. My own takeaway: the pricing decision was made on day one, not in year three."`,
    commonMistakes: [
      'Opening on the host introducing the episode instead of the guest saying the most interesting thing they say all hour.',
      'Asking five shallow questions instead of one core question with real follow ups.',
      'Filling every pause, which trains the guest to give short answers and flattens the rest of the episode.',
      'Skipping the practical question, so the listener leaves entertained but with nothing to do next.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 18 minutes', value: `About ${fills(1080)} words` },
      { metric: 'Cold open block to write first', value: '0:00 to 0:30' },
      { metric: 'Words per minute of finished video', value: `${paceNote(1080)}` },
    ],
    faqs: [
      {
        question: 'Do I need a script for an interview?',
        answer: `Script the frame and the questions, never the answers. Write the cold open, the introduction, and your question ladder word for word, then ask follow ups live. An 18 minute episode holds roughly ${fills(1080)} words of talk, most of it improvised.`,
      },
      {
        question: 'How many questions should an interview episode have?',
        answer: 'Three to five substantial ones, with a core question that gets its own large block. A long list of shallow questions produces a long list of shallow answers, and the guest never gets to say anything they have not said before.',
      },
      {
        question: 'How do I ask a hard question without being hostile?',
        answer: 'Ask it plainly and let the guest answer. Skip the preamble that softens it into a compliment. If the answer dodges, ask the same question in a narrower form rather than moving on.',
      },
      {
        question: 'What should the cold open of an interview be?',
        answer: 'The guest\'s single most interesting line from anywhere in the conversation, cut clean with no context. It is the only material in an interview that a stranger cannot get anywhere else.',
      },
    ],
    keywords: [
      'interview video script template',
      'podcast interview template',
      'guest episode script',
      'how to prepare interview questions',
      'interview episode structure',
    ],
    relatedTemplates: ['podcast-clip', 'news-commentary', 'comparison-video', 'storytime'],
    relatedGuides: ['youtube-hook-examples', 'youtube-watch-time-psychology'],
  },

  'beginner-guide': {
    slug: 'beginner-guide',
    title: 'Beginner\'s Guide',
    metaTitle: 'Beginner\'s Guide Script Template: Free Outline and Example',
    metaDescription: 'A beginner guide video script template that starts from zero: what you need, first steps, common mistakes, and a clear next step.',
    category: 'Education',
    templateText: `[HOOK - 0:00 to 0:20 | about ${words(20, 660)} words]
Open on the moment a beginner stops feeling lost, not on the finished expert result.
"Everything you need to start [ACTIVITY], in the order you actually need it."
"No prior knowledge assumed. If you already [BASIC SKILL], skip to [TIMESTAMP]."

[WHAT THIS IS AND IS NOT - 0:20 to 1:00 | about ${words(40, 660)} words]
"This video is about [SCOPE]. It is not about [WHAT YOU ARE LEAVING OUT]."
"That is a separate video, and I will point to it at the end."
Say how long the beginner stage lasts, so nobody expects instant competence.

[WHAT YOU ACTUALLY NEED - 1:00 to 2:30 | about ${words(90, 660)} words]
"Three things, and only two of them cost anything: [ITEM ONE], [ITEM TWO], [ITEM THREE]."
"Do not buy [THING BEGINNERS WASTE MONEY ON] yet. You will not need it for a while."
Show the cheap setup and say what it costs.

[FIRST STEP - 2:30 to 5:00 | about ${words(150, 660)} words]
"Step one, and do this before anything else: [ACTION]."
Do it on screen slowly, with the mistake people make named first.
"The wrong way looks like [WRONG VERSION]. The right way looks like [RIGHT VERSION]."
"Once you can do this, you are past the hardest part."

[SECOND STEP - 5:00 to 7:00 | about ${words(120, 660)} words]
"Step two: [ACTION]."
"Now that you can do step one, this gets easier because [REASON]."
Give a simple check: "If [SIGN], you are ready for the next step."

[THIRD STEP - 7:00 to 9:00 | about ${words(120, 660)} words]
"Step three: [ACTION]."
"This is the one people rush. Taking a week here saves you a month later."

[THE FIVE BEGINNER MISTAKES - 9:00 to 10:30 | about ${words(90, 660)} words]
List them fast, one line each, most common first.
"Mistake one: [MISTAKE]. Fix: [FIX]."

[WHAT TO DO NEXT - 10:30 to 11:30 | about ${words(60, 660)} words]
"When you can [SIGN OF READINESS], the next thing to learn is [NEXT SKILL]."
"That video is linked on screen now."`,
    annotatedBreakdown: [
      {
        section: 'Hook (0:00-0:20)',
        purpose: 'Beginners are looking for permission to start, not a demonstration of expertise. Promise the sequence rather than the result.',
        tips: 'Say explicitly that no prior knowledge is assumed, and offer a timestamp for people who already have the basics so you do not lose them.',
      },
      {
        section: 'What This Is and Is Not (0:20-1:00)',
        purpose: 'Bound the video, so a beginner is not scared by everything you are not covering today.',
        tips: 'Name the excluded topics and promise a separate video. State how long the beginner stage realistically lasts.',
      },
      {
        section: 'What You Actually Need (1:00-2:30)',
        purpose: 'Stop beginners from spending money before they know whether they enjoy the activity.',
        tips: 'Three items, with costs. Name the thing beginners buy too early and tell them not to buy it yet.',
      },
      {
        section: 'Steps (2:30-9:00)',
        purpose: 'Deliver the first three steps in order, each with the common mistake shown before the correct version.',
        tips: 'Demonstrate slowly and give a readiness check after each step. Beginners need to know when to move on.',
      },
      {
        section: 'The Five Beginner Mistakes (9:00-10:30)',
        purpose: 'Compress the things that derail beginners into a fast list they can remember.',
        tips: 'One line each, most common first, with the fix attached rather than a separate explanation.',
      },
      {
        section: 'What to Do Next (10:30-11:30)',
        purpose: 'Send the viewer to the next skill with a clear readiness signal, so the learning path continues.',
        tips: 'Name the readiness signal, then link the next video. A beginner guide that ends without a next step strands the viewer.',
      },
    ],
    filledExample: `[HOOK - 0:00 to 0:20 | about ${words(20, 660)} words]
Open on a person sitting down at a sewing machine for the first time, threading it slowly.
"Everything you need to start sewing, in the order you actually need it. No prior knowledge assumed."
"If you can already thread a machine, skip to the timestamp on screen."

[WHAT THIS IS AND IS NOT - 0:20 to 1:00 | about ${words(40, 660)} words]
"This video is about your first three sewing sessions. It is not about making clothes, which is a separate video I will point to at the end."
"Expect the first month to be about straight lines and unlearning the urge to go fast."
This is an invented example used to show the structure.

[WHAT YOU ACTUALLY NEED - 1:00 to 2:30 | about ${words(90, 660)} words]
"Three things, and only two of them cost anything: a machine you already have access to, a spool of all purpose thread, and a piece of cotton about the size of a tea towel."
"Do not buy the overlocker yet. You will not need it for months, and half of them sit unused."
Show the setup on the table and say what the thread and the cloth cost.

[FIRST STEP - 2:30 to 5:00 | about ${words(150, 660)} words]
"Step one, and do this before anything else: thread the machine with the presser foot up."
"The wrong way is to thread it with the foot down, which leaves the thread outside the tension discs, so everything tangles underneath."
Do it slowly on camera. "Once you can thread it without checking the manual, you are past the hardest part."

[SECOND STEP - 5:00 to 7:00 | about ${words(120, 660)} words]
"Step two: sew straight lines on the cotton, with no thread in the machine."
"Now that threading is automatic, this gets easier because you are only watching the line, not the machine."
"If your line wanders by more than a few millimetres on a short run, stay here another session."

[THIRD STEP - 7:00 to 9:00 | about ${words(120, 660)} words]
"Step three: same lines, with thread, and practise the backstitch at each end."
"This is the one people rush. Taking a week here saves you a month later."

[THE FIVE BEGINNER MISTAKES - 9:00 to 10:30 | about ${words(90, 660)} words]
"Mistake one: buying fabric before practising. Fix: practise on scrap. Mistake two: threading with the foot down. Fix: foot up. Mistake three: pulling the fabric. Fix: let the feed dogs move it. Mistake four: never changing the needle. Fix: change it every project. Mistake five: skipping the iron. Fix: press every seam."

[WHAT TO DO NEXT - 10:30 to 11:30 | about ${words(60, 660)} words]
"When you can follow a curve without stopping, the next thing to learn is seam allowance and why patterns assume it."
"That video is linked on screen now."`,
    commonMistakes: [
      'Showing the expert result in the hook instead of the moment a beginner stops feeling lost, which makes the activity look unattainable.',
      'Recommending equipment a beginner will not need for months, which is the fastest way to make the video expensive to follow.',
      'Teaching steps without readiness checks, so beginners cannot tell when to move on.',
      'Ending without a next step, which strands the viewer at exactly the point they need direction.',
    ],
    retentionBenchmarks: [
      { metric: 'Script length that fills 11 minutes', value: `About ${fills(660)} words` },
      { metric: 'Opening block to write first', value: '0:00 to 0:20' },
      { metric: 'Words per minute of finished video', value: `${paceNote(660)}` },
    ],
    faqs: [
      {
        question: 'How long should a beginner guide video be?',
        answer: `Long enough for three steps and the mistakes list, usually 8 to 15 minutes. An 11 minute beginner guide is about ${fills(660)} words. Beginner videos get long when they try to cover the whole subject, so leave the advanced material for a follow up.`,
      },
      {
        question: 'How do I write for viewers who know nothing?',
        answer: 'Name every item and every action, show the mistake before the correct version, and add a readiness check after each step. Avoid words a beginner would not know unless you define them at first use, and say plainly that no prior knowledge is assumed.',
      },
      {
        question: 'Should a beginner guide include equipment recommendations?',
        answer: 'Only the minimum, with costs, and say which popular purchase to delay. Beginners who spend money before they know whether they enjoy the activity are the ones who quit.',
      },
      {
        question: 'How do I keep experienced viewers watching a beginner guide?',
        answer: 'Give them a timestamp to skip to and put the advanced material at the end. A beginner guide can serve both audiences if the more useful parts are placed where an experienced viewer will actually reach them.',
      },
    ],
    keywords: [
      'beginner guide video script template',
      'how to script a beginner tutorial',
      'everything you need to know video structure',
      'starter guide video template',
      'beginner youtube video script',
    ],
    relatedTemplates: ['tutorial', 'software-tutorial', 'educational-explainer', 'workout-video'],
    relatedGuides: ['youtube-tutorial-scripts', 'script-structure-guide'],
  },
}

export function getAllTemplateSlugs(): string[] {
  return Object.keys(templates)
}

export function getTemplateBySlug(slug: string): TemplateData | undefined {
  return templates[slug]
}
